// @ts-nocheck - Large configuration file with complex dynamic types, converted from JS
/* eslint-disable no-nested-ternary */
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React from "react";
import { Link } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { faEdit } from "@fortawesome/free-solid-svg-icons";
import {
  businessHoursInputs,
  compoundInputs,
  contactSetInputs,
  dataCategoryInputs,
  milestoneInputs,
  threadInput,
} from "../Inputs/inputs";
import ExtractOptions from "../Inputs/ExtractOptions";
import TestExtraction from "../Inputs/TestExtraction";
import {
  addNewMessageTemplate,
  selectCounterCategories,
  selectMilestoneLabels,
} from "../../../../store/slices/projectSlice";
import { store } from "../../../../store/store";
import {
  loadApiProfilesPaginated,
  loadComponentOptionsPaginated,
  loadNylasEventsCalendarOptionsPaginated,
  proxyAPI,
} from "../../../../proxyAPI";
import { pageProps as defaultPageProps } from "../../../../helpers/pageProps";
import timezones from "../timezones";
import InputLabel from "../Inputs/InputLabel";
import {
  formatAssetKindOptionLabel,
  getAssetKindValueLabels,
  loadAssetKindOptions,
} from "../../../../Assets/assetIconsList";
import type {
  LogicConfig,
  LogicInputConfig,
  LogicOptionsConfigResult,
} from "../Inputs/optionsConfigTypes";

const timeConversion = {
  day: 1,
  hour: 24,
  minute: 24 * 60,
  second: 24 * 60 * 60,
};

/** A single input configuration entry inside an action's `inputs` array. */
export type ActionInputConfig = LogicInputConfig;

/** The configuration object for a single action type. */
export type ActionConfig = LogicConfig;

/** Parameters accepted by `actionOptionsConfig`. */
export interface ActionOptionsConfigParams {
  action: Record<string, any>;
  context: Record<string, any>;
  params: Record<string, any>;
  rule?: Record<string, any>;
  event?: Record<string, any>;
  dispatch: (...args: any[]) => void;
  pageProps?: Record<string, any>;
}

const actionOptionsConfig = ({
  action,
  context,
  params,
  rule,
  event,
  dispatch,
  pageProps = defaultPageProps,
}: ActionOptionsConfigParams): LogicOptionsConfigResult => {
  const {
    contactFields,
    reportingTopics,
    reportingTemplates,
    jsonCollections,
    systemDataStructures,
    addChild,
    components,
  } = context;

  const projectData = store.getState()?.project || {};
  const storedispatch = store.dispatch;
  const workflows = projectData.Workflows || [];
  const eventHandlers = projectData.EventHandlers || [];
  const messageTemplates = projectData.MessageTemplates || [];
  const inputArguments = projectData.InputArguments || [];
  const workflow = workflows.find((wf) => wf.UUID === params.workflowUUID);
  const milestones = selectMilestoneLabels(store.getState());
  const counters = selectCounterCategories(store.getState());

  // returns an array of questions used in the survey
  const getFormQuestions = () => {
    const formQuestions = [];
    eventHandlers.forEach((eh) =>
      eh?.Rules?.forEach((r) => {
        r.Actions?.forEach((act) => {
          if (act.Type === "form-question") {
            formQuestions.push(
              act.Options?.reduce(
                (prev, curr) => ({
                  ...prev,
                  [curr.Type]: curr.Value,
                }),
                { UUID: act.UUID }
              )
            );
          }
        });
      })
    );
    return formQuestions;
  };

  // returns an array of values for an ooption type
  const getOptionValues = (optionType) =>
    [...(action.Options || [])]
      .filter((actOpt) => actOpt.Type === optionType)
      .sort((a, b) => a.Order - b.Order)
      .map((opt) => opt.Value);

  const broadcastActionMilestoneLabel = getOptionValues("MilestoneLabel")[0];
  const affectedBroadcastActions = (workflow?.EventListeners || []).flatMap(
    (listener) => {
      const handler = eventHandlers.find(
        (eh) => eh.UUID === listener.EventHandlerUUID
      );
      if (!handler) {
        return [];
      }

      return (handler.Rules || []).flatMap((eventRule, ruleIndex) =>
        (eventRule.Actions || [])
          .filter(
            (eventAction) =>
              eventAction.Type === "start:broadcast" &&
              eventAction.ExpectReplies &&
              (eventAction.Options || []).some(
                (option) =>
                  option.Type === "MilestoneLabel" &&
                  option.Value === broadcastActionMilestoneLabel
              )
          )
          .map((eventAction) => ({
            label: `${workflow.Label || workflow.Name || "Workflow"} / ${
              eventRule.Label || `Rule ${ruleIndex + 1}`
            } / Broadcast action #${(eventAction.Order ?? 0) + 1} - ${
              eventAction.UUID
            }`,
            path: `/library/logic/builder/${params.projectID}/workflow/${workflow.UUID}/event/${listener.EventType}/listener/${listener.UUID}/action/${eventAction.UUID}`,
            uuid: eventAction.UUID,
          }))
      );
    }
  );

  const fieldChannels = (cf) => [
    ...(cf?.isSms ? ["sms"] : []),
    ...(cf?.isEmail ? ["email"] : []),
    ...(cf?.isVoice ? ["voice"] : []),
  ];

  // returns an array of comms channels in fields with multiple channels
  const selectedFieldChannels = (cfs = getOptionValues("contactFieldUUID")) =>
    contactFields.reduce((prev, cf) => {
      if (cfs.includes(cf.uuid) && fieldChannels(cf).length > 1) {
        return [...new Set([...prev, ...fieldChannels(cf)])];
      }
      return prev;
    }, []);

  const fieldsWithChannels = contactFields.filter(
    (cf) => cf.isSms || cf.isEmail || cf.isVoice
  );

  // returns an array of comms channels in fields with multiple channels
  const multiFieldChannels = [
    ...(fieldsWithChannels[0]?.isSms ? ["sms"] : []),
    ...(fieldsWithChannels[0]?.isEmail ? ["email"] : []),
    ...(fieldsWithChannels[0]?.isVoice ? ["voice"] : []),
  ];

  const selectedContactField = (
    uuid = getOptionValues("contactFieldUUID")[0]
  ) => contactFields.find((cf) => cf.uuid === uuid);

  const selectedContactFields = contactFields.filter((cf) =>
    getOptionValues("contactFieldUUID").includes(cf.uuid)
  );

  const selectedComponent = components.find((c) => c.UUID === action.TypeUUID);

  const selectedWorkflow = workflows.find((wf) => wf.UUID === action.TypeUUID);
  const runtimeCompletionMilestoneLabels = new Set(
    (projectData.EventHandlers || []).flatMap((eh) =>
      (eh.Rules || []).flatMap((eventRule) =>
        (eventRule.Actions || []).flatMap((eventAction) =>
          (eventAction.Options || [])
            .filter(
              (option) => option.Type === "runtime-completion-milestone-label"
            )
            .map((option) => option.Value)
        )
      )
    )
  );
  const scheduledMilestones = milestones.filter(
    (milestone) => !runtimeCompletionMilestoneLabels.has(milestone)
  );

  const selectedFileRowsTargetVariable = inputArguments.find(
    (arg) => arg.UUID === getOptionValues("variableTo")[0]
  );
  const selectedFileRowsDataStructure = jsonCollections.find(
    (collection) =>
      collection.Uuid === selectedFileRowsTargetVariable?.DataStructure
  );
  const selectedFileRowsDataStructureFields =
    selectedFileRowsDataStructure?.Fields?.map((field) => ({
      value: field.Path || field.Name,
      label: field.Name || field.Path,
    })) || [];
  const selectedJsonToAssetSourceVariable = inputArguments.find(
    (arg) => arg.UUID === getOptionValues("json")[0]
  );
  const selectedJsonToAssetDataStructure = jsonCollections.find(
    (collection) =>
      collection.Uuid === selectedJsonToAssetSourceVariable?.DataStructure
  );
  const selectedJsonToAssetDataStructureFields =
    selectedJsonToAssetDataStructure?.Fields?.map((field) => ({
      value: field.Path || field.Name,
      label: field.Name || field.Path,
    })) || [];
  const jsonToAssetOperation = getOptionValues("operation")[0] || "both";
  const jsonToAssetUsesUpdate = ["update", "both"].includes(
    jsonToAssetOperation
  );
  const jsonToAssetFields = [
    ...(jsonToAssetOperation === "update"
      ? []
      : [{ value: "Uuid", label: "UUID" }]),
    { value: "Reference", label: "Reference" },
    { value: "Type", label: "Type" },
    { value: "Label", label: "Label" },
    { value: "Description", label: "Description" },
    { value: "Address", label: "Address" },
  ];
  const jsonToAssetMatchFields = jsonToAssetFields.filter((field) =>
    ["Uuid", "Reference", "Type", "Label"].includes(field.value)
  );
  const jsonToAssetDefaultMappings = jsonToAssetFields.map((field) => ({
    Value: "",
    Key: field.value,
    ValueFrom: "static",
  }));

  const getContactFieldsChannels = (
    cfs = getOptionValues("contactFieldUUID")
  ) =>
    contactFields.reduce((prev, cf) => {
      if (cfs.includes(cf.uuid)) {
        return [...new Set([...prev, ...fieldChannels(cf)])];
      }
      return prev;
    }, []);

  const isParentWorkflowNewThread = workflows.some((wf) =>
    eventHandlers.some((eh) =>
      eh.Rules?.some((r) =>
        r.Actions?.some(
          (a) =>
            wf.EventListeners?.some((el) => el.EventHandlerUUID === eh.UUID) &&
            workflow &&
            a.TypeUUID === workflow.UUID &&
            a.StartWith === "NewThread"
        )
      )
    )
  );

  return {
    "start:runtime": {
      label: "",
      inputs: [
        {
          type: "property",
          field: "IsSelf",
          defaultValue: [false],
          optionDefaults: ([v]) =>
            v
              ? {
                  // IsSelf
                  ForEach: null,
                  BatchesFrom: null,
                  BatchCount: null,
                  BatchesMode: null,
                  Value: null,
                  Thread: null,
                }
              : {
                  // !IsSelf
                  ForEach: "",
                  BatchesFrom: null,
                  BatchCount: null,
                  BatchesMode: null,
                  Value: null,
                  Thread: null,
                },
          props: {
            label: "Starts self?",
            isDisabled: action.Type === "start:runtime" && action.TypeUUID,
            inputType: "select",
            isValidNewOption: () => false,
            options: [
              { value: true, label: "Start itself" },
              {
                value: false,
                label: `Start another workflow ${
                  action.Type === "start:runtime" && action.TypeUUID
                    ? "(connected)"
                    : ""
                }`,
              },
            ],
          },
        },

        {
          type: "html",
          className: "mt-n5",
          html: selectedWorkflow ? (
            <span>
              This action will start the workflow{" "}
              <strong>{selectedWorkflow.Label}</strong>.
            </span>
          ) : (
            <div>
              Select a workflow to start
              <button
                type="button"
                className="btn btn-outline-primary btn-sm float-end"
                disabled={
                  action.IsSelf ||
                  (action.Type === "start:runtime" && action.TypeUUID)
                }
                onClick={() => {
                  const newUUID = uuidv4();
                  dispatch({
                    type: "edit-action",
                    actionId: action.UUID,
                    newAction: { ...action, TypeUUID: newUUID },
                  });
                  addChild(
                    {
                      workflow: {
                        Label: `Workflow-${newUUID.slice(0, 4)}`,
                        UUID: newUUID,
                        IsEntrypoint: false,
                        EventListeners: [],
                      },
                    },
                    params.workflowUUID
                  );
                }}
              >
                Add Node
              </button>
            </div>
          ),
        },

        ...(!action.IsSelf
          ? [
              {
                type: "option",
                field: "ForEach",
                defaultValue: [""],
                optionDefaults: ([{ Value: v }]) => {
                  if (["Sequence", "Parallel"].includes(v)) {
                    return {
                      BatchesFrom: "SendList",
                      BatchCount: "100",
                      BatchesMode: "PerBatch",
                      Value: null,
                      Thread: null,
                    };
                  }
                  return {
                    BatchesFrom: null,
                    BatchCount: null,
                    BatchesMode: null,
                    Value: null,
                    Thread: null,
                  };
                },
                props: {
                  label: "Start workflow",
                  inputType: "select",
                  isValidNewOption: () => false,
                  options: [
                    {
                      value: "",
                      label: "Start workflow once",
                    },
                    {
                      value: "Sequence",
                      label: "Start workflow multiple times in a sequence",
                    },
                    {
                      value: "Parallel",
                      label: "Start workflow multiple times in parallel",
                    },
                  ],
                },
              },
            ]
          : []),

        {
          type: "heading",
          label: "Completion milestone",
          className: "mb-n1",
        },
        {
          type: "option",
          field: "runtime-completion-milestone",
          defaultValue: ["false"],
          optionDefaults: ([{ Value: value }]) =>
            value === "true"
              ? {
                  "runtime-completion-milestone-label": "",
                  "runtime-completion-milestone-mode": "append",
                }
              : {
                  "runtime-completion-milestone-label": null,
                  "runtime-completion-milestone-mode": null,
                },
          props: {
            label: "Create milestone when started runtimes complete",
            inputType: "select",
            isValidNewOption: () => false,
            options: [
              { label: "No", value: "false" },
              { label: "Yes", value: "true" },
            ],
          },
        },
        {
          type: "option",
          field: "runtime-completion-milestone-label",
          ifOptionTypeHas: [
            {
              optionType: "runtime-completion-milestone",
              values: ["true"],
            },
          ],
          props: {
            label: "Milestone label",
            inputType: "select",
            options: milestones.map((m) => ({
              label: m,
              value: m,
            })),
            isValidNewOption: (v) => v,
            onCreateOption: (value) => value,
          },
        },
        {
          type: "option",
          field: "runtime-completion-milestone-mode",
          defaultValue: ["append"],
          ifOptionTypeHas: [
            {
              optionType: "runtime-completion-milestone",
              values: ["true"],
            },
          ],
          props: {
            label: "Existing completion milestone handling",
            inputType: "select",
            isValidNewOption: () => false,
            options: [
              { label: "Append started runtimes", value: "append" },
              { label: "Replace tracked runtimes", value: "replace" },
            ],
          },
        },

        ...(getOptionValues("ForEach")[0] !== "Parallel"
          ? [
              {
                type: "property",
                field: "StartWith",
                defaultValue: ["CurrentThread"],
                props: {
                  label: "Start with",
                  inputType: "select",
                  isValidNewOption: () => false,
                  options:
                    getOptionValues("BatchesFrom")[0] === "Variable"
                      ? [
                          { label: "Current thread", value: "CurrentThread" },
                          {
                            label: "Main thread",
                            value: "MainThread",
                          },
                          {
                            label: "New thread",
                            value: "NewThread",
                          },
                        ]
                      : [
                          { label: "Current thread", value: "CurrentThread" },
                          {
                            label: "Main thread",
                            value: "MainThread",
                          },
                          {
                            label: "New thread",
                            value: "NewThread",
                          },
                        ],
                },
              },
            ]
          : []),

        // ...(action.StartWith === "NewThread"
        //   ? [
        ...([
          "message:reply",
          "outbound-delivery-status",
          "form-submitted",
          "form-accessed",
        ].includes(event)
          ? [
              {
                type: "compound",
                field: "responder",
                defaultValue: [""],
                props: {
                  label:
                    event === "outbound-delivery-status"
                      ? "Store recipient to variable"
                      : "Store responder to variable",
                  isInputArgumentOnly: true,
                  inputArgumentProps: { Format: "contact", IsReadonly: false },
                  isValidNewOption: (v) => v.trim(),
                  showOperationSelector: true,
                  showNewThreadOption: action.StartWith === "NewThread",
                },
              },
              {
                type: "compound",
                field:
                  event === "outbound-delivery-status" ? "status" : "reply",
                defaultValue: [""],
                props: {
                  label:
                    event === "outbound-delivery-status"
                      ? "Store status to variable"
                      : "Store reply to variable",
                  isInputArgumentOnly: true,
                  inputArgumentProps: {
                    Format: "short-text",
                    IsReadonly: false,
                  },
                  isValidNewOption: (v) => v.trim(),
                  showOperationSelector: true,
                  showNewThreadOption: action.StartWith === "NewThread",
                },
              },
            ]
          : []),
        //   ]
        // : []),

        ...(!action.IsSelf && getOptionValues("ForEach")[0]
          ? [
              {
                type: "option",
                field: "BatchesFrom",
                optionDefaults: ([{ Value: v }]) => {
                  if (["ParticipantCategory"].includes(v)) {
                    return {
                      Value: null,
                      Thread: "CurrentThread",
                    };
                  }
                  return {
                    Value: null,
                    Thread: null,
                    variable: null,
                  };
                },
                props: {
                  label: "Contact batches from",
                  inputType: "select",
                  isValidNewOption: () => false,
                  options: [
                    {
                      value: "SendList",
                      label: "This workflows send list",
                    },
                    {
                      value: "ContactSet",
                      label: "Group",
                    },
                    {
                      value: "Variable",
                      label: "Variable",
                    },
                  ],
                },
              },
              {
                type: "compound",
                field: "group",
                defaultValue: [""],
                ifOptionTypeHas: [
                  {
                    optionType: "BatchesFrom",
                    values: ["ContactSet"],
                  },
                ],
                props: {
                  label: "Contact group",
                  inputType: "contactSet",
                  contactSetType: "static",
                  inputArgumentProps: { Format: "group", IsMultiple: false },
                  isValidNewOption: (v) => v.trim(),
                },
              },
              {
                type: "compound",
                field: "variable",
                defaultValue: [""],
                ifOptionTypeHas: [
                  {
                    optionType: "BatchesFrom",
                    values: ["Variable"],
                  },
                ],
                props: {
                  label: "Source",
                  isInputArgumentOnly: true,
                  isMulti: true,
                  inputArgumentProps: { IsMultiple: true },
                  isValidNewOption: (v) => v.trim(),
                  showKeyInputArgumentSelector:
                    getOptionValues("ForEach")[0] === "Sequence" &&
                    ["CurrentThread", "MainThread"].includes(action.StartWith),
                  keyInputLabel: "Target",
                },
              },
              {
                type: "option",
                field: "BatchCount",
                props: {
                  label: "Batch count",
                  type: "number",
                },
              },
              {
                type: "option",
                field: "BatchesMode",
                props: {
                  label: "Batch count mode",
                  inputType: "select",
                  options: [
                    {
                      value: "CountOfBatches",
                      label: "Count of batches",
                    },
                    {
                      value: "PerBatch",
                      label: "Per batch",
                    },
                  ],
                },
              },
            ]
          : []),
      ],
    },

    // Start a new process in another component and pass data to it.
    // This starts a new process in the selected component and passes data to it.
    // Error handling: if the component is not in production status or the process fails to start due to validation,
    // the action will fail and error message can be stored in a variable.
    // "Type": "component:start",

    // uuid of the component to start, required
    // select only "production", start only if the component is in production status
    // "TypeUUID": "process-uuid-123",
    "component:start": {
      inputs: [
        {
          type: "property",
          field: "TypeUUID",
          defaultValue: [""],
          optionDefaults: ([v]) => {
            console.log(v);
            const defaultValues = {
              // remove previous values
              ...action.Options.reduce((p, c) => {
                if (c.Type === "data") {
                  return { ...p, [c.Type]: null };
                }
                return p;
              }, {}),
              // add new ones
              ...components
                ?.find((component) => component.UUID === v)
                ?.InputArguments?.reduce(
                  (p, c) => ({
                    ...p,
                    // check if field has defaultValue
                    data: [
                      ...p.data,
                      {
                        Value: c.defaultValue || "",
                        Key: c.Field,
                        ValueFrom: "static",
                      },
                    ],
                  }),
                  { data: [] }
                ),
            };
            console.log("Default values set to", defaultValues);
            return defaultValues;
          },
          props: {
            label: "Component to start",
            inputType: "select",
            isValidNewOption: () => false,
            options: components
              .filter((component) => component.Status === "production")
              .map((component) => ({
                value: component.UUID,
                label: component.Label,
              })),
            // loadOptions: loadComponentOptionsPaginated,
          },
        },
        // "Options": [
        // FOR EACH parameter defined in the component:
        // DATA to pass to the process, optional for each optional parameter in the selected component,
        // the key is the name of the parameter defined in the component, the value can be static or from variable.
        // For required parameters, if data is not provided the action will fail.
        //
        // Source variable format must match the parameter format defined in the component.
        // If multiple values are provided but the parameter is not multiple, only the first value will be used.
        // {"Type": "data", "Value": "variable-uuid-123", "Key": "field_name_abc", "ValueFrom": "inputArgument", "MainThread": true/false},

        ...(selectedComponent?.InputArguments?.map((f) => ({
          type: "compound",
          field: "data",
          defaultValue: [""],
          props: {
            label: f.Label,
            keyValue: f.Field,
            uniqueKey: f.Field,
            isInputArgumentOnly: f.Format === "editor",
            inputArgumentProps: {
              Format: f.Format,
            },
            // isValidNewOption: (v) => v.trim(),
            // isDateTime: f?.type === "TIME",
            // dateTimeType: "dateTime",
            // isSelect: f?.type === "CATEGORY",
            // options:
            //   f?.type === "CATEGORY"
            //     ? f.categories.map((o) => ({
            //         value: o,
            //         label: o,
            //       }))
            //     : [],
          },
        })) ?? []),

        // ERROR variable to store the error message if the process fails to start, optional
        // short-text variable, can be single or multiple
        // {"Type": "error", "Value": "variable-uuid-123", "MainThread": true, "Operation": "replace"}
        {
          type: "compound",
          field: "error",
          defaultValue: [""],
          props: {
            label: "Store error in variable if the process fails to start",
            isInputArgumentOnly: true,
            inputArgumentProps: { Format: "short-text" },
            isValidNewOption: (v) => v.trim(),
          },
        },

        // New process guid if the process is started successfully, optional
        // short-text variable, can be multiple
        // {"Type": "guid", "Value": "variable-uuid-123", "MainThread": true, "Operation": "replace"}
        {
          type: "compound",
          field: "guid",
          defaultValue: [""],
          props: {
            label: "New process guid if the process is started successfully",
            isInputArgumentOnly: true,
            inputArgumentProps: { Format: "short-text" },
            isValidNewOption: (v) => v.trim(),
          },
        },
      ],
    },

    ForwardEvent: {
      label: "",
      inputs: [
        { type: "", label: "" },
        {
          type: "property",
          field: "TypeUUID",
          props: {
            label: "Select a workflow",
            inputType: "select",
            isValidNewOption: () => false,
            options: [
              ...workflows
                .filter((wf) => wf.UUID !== workflow?.UUID)
                .map((wf) => ({
                  value: wf.UUID,
                  label: wf.Label,
                })),
            ],
          },
        },
        threadInput(),
      ],
    },

    "start:broadcast": {
      label: "Start broadcast new",
      inputs: [
        {
          type: "property",
          field: "TypeUUID",
          defaultValue: [""],
          props: {
            label: "Message template",
            inputType: "select",
            options: [
              {
                value: "",
                label: "Use the template set in the workflow level",
              },
              ...messageTemplates.map((template) => ({
                value: template?.UUID,
                label: template?.Label,
              })),
            ],
            isValidNewOption: (v) => v,
            onCreateOption: (value) => {
              const newUUID = uuidv4();
              storedispatch(
                addNewMessageTemplate({
                  UUID: newUUID,
                  Label: value,
                  Text: value,
                })
              );
              return newUUID;
            },
          },
          children: action.TypeUUID && (
            <Link
              to={`/library/logic/builder/${params.projectID}/message-template/${action?.TypeUUID}`}
              state={{ nextPage: -1 }}
            >
              <small>
                <FontAwesomeIcon icon={faEdit} /> Edit message
              </small>
            </Link>
          ),
        },
        // simple or advanced mode
        {
          type: "option",
          field: "_simple_advanced",
          defaultValue: ["simple"],
          optionDefaults: ([{ Value: v }]) => ({
            mode: v === "simple" ? "contactField" : "multiField",
            channel: selectedFieldChannels([fieldsWithChannels[0]?.uuid])[0],
            IsMultipleMessage: v === "advanced" ? "one" : null,
            contactFieldUUID: fieldsWithChannels[0]?.uuid,
          }),
          props: {
            label: "Send mode",
            inputType: "radio",
            radioName: "mode",
            options: [
              { value: "simple", label: "Simple" },
              { value: "advanced", label: "Advanced" },
            ],
          },
        },
        // multi field or channels mode
        {
          type: "option",
          field: "mode",
          defaultValue: ["contactField"],
          ifOptionTypeHas: [
            {
              optionType: "_simple_advanced",
              values: ["advanced"],
            },
          ],
          optionDefaults: ([{ Value: v }]) => ({
            channel:
              v === "channel"
                ? "sms"
                : selectedFieldChannels([fieldsWithChannels[0]?.uuid])[0],
            contactFieldUUID:
              v === "multiField" ? fieldsWithChannels[0]?.uuid : null,
            IsMultipleMessage: "one",
          }),
          props: {
            // label: "Send mode",
            inputType: "tab",
            options: [
              // { value: "contactField", label: "Single field" },
              { value: "multiField", label: "Multiple fields" },
              { value: "channel", label: "Channels" },
            ],
          },
        },
        // contact field
        {
          type: "option",
          field: "contactFieldUUID",
          defaultValue:
            getOptionValues("mode")[0] === "channel"
              ? null
              : [fieldsWithChannels[0]?.uuid],
          ifOptionTypeHas: [
            { optionType: "mode", values: ["contactField", "multiField"] },
          ],
          optionDefaults: (values) => ({
            channel: selectedFieldChannels(values.map((v) => v.Value))[0],
            attachments: getContactFieldsChannels(
              values.map((v) => v.Value)
            ).includes("email")
              ? { ValueFrom: "static", Value: "" }
              : null,
            "rename-attachments": getContactFieldsChannels(
              values.map((v) => v.Value)
            ).includes("email")
              ? { ValueFrom: "static", Value: "" }
              : null,
          }),
          props: {
            label:
              getOptionValues("mode")[0] === "multiField"
                ? "Contact fields"
                : "Contact field",
            inputType: "select",
            isMulti: getOptionValues("mode")[0] === "multiField",
            options: contactFields
              .filter((cf) => cf.isSms || cf.isEmail || cf.isVoice)
              .map((f) => ({
                value: f.uuid,
                label: `${f.label} (${[
                  ...(f.isSms ? ["sms"] : []),
                  ...(f.isEmail ? ["email"] : []),
                  ...(f.isVoice ? ["voice"] : []),
                ].join(", ")})`,
              })),
          },
        },
        // show channels if the selected field is capable of multiple channels
        ...(selectedFieldChannels().length > 1
          ? [
              {
                type: "option",
                field: "channel",
                optionDefaults: ([{ Value: v }]) => ({
                  "rename-attachments":
                    v === "email" ? { ValueFrom: "static", Value: "" } : null,
                  attachments:
                    v === "email" ? { ValueFrom: "static", Value: "" } : null,
                }),
                props: {
                  label: "Preferred channel",
                  inputType: "select",
                  isValidNewOption: () => false,
                  options: [
                    { value: "sms", label: "SMS" },
                    { value: "email", label: "e-mail" },
                    { value: "voice", label: "Voice" },
                  ].filter((opt) =>
                    selectedFieldChannels().includes(opt.value)
                  ),
                },
              },
            ]
          : []),
        // show multi channel input
        {
          type: "option",
          field: "channel",
          // TODO
          ifOptionTypeHas: [{ optionType: "mode", values: ["channel"] }],
          optionDefaults: ([{ Value: v }]) => ({
            "rename-attachments":
              v === "email" ? { ValueFrom: "static", Value: "" } : null,
            attachments:
              v === "email" ? { ValueFrom: "static", Value: "" } : null,
          }),
          props: {
            label: "Communication channels",
            isMulti: true,
            isValidNewOption: () => false,
            options: [
              { value: "sms", label: "SMS" },
              { value: "email", label: "e-mail" },
              { value: "voice", label: "Voice" },
            ],
          },
        },
        // Send one/multiple channel at a time
        {
          type: "option",
          field: "IsMultipleMessage",
          ifOptionTypeHas: [
            { optionType: "mode", values: ["channel", "multiField"] },
          ],
          props: {
            label: "Send one/multiple channel at a time",
            inputType: "select",
            isValidNewOption: () => false,
            options: [
              { value: "one", label: "Send one channel/field at a time" },
              { value: "all", label: "Send all channels/fields at once" },
            ],
          },
        },
        ...(action.Options?.find(
          (actOpt) => actOpt.Type === "channel" && actOpt.Value === "email"
        ) || selectedContactFields.find((cf) => cf.isEmail)
          ? [
              {
                type: "compound",
                field: `attachments`,
                defaultValue: [""],
                optionDefaults: ([{ Value: v }]) => ({
                  "rename-attachments": v
                    ? { ValueFrom: "static", Value: "" }
                    : null,
                }),
                props: {
                  label: "Attachments",
                  isInputArgumentOnly: true,
                  isValidNewOption: (v) => v.trim(),
                  inputArgumentProps: { Format: "binary" },
                  isClearable: true,
                },
              },
              ...(getOptionValues("attachments")?.[0]
                ? [
                    {
                      type: "compound",
                      field: `rename-attachments`,
                      defaultValue: [""],
                      props: {
                        label: "Rename attachments",
                        isValidNewOption: (v) => v.trim(),
                        inputArgumentProps: { Format: "short-text" },
                      },
                    },
                  ]
                : []),
            ]
          : []),
        {
          type: "option",
          field: "acceptDynamicComms",
          defaultValue: ["true"],
          props: {
            label: "Use dynamic communication channel",
            inputType: "checkbox",
            options: [{ value: "true" }, { value: "false" }],
          },
        },
        { type: "heading", label: "Recipients" },
        {
          type: "option",
          field: "SendTo",
          defaultValue: ["workflow", "template", "variable"],
          props: {
            label: "Send to contacts",
            inputType: "select",
            isMulti: true,
            isValidNewOption: () => false,
            options: [
              { value: "workflow", label: "Workflow" },
              { value: "template", label: "Template" },
              // { value: "dynamicGroup", label: "Contact context" },
              {
                value: "variable",
                label: "Variable",
              },
            ],
          },
        },
        ...(getOptionValues("SendTo").includes("variable")
          ? [
              {
                type: "compound",
                field: `variable`,
                defaultValue: [""],
                props: {
                  label: "Variable name",
                  isInputArgumentOnly: true,
                  isMulti: true,
                  isValidNewOption: (v) => v.trim(),
                  inputArgumentProps: { Format: "contact" },
                },
              },
            ]
          : []),
        {
          type: "property",
          field: "ExpectReplies",
          defaultValue: [false],
          optionDefaults: ([v]) => ({
            MilestoneType: v ? "interval" : null,
            MilestoneFrom: v ? "fixed" : null,
            MilestoneValue: v ? { ValueFrom: "static", Value: "1" } : null,
            MilestoneLabel: null,
            DsMilestone: v ? { ValueFrom: "static", Value: "no" } : null,
            DsMilestoneLabel: null,
            DsMilestoneValue: null,
            MilestoneCreateMode: null,
          }),
          propertyDefaults: ([v]) => ({ WithMilestoneUnit: "minute" }),
          props: {
            label: "Expect replies",
            inputType: "select",
            isValidNewOption: () => false,
            options: [
              {
                value: true,
                label: "yes",
              },
              {
                value: false,
                label: "no",
              },
            ],
          },
        },
        ...(action.Type === "start:broadcast" && action.ExpectReplies
          ? [
              {
                type: "heading",
                label: "Reply handling",
                className: "mb-n1",
              },
              {
                type: "option",
                field: "ReplyHandling",
                defaultValue: ["workflow"],
                className: "mt-n3",
                optionDefaults: ([{ Value: value }]) => ({
                  ReplyWorkflowUUID:
                    value === "workflowUuid"
                      ? { ValueFrom: "static", Value: "" }
                      : null,
                }),
                props: {
                  label: "Forward replies to",
                  inputType: "select",
                  isValidNewOption: () => false,
                  options: [
                    {
                      value: "workflow",
                      label: "This workflow on the sending thread",
                    },
                    {
                      value: "thread",
                      label: "All workflows on the sending thread",
                    },
                    {
                      value: "workflowUuid",
                      label: "Another workflow on the sending thread",
                    },
                  ],
                },
              },
              ...(getOptionValues("ReplyHandling")[0] === "workflowUuid"
                ? [
                    {
                      type: "option",
                      field: "ReplyWorkflowUUID",
                      defaultValue: [""],
                      props: {
                        label: "Reply workflow",
                        inputType: "select",
                        isValidNewOption: () => false,
                        options: workflows.map((wf) => ({
                          value: wf.UUID,
                          label: wf.Label || wf.Name || wf.UUID,
                        })),
                      },
                    },
                  ]
                : []),
              {
                type: "heading",
                label: "Milestone options",
                className: "mb-n1",
              },

              {
                type: "option",
                field: `MilestoneCreateMode`,
                defaultValue: ["create"],
                className: "mt-n3",
                props: {
                  label: "Existing milestones handling (same label)",
                  inputType: "select",
                  options: [
                    {
                      label: "Always create (ignore existing)",
                      value: "create",
                    },
                    {
                      label: "Do not create if exists",
                      value: "skip",
                    },
                    {
                      label:
                        "Always replace (cancel all existing and create new)",
                      value: "replace",
                    },
                    {
                      label:
                        "Replace existing milestones where the new one is later",
                      value: "replace-if-later",
                    },
                    {
                      label:
                        "Replace existing milestones where the new one is earlier",
                      value: "replace-if-earlier",
                    },
                  ],
                },
              },

              // milestone type interval/dateTime/useMilestone
              {
                type: "option",
                field: "MilestoneType",
                defaultValue: ["interval"],
                optionDefaults: ([{ Value: value }]) => {
                  const date = new Date();
                  return value === "interval"
                    ? {
                        variable: { ValueFrom: "static", Value: "1" },
                        MilestoneTimeZoneFrom: null,
                        OffsetFrom: null,
                        offsetType: null,
                        offset: null,
                        timezone: null,
                        DsMilestone: { ValueFrom: "static", Value: "no" },
                      }
                    : {
                        variable: {
                          ValueFrom: "static",
                          Value: `${date
                            .getFullYear()
                            .toString()
                            .padStart(4, "0")}-${(date.getMonth() + 1)
                            .toString()
                            .padStart(2, "0")}-${date
                            .getDate()
                            .toString()
                            .padStart(2, "0")} ${date
                            .getHours()
                            .toString()
                            .padStart(2, "0")}:${date
                            .getMinutes()
                            .toString()
                            .padStart(2, "0")}`,
                        },
                        timezone: { ValueFrom: "static", Value: "" },
                        offset: { ValueFrom: "static", Value: "0" },
                        offsetType: "positive",
                        DsMilestone: null,
                        DsMilestoneValue: null,
                      };
                },
                props: {
                  label: "Milestone type",
                  inputType: "select",
                  isValidNewOption: () => false,
                  options: [
                    { label: "Interval", value: "interval" },
                    { label: "Date/time", value: "dateTime" },
                  ],
                },
              },
              {
                type: "option",
                field: "MilestoneLabel",
                props: {
                  label: "Milestone label",
                  inputType: "select",
                  options: milestones.map((m) => ({
                    label: m,
                    value: m,
                  })),
                  isValidNewOption: (v) => v,
                  onCreateOption: (value) => value,
                },
              },
              {
                type: "compound",
                field: `DsMilestone`,
                defaultValue: [{ ValueFrom: "static", Value: "no" }],
                ifOptionTypeHas: [
                  { optionType: "MilestoneType", values: ["interval"] },
                ],
                optionDefaults: ([{ Value: value }]) => {
                  return value === "yes"
                    ? {
                        DsMilestoneValue: { ValueFrom: "static", Value: "no" },
                      }
                    : {
                        DsMilestoneValue: null,
                      };
                },
                props: {
                  label: "Do you want to set delivery status milestone?",
                  inputArgumentProps: { Format: "short-text" },
                  isValidNewOption: (v) => v.trim(),
                  inputType: "select",
                  options: [
                    { label: "No", value: "no" },
                    { label: "Yes", value: "yes" },
                  ],
                },
              },

              ...(getOptionValues("DsMilestone")[0] !== "no"
                ? [
                    {
                      type: "option",
                      field: "DsMilestoneLabel",
                      props: {
                        label: " Delivery Status Milestone label",
                        inputType: "select",
                        options: milestones.map((m) => ({
                          label: m,
                          value: m,
                        })),
                        isValidNewOption: (v) => v,
                        onCreateOption: (value) => value,
                      },
                    },
                    {
                      type: "compound",
                      field: `DsMilestoneValue`,
                      defaultValue: ["1"],
                      props: {
                        label: "Delivery Status Milestone value",
                        inputArgumentProps: { Format: "number" },
                        isValidNewOption: (v) => v.trim(),
                        type: "number",
                        min: 0,
                      },
                    },
                  ]
                : []),

              ...(getOptionValues("MilestoneType")[0] === "dateTime"
                ? [
                    {
                      type: "compound",
                      field: `MilestoneValue`,
                      defaultValue: ["1"],
                      ifOptionTypeHas: [
                        { optionType: "MilestoneType", values: ["dateTime"] },
                      ],
                      props: {
                        label: "Milestone value",
                        inputArgumentProps: { Format: "dateTime" },
                        isValidNewOption: (v) => v.trim(),
                        inputType: "dateTime",
                        dateTimeType: "dateTime",
                        isDateTimeUTC: false,
                      },
                    },
                  ]
                : []),

              // ...(getOptionValues(action, "MilestoneType")[0] === "interval" ? [
              {
                type: "compound",
                field: `MilestoneValue`,
                defaultValue: ["1"],
                ifOptionTypeHas: [
                  { optionType: "MilestoneType", values: ["interval"] },
                ],
                props: {
                  label: "Milestone value",
                  inputArgumentProps: { Format: "number" },
                  isValidNewOption: (v) => v.trim(),
                  type: "number",
                  min: 0,
                },
              },
              // ] : []),

              // show milestone unit
              {
                type: "property",
                field: "WithOffsetUnit",
                ifOptionTypeHas: [
                  { optionType: "MilestoneType", values: ["interval"] },
                ],
                defaultValue: ["minute"],
                optionDefaults: ([value]) =>
                  getOptionValues("MilestoneValue")[0] >
                  store.getState().features.featuresMaxMilestone *
                    timeConversion[value]
                    ? { MilestoneValue: { ValueFrom: "static", Value: "1" } }
                    : {},
                props: {
                  label: "Milestone unit",
                  inputType: "select",
                  isValidNewOption: () => false,
                  options: [
                    ...(pageProps.isLocal
                      ? [{ value: "second", label: "Second" }]
                      : []),
                    { value: "minute", label: "Minute" },
                    { value: "hour", label: "Hour" },
                    { value: "day", label: "Day" },
                  ],
                },
              },

              // show time select if variable is date only
              ...(inputArguments.find(
                (ia) => ia.UUID === getOptionValues("MilestoneValue")[0]
              )?.Format === "date"
                ? compoundInputs({
                    context,
                    field: "MilestoneTime",
                    label: "Milestone time from",
                    isDataCategory: false,
                    isIgnore: false,
                    ifOptionTypeHas: [
                      { optionType: "MilestoneType", values: ["dateTime"] },
                      {
                        optionType: "MilestoneFrom",
                        values: ["inputArgument"],
                      },
                    ],
                    filterFn: (arg) => ["time"].includes(arg.Format),
                    inputProps: {
                      dateTimeType: "time",
                    },
                  })
                : []),
              // milestone time zone
              {
                type: "heading",
                label: "Timezone override options",
                ifOptionTypeHas: [
                  { optionType: "MilestoneType", values: ["dateTime"] },
                ],
              },
              {
                type: "compound",
                field: `timezone`,
                defaultValue: [""],
                ifOptionTypeHas: [
                  { optionType: "MilestoneType", values: ["dateTime"] },
                ],
                props: {
                  label: "Timezone override",
                  inputArgumentProps: { Format: "short-text" },
                  isValidNewOption: (v) => v.trim(),
                  inputType: "select",
                  options: [
                    {
                      value: "",
                      label: `Project default (${pageProps.defaultTZ})`,
                    },
                    ...timezones.map((tz) => ({
                      value: tz.tzCode,
                      label: tz.label,
                    })),
                  ],
                },
              },

              // Offset options
              {
                type: "heading",
                label: "Offset options",
                ifOptionTypeHas: [
                  { optionType: "MilestoneType", values: ["dateTime"] },
                ],
              },

              {
                type: "compound",
                field: `offset`,
                defaultValue: ["0"],
                ifOptionTypeHas: [
                  { optionType: "MilestoneType", values: ["dateTime"] },
                ],
                props: {
                  label: "Milestone offset value",
                  inputArgumentProps: { Format: "number" },
                  isValidNewOption: (v) => v.trim(),
                  type: "number",
                  min: 0,
                },
              },

              {
                type: "option",
                field: "offsetType",
                defaultValue: ["positive"],
                ifOptionTypeHas: [
                  { optionType: "MilestoneType", values: ["dateTime"] },
                ],
                props: {
                  label: "Offset type",
                  inputType: "select",
                  isValidNewOption: () => false,
                  options: [
                    { value: "positive", label: "Positive" },
                    { value: "negative", label: "Negative" },
                  ],
                },
              },
              // show milestone unit
              {
                type: "property",
                field: "WithOffsetUnit",
                defaultValue: ["minute"],
                ifOptionTypeHas: [
                  { optionType: "MilestoneType", values: ["dateTime"] },
                ],
                props: {
                  label: "Offset unit",
                  inputType: "select",
                  isValidNewOption: () => false,
                  options: [
                    ...(pageProps.isLocal
                      ? [{ value: "second", label: "Second" }]
                      : []),
                    { value: "minute", label: "Minute" },
                    { value: "hour", label: "Hour" },
                    { value: "day", label: "Day" },
                  ],
                },
              },

              ...businessHoursInputs({ context, action, params }),
            ]
          : []),
      ],
    },
    setDynamicComms: {
      inputs: [
        {
          type: "option",
          field: "mode",
          defaultValue: [
            "message:reply",
            "outbound-delivery-status",
            "form-render",
            "form-submitted",
            "form-accessed",
          ].includes(event)
            ? ["current"]
            : ["contactField"],
          optionDefaults: ([{ Value: v }]) =>
            // eslint-disable-next-line no-nested-ternary
            v === "channel"
              ? {
                  channel: { ValueFrom: "static", Value: "sms" },
                  contactFieldUUID: null,
                }
              : v === "contactField"
              ? {
                  channel: selectedFieldChannels([
                    fieldsWithChannels[0]?.uuid,
                  ])[0],
                  contactFieldUUID: fieldsWithChannels[0]?.uuid,
                }
              : { channel: null, contactFieldUUID: null },
          props: {
            label: "Set dynamic comms",
            inputType: "select",
            options: [
              ...([
                "message:reply",
                "outbound-delivery-status",
                "form-render",
                "form-submitted",
                "form-accessed",
              ].includes(event)
                ? [
                    {
                      value: "current",
                      label: "Set current recipient comms",
                    },
                  ]
                : []),
              { value: "contactField", label: "Contact field" },
              { value: "channel", label: "Channel" },
              { value: "nextEscalation", label: "Next escalation" },
              { value: "resetComms", label: "Reset dynamic comms channel" },
            ],
          },
        },
        // contact field
        {
          type: "option",
          field: "contactFieldUUID",
          defaultValue: [
            "message:reply",
            "outbound-delivery-status",
            "form-render",
            "form-submitted",
            "form-accessed",
          ].includes(event)
            ? undefined
            : [fieldsWithChannels[0]?.uuid],
          ifOptionTypeHas: [{ optionType: "mode", values: ["contactField"] }],
          optionDefaults: (values) => ({
            channel: selectedFieldChannels(values.map((v) => v.Value))[0],
          }),
          props: {
            label: "Contact field",
            inputType: "select",
            options: contactFields
              .filter((cf) => cf.isSms || cf.isEmail || cf.isVoice)
              .map((f) => ({
                value: f.uuid,
                label: `${f.label} (${[
                  ...(f.isSms ? ["sms"] : []),
                  ...(f.isEmail ? ["email"] : []),
                  ...(f.isVoice ? ["voice"] : []),
                ].join(", ")})`,
              })),
          },
        },
        // show channels if the selected field is capable of multiple channels
        ...(selectedFieldChannels().length > 1
          ? [
              {
                type: "option",
                field: "channel",
                // TODO
                props: {
                  label: "Preferred channel",
                  inputType: "select",
                  isValidNewOption: () => false,
                  options: [
                    { value: "sms", label: "SMS" },
                    { value: "email", label: "e-mail" },
                    { value: "voice", label: "Voice" },
                  ].filter((opt) =>
                    selectedFieldChannels().includes(opt.value)
                  ),
                },
              },
            ]
          : []),
        // show channel input
        {
          type: "compound",
          field: "channel",
          defaultValue: [{ ValueFrom: "static", Value: "sms" }],
          ifOptionTypeHas: [{ optionType: "mode", values: ["channel"] }],
          props: {
            inputType: "select",
            label: "Communication channel",
            inputArgumentProps: { Format: "short-text" },
            isValidNewOption: () => false,
            options: [
              { value: "sms", label: "SMS" },
              { value: "email", label: "e-mail" },
              { value: "voice", label: "Voice" },
            ],
          },
        },
      ],
    },

    // asset:groups-to-variable

    // [
    // {"Type": "variable", Value: "uuid-of-asset-variable" , MainThread: true/false} // copy groups from selected assets
    // {Type: "variableTo", Value: "uuid-of-group-variable",  MainThread: true/false,  Operation: "append"}// copy groups to this variable
    // ]

    "asset:groups-to-variable": {
      inputs: [
        {
          type: "compound",
          field: "variable",
          defaultValue: [""],
          props: {
            label: "Asset variable",
            isInputArgumentOnly: true,
            inputArgumentProps: { Format: "asset" },
            isValidNewOption: (v) => v.trim(),
          },
        },
        {
          type: "compound",
          field: `variableTo`,
          defaultValue: [""],
          optionDefaults: ([{ Value: v }]) => ({
            ...(!inputArguments.find((arg) => arg.UUID === v)?.IsMultiple &&
            getOptionValues("operation")[0] === "append"
              ? { operation: "replace" }
              : {}),
          }),
          props: {
            label: "Group variable to copy to",
            isInputArgumentOnly: true,
            inputArgumentProps: { Format: "group", IsReadonly: false },
            showOperationSelector: true,
          },
        },
      ],
    },

    "json:to-asset": {
      inputs: [
        {
          type: "option",
          field: "operation",
          defaultValue: ["both"],
          props: {
            label: "Operation",
            inputType: "radio",
            radioName: "json-to-asset-operation",
            options: [
              { value: "create", label: "Create" },
              { value: "update", label: "Update" },
              { value: "both", label: "Create or update" },
            ],
          },
        },
        {
          type: "compound",
          field: "json",
          defaultValue: [""],
          props: {
            label: "Source JSON variable",
            isInputArgumentOnly: true,
            inputArgumentFilter: (arg) =>
              arg.Format === "json" && !!arg.DataStructure,
            isValidNewOption: (v) => v.trim(),
          },
        },
        {
          type: "compound",
          field: "variableTo",
          defaultValue: [{ Value: "", Operation: "replace" }],
          props: {
            label: "Store asset in variable",
            isInputArgumentOnly: true,
            inputArgumentProps: { Format: "asset", IsReadonly: false },
            showOperationSelector: true,
            isValidNewOption: (v) => v.trim(),
          },
        },
        ...(jsonToAssetUsesUpdate
          ? [
              {
                type: "option",
                field: "asset:match-field",
                defaultValue: [],
                props: {
                  label: "Match existing assets by",
                  inputType: "select",
                  isMulti: true,
                  isClearable: true,
                  isValidNewOption: () => false,
                  options: jsonToAssetMatchFields,
                },
              },
              {
                type: "option",
                field: "asset:skip-empty-update",
                defaultValue: ["true"],
                props: {
                  label: "Skip empty updates",
                  inputType: "radio",
                  radioName: "json-to-asset-skip-empty-update",
                  options: [
                    { value: "true", label: "Yes" },
                    { value: "false", label: "No" },
                  ],
                },
              },
            ]
          : []),
        {
          type: "compound",
          field: "asset:field-map",
          defaultValue: jsonToAssetDefaultMappings,
          props: {
            label: "Asset field mappings",
            isMulti: true,
            disableMultiControls: true,
            isStaticOnly: true,
            inputType: "select",
            options: selectedJsonToAssetDataStructureFields,
            showFixedKeyLabel: true,
            keyInputOptions: jsonToAssetFields,
            defaultValueInputLabel: "Default value",
            showDefaultValueCompoundInput: true,
            defaultValueInputArgumentProps: {
              Format: "short-text",
              IsReadonly: false,
            },
            isValidNewOption: () => false,
          },
        },
        {
          type: "compound",
          field: "contact",
          defaultValue: [{ Value: "", MainThread: false }],
          props: {
            label: "Assign contacts from variable",
            isInputArgumentOnly: true,
            inputArgumentProps: { Format: "contact" },
            isClearable: true,
          },
        },
        {
          type: "compound",
          field: "asset:parent",
          defaultValue: [{ Value: "", MainThread: false }],
          props: {
            label: "Assign parent assets from variable",
            isInputArgumentOnly: true,
            inputArgumentProps: { Format: "asset" },
            isClearable: true,
          },
        },
        {
          type: "compound",
          field: "asset:child",
          defaultValue: [{ Value: "", MainThread: false }],
          props: {
            label: "Assign child assets from variable",
            isInputArgumentOnly: true,
            inputArgumentProps: { Format: "asset" },
            isClearable: true,
          },
        },
        {
          type: "compound",
          field: "geo-json",
          defaultValue: [""],
          props: {
            label: "Assign locations from GeoJSON variable",
            isInputArgumentOnly: true,
            inputArgumentProps: { Format: "geo-json" },
            isClearable: true,
          },
        },
      ],
    },

    RemoveAllFromContactSet: {
      label: "Group options",
      inputs: [
        {
          type: "compound",
          field: "group",
          defaultValue: [""],
          props: {
            label: "Contact group",
            inputType: "contactSet",
            contactSetType: "static",
            inputArgumentProps: { Format: "group", IsMultiple: false },
            isValidNewOption: (v) => v.trim(),
          },
        },
      ],
    },
    AddResponderToContactSet: {
      label: "Group options",
      inputs: [
        {
          type: "compound",
          field: "group",
          defaultValue: [""],
          props: {
            label: "Contact group",
            inputType: "contactSet",
            contactSetType: "static",
            inputArgumentProps: { Format: "group", IsMultiple: false },
            isValidNewOption: (v) => v.trim(),
          },
        },
      ],
    },
    RemoveResponderFromVariable: {
      label: "Variable options",
      inputs: [
        {
          type: "compound",
          field: "variable",
          defaultValue: [""],
          props: {
            label: "Variable to remove recipient from",
            isInputArgumentOnly: true,
            inputArgumentProps: {
              Format: "contact",
              IsMultiple: true,
              IsReadonly: false,
            },
            isValidNewOption: (v) => v.trim(),
          },
        },
      ],
    },
    RemoveResponderFromContactSet: {
      label: "Group options",
      inputs: [
        {
          type: "compound",
          field: "group",
          defaultValue: [""],
          props: {
            label: "Contact group",
            inputType: "contactSet",
            contactSetType: "static",
            inputArgumentProps: { Format: "group", IsMultiple: false },
            isValidNewOption: (v) => v.trim(),
          },
        },
      ],
    },
    AddStarterToContactSet: {
      label: "Group options",
      inputs: [
        {
          type: "compound",
          field: "group",
          defaultValue: [""],
          props: {
            label: "Contact group",
            inputType: "contactSet",
            contactSetType: "static",
            inputArgumentProps: { Format: "group", IsMultiple: false },
            isValidNewOption: (v) => v.trim(),
          },
        },
      ],
    },

    RemoveStarterFromContactSet: {
      label: "Group options",
      inputs: [
        {
          type: "compound",
          field: "group",
          defaultValue: [""],
          props: {
            label: "Contact group",
            inputType: "contactSet",
            contactSetType: "static",
            inputArgumentProps: { Format: "group", IsMultiple: false },
            isValidNewOption: (v) => v.trim(),
          },
        },
      ],
    },

    "restart:runtime": {
      label: "Restart runtime options",
      inputs: [
        {
          type: "option",
          field: "RestartType",
          defaultValue: ["ScheduleOrRestart"],
          props: {
            inputType: "select",
            isValidNewOption: () => false,
            options: [
              { label: "If not running", value: "IfNotRunning" },
              {
                label: "Schedule if running",
                value: "ScheduleIfRunning",
              },
              {
                label: "Schedule or restart",
                value: "ScheduleOrRestart",
              },
            ],
          },
        },
      ],
    },

    setMilestone: {
      label: "Create milestone options",
      inputs: [
        {
          type: "option",
          field: "MilestoneLabel",
          props: {
            label: "Milestone label",
            inputType: "select",
            options: scheduledMilestones.map((m) => ({
              label: m,
              value: m,
            })),
            isValidNewOption: (v) => v,
            onCreateOption: (value) => value,
          },
        },
        {
          type: "option",
          field: `MilestoneCreateMode`,
          defaultValue: ["create"],
          optionDefaults: () => ({
            ExtendTwoWayValidity: "false",
          }),
          className: "mt-n3",
          props: {
            label: "Existing milestones handling (same label)",
            inputType: "select",
            options: [
              { label: "Always create (ignore existing)", value: "create" },
              { label: "Do not create if exists", value: "skip" },
              {
                label: "Always replace (cancel all existing and create new)",
                value: "replace",
              },
              {
                label: "Replace existing milestones where the new one is later",
                value: "replace-if-later",
              },
              {
                label:
                  "Replace existing milestones where the new one is earlier",
                value: "replace-if-earlier",
              },
            ],
          },
        },
        {
          type: "option",
          field: "ExtendTwoWayValidity",
          defaultValue: ["false"],
          ifOptionTypeHas: [
            {
              optionType: "MilestoneCreateMode",
              values: ["replace", "replace-if-later", "replace-if-earlier"],
            },
          ],
          optionDefaults: ([{ Value }]) => (Value === "true" ? {} : {}),
          props: {
            label: "Extend 2-way validity",
            inputType: "select",
            isValidNewOption: () => false,
            options: [
              { label: "No", value: "false" },
              { label: "Yes", value: "true" },
            ],
          },
        },
        {
          type: "html",
          field: "ExtendTwoWayValidityAffectedBroadcastActions",
          ifOptionTypeHas: [
            {
              optionType: "MilestoneCreateMode",
              values: ["replace", "replace-if-later", "replace-if-earlier"],
            },
            { optionType: "ExtendTwoWayValidity", values: ["true"] },
          ],
          html: (
            <div>
              <InputLabel>Affected broadcast actions</InputLabel>
              {affectedBroadcastActions.length > 0 ? (
                <div className="list-group mt-2">
                  {affectedBroadcastActions.map((broadcastAction) => (
                    <Link
                      className="list-group-item list-group-item-action py-2"
                      key={broadcastAction.uuid}
                      to={broadcastAction.path}
                    >
                      {broadcastAction.label}
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-muted mt-2">
                  No matching 2-way broadcast actions
                </div>
              )}
            </div>
          ),
        },
        ...milestoneInputs({ action, context, rule, pageProps }),
        ...businessHoursInputs({ context, action, params }),
      ],
    },

    /*
				Cancel a milestone by name or uuid.
				{
					"Type": "milestone:cancel",
					"Options": [
						// Which runtime,
						// "this-runtime" or empty (default) for the current runtime
						// or "all-runtimes" to cancel the milestone in all runtimes or "other-runtimes" to exclude this runtime
						{"Type": "milestone-runtimes", "Value": "this-runtime"/"all-runtimes"/"other-runtimes"},
						// Specific milestone labels or all milestone labels in the runtime(s) will be cancelled based on the following options:
						{"Type": "any-label", "Value": "true"}, // default: "false". If true, then at least one of the provided labels must match to cancel the milestone(s)
						// show only if any-label is false or not provided
						{"Type": "isMilestone", "Value": "Label 1"},
						{"Type": "isMilestone", "Value": "Label 2"}
					]
				}
			}
		*/

    "milestone:cancel": {
      label: "Cancel milestone options",
      inputs: [
        {
          type: "option",
          field: "milestone-runtimes",
          defaultValue: ["this-runtime"],
          props: {
            label: "Milestone runtime",
            inputType: "select",
            isValidNewOption: () => false,
            options: [
              { label: "This runtime", value: "this-runtime" },
              { label: "Other runtimes", value: "other-runtimes" },
              { label: "All runtimes", value: "all-runtimes" },
            ],
          },
        },
        {
          type: "option",
          field: "any-label",
          defaultValue: ["false"],
          props: {
            label: "Milestones with a particular label or all milestones",
            inputType: "select",
            isValidNewOption: () => false,
            options: [
              { label: "With particular label", value: "false" },
              { label: "All milestones", value: "true" },
            ],
          },
        },
        {
          type: "option",
          field: "isMilestone",
          ifOptionTypeHas: [{ optionType: "any-label", values: ["false"] }],
          props: {
            label: "Milestone label",
            isMulti: true,
            isValidNewOption: () => false,
            options: milestones.map((m) => ({ label: m, value: m })),
          },
        },
      ],
    },

    //  Accept a package from another component. This action is available only in component:package event.
    // 	{
    // 		"Type": "component:accept-package",
    // 		"Options": [

    "component:accept-package": {
      label: "Accept component package options",
      event: "component:package",
      inputs: [
        // 			// VARIABLE to store the received data, required, any variable format, single or multiple
        // 			{"Type": "variable", "Value": "variable-uuid-123", "MainThread": "true"/"false", "Operation": "replace"},
        {
          type: "compound",
          field: `variable`,
          defaultValue: [""],
          props: {
            label: "Variable to store the received data (required)",
            isValidNewOption: (v) => v.trim(),
            isInputArgumentOnly: true,
            showOperationSelector: true,
            inputArgumentProps: {
              IsReadonly: false,
            },
          },
        },
        // 			// LABEL to store the identify the package, optional, short-text variable, single/multiple
        // 			{"Type": "label", "Value": "variable-uuid-123", "MainThread": "true"/"false", "Operation": "replace"}
        {
          type: "compound",
          field: `label`,
          defaultValue: [""],
          props: {
            label: "Label to identify the package (optional)",
            inputArgumentProps: { Format: "short-text" },
            isValidNewOption: (v) => v.trim(),
            isInputArgumentOnly: true,
            showOperationSelector: true,
            inputArgumentProps: {
              IsReadonly: false,
            },
          },
        },
        // 		]
        // 	}
      ],
    },

    //    Send a variable values with a label attached to another component process by guid.
    // 		Any variable format can be sent. Receiving process will validate the data.
    // 		Receiving process will accept the data in the component:package event.
    // 		The package needs a label to identify the data in the receiving process.
    // 		{
    "component:package": {
      // 			"Type": "component:package",
      inputs: [
        {
          type: "option",
          field: "self",
          defaultValue: ["false"],
          props: {
            inputType: "radio",
            radioName: "self",
            label: "Send to self (same process)",
            options: [
              { value: "false", label: "No" },
              { value: "true", label: "Yes" },
            ],
          },
        },
        // 			"Options": [
        // 				// PROCESS to send the data to, required
        // 				// short-text variable, can be multiple
        // 				{"Type": "process", "Value": "variable-where-guids-are-stored-123", "MainThread": true},
        {
          type: "compound",
          field: `process`,
          defaultValue: [""],
          ifOptionTypeHas: [{ optionType: "self", values: ["false"] }],
          props: {
            label: "Process to send data to (guid)",
            isInputArgumentOnly: true,
            inputArgumentProps: { Format: "short-text" },
            isValidNewOption: (v) => v.trim(),
          },
        },
        // 				// LABEL to identify the data package in the receiving process, required, static only
        // 				{"Type": "label", "Value": "USEFUL LABEL", "ValueFrom": "static"},
        {
          type: "compound",
          field: `label`,
          defaultValue: [""],
          props: {
            label:
              "Label to identify the data package in the receiving process (required)",
            isStaticOnly: true,
            isValidNewOption: (v) => v.trim(),
          },
        },
        // 				// DATA to send, required, any variable format, single or multiple or static value
        // 				{"Type": "data", "Value": "variable-uuid-123", "ValueFrom": "inputArgument", "MainThread": true/false}
        {
          type: "compound",
          field: `data`,
          defaultValue: [""],
          props: {
            label: "Data to send",
            isValidNewOption: (v) => v.trim(),
          },
        },
        // 			]
        // 		}
      ],
    },
    StoreReplyToVariable: {
      label: "",
      inputs: [
        {
          type: "compound",
          field: `variable`,
          defaultValue: [""],
          optionDefaults: ([{ Value: v }]) => ({
            ...(!inputArguments.find((arg) => arg.UUID === v)?.IsMultiple &&
            getOptionValues("operation")[0] === "append"
              ? { operation: "replace" }
              : {}),
          }),
          props: {
            label: "Variable name",
            isInputArgumentOnly: true,
            isValidNewOption: (v) => v.trim(),
            showOperationSelector: true,
            inputArgumentProps: { IsReadonly: false },
          },
        },
      ],
    },

    UpdateVariable: {
      label: "",
      inputs: [
        {
          type: "compound",
          field: `variable`,
          defaultValue: [""],
          optionDefaults: ([{ Value: v }]) => ({
            "extract:reference-by": null,
            "extract:geojson-resolve": null,
            ...(!inputArguments.find((arg) => arg.UUID === v)?.IsMultiple &&
            getOptionValues("operation")[0] === "append"
              ? { operation: "replace" }
              : {}),
          }),
          props: {
            label: "Variable name",
            isInputArgumentOnly: true,
            inputArgumentProps: { IsReadonly: false },
            inputArgumentFilter: (arg) =>
              arg.Format !== "contact" && !arg.IsReadonly,
            isValidNewOption: (v) => v.trim(),
            showOperationSelector: true,
          },
        },
        {
          type: "option",
          field: "data",
          props: {
            inputType: "textArea",
            valueType: "data",
            label: "Content",
            maxLength: 64000,
          },
        },
      ],
    },
    "variable:set-time": {
      label: "",
      inputs: [
        {
          type: "compound",
          field: "variable",
          defaultValue: [""],
          optionDefaults: () => ({ dateTime: null }),
          props: {
            label: "Time variable to set",
            isInputArgumentOnly: true,
            isValidNewOption: (v) => v.trim(),
            inputArgumentProps: {
              Format: ["date", "time", "dateTime"],
              DefaultTimeZone: pageProps.defaultTZ,
              IsMultiple: false,
              IsReadonly: false,
            },
          },
        },
        {
          type: "compound",
          field: "dateTime",
          defaultValue: [{ ValueFrom: "static", Value: "" }],
          props: {
            label: "Value",
            inputType: "dateTime",
            dateTimeType:
              inputArguments.find(
                (arg) => arg.UUID === getOptionValues("variable")[0]
              )?.Format || "dateTime",
            isValidNewOption: () => false,
            inputArgumentFilter: (arg) => {
              const target = inputArguments.find(
                (input) => input.UUID === getOptionValues("variable")[0]
              );
              return (
                !!target &&
                arg.UUID !== target.UUID &&
                arg.Format === target.Format &&
                !arg.IsMultiple
              );
            },
            inputArgumentProps: { IsMultiple: false },
            customValueFromOptions: [{ value: "now", label: "Now" }],
          },
        },
        {
          type: "compound",
          field: "Offset",
          defaultValue: [
            {
              ValueFrom: "static",
              Value: "0",
              Unit: "minute",
              Sign: "positive",
            },
          ],
          props: {
            label: "Offset",
            type: "number",
            inputArgumentProps: { Format: ["number"], IsMultiple: false },
            isValidNewOption: () => false,
            showUnitInput: true,
            showSignInput: true,
          },
        },
      ],
    },
    startedByToVariable: {
      inputs: [
        {
          type: "compound",
          field: `variable`,
          defaultValue: [""],
          optionDefaults: ([{ Value: v }]) => ({
            ...(!inputArguments.find((arg) => arg.UUID === v)?.IsMultiple &&
            getOptionValues("operation")[0] === "append"
              ? { operation: "replace" }
              : {}),
          }),
          props: {
            label: "Variable name",
            isInputArgumentOnly: true,
            inputArgumentProps: { Format: "contact", IsReadonly: false },
            isValidNewOption: (v) => v.trim(),
            showOperationSelector: true,
          },
        },
      ],
    },
    recipientToVariable: {
      inputs: [
        {
          type: "compound",
          field: `variable`,
          defaultValue: [""],
          optionDefaults: ([{ Value: v }]) => ({
            ...(!inputArguments.find((arg) => arg.UUID === v)?.IsMultiple &&
            getOptionValues("operation")[0] === "append"
              ? { operation: "replace" }
              : {}),
          }),
          props: {
            label: "Variable name",
            isInputArgumentOnly: true,
            inputArgumentProps: { Format: "contact", IsReadonly: false },
            isValidNewOption: (v) => v.trim(),
            showOperationSelector: true,
          },
        },
      ],
    },
    "workflow:contacts-to-variable": {
      inputs: [
        {
          type: "compound",
          field: `variable`,
          defaultValue: [""],
          optionDefaults: ([{ Value: v }]) => ({
            ...(!inputArguments.find((arg) => arg.UUID === v)?.IsMultiple &&
            getOptionValues("operation")[0] === "append"
              ? { operation: "replace" }
              : {}),
          }),
          props: {
            label: "Variable name",
            isInputArgumentOnly: true,
            inputArgumentProps: { Format: "contact", IsReadonly: false },
            isValidNewOption: (v) => v.trim(),
            showOperationSelector: true,
          },
        },
      ],
    },
    mergeVariables: {
      inputs: [
        {
          type: "compound",
          field: `variable`,
          defaultValue: [""],
          optionDefaults: ([{ Value: v }]) => ({
            variableTo: null,
            ...(!inputArguments.find((arg) => arg.UUID === v)?.IsMultiple &&
            getOptionValues("operation")[0] === "append"
              ? { operation: "replace" }
              : {}),
          }),
          props: {
            label: "Variable to copy from",
            isInputArgumentOnly: true,
            showParentThreadOption: isParentWorkflowNewThread,
          },
        },
        {
          type: "compound",
          field: `variableTo`,
          defaultValue: [""],
          optionDefaults: ([{ Value: v }]) => ({
            ...(!inputArguments.find((arg) => arg.UUID === v)?.IsMultiple &&
            getOptionValues("operation")[0] === "append"
              ? { operation: "replace" }
              : {}),
          }),
          props: {
            label: "Variable to copy to",
            uniqueKey: getOptionValues("variable")[0],
            isInputArgumentOnly: true,
            inputArgumentProps: { IsReadonly: false },
            inputArgumentFilter: (arg) => {
              if (arg.IsReadonly) return false;
              if (arg.UUID === getOptionValues("variable")[0]) {
                return false;
              }
              const ia = inputArguments.find(
                (arg) => arg.UUID === getOptionValues("variable")[0]
              );
              if (!ia) return false;
              if (
                ["sms", "voice", "email"].includes(ia.Format) &&
                arg.Format === "contact"
              ) {
                return true;
              }
              return arg.Format === ia.Format;
            },
            isValidNewOption: (v) => v.trim(),
            showOperationSelector: true,
            disableThreadOption: [...(action.Options || [])].find(
              (actOpt) => actOpt.Type === "variable"
            )?.FromParentThread,
          },
        },
      ],
    },
    "get-filenames": {
      inputs: [
        {
          type: "compound",
          field: `variable`,
          defaultValue: [""],
          optionDefaults: ([{ Value: v }]) => ({
            variableTo: null,
            ...(!inputArguments.find((arg) => arg.UUID === v)?.IsMultiple &&
            getOptionValues("operation")[0] === "append"
              ? { operation: "replace" }
              : {}),
          }),
          props: {
            label: "Variable to copy from",
            isInputArgumentOnly: true,
            inputArgumentProps: { Format: "binary" },
            isValidNewOption: (v) => v.trim(),
          },
        },
        {
          type: "compound",
          field: `variableTo`,
          defaultValue: [""],
          optionDefaults: ([{ Value: v }]) => ({
            ...(!inputArguments.find((arg) => arg.UUID === v)?.IsMultiple &&
            getOptionValues("operation")[0] === "append"
              ? { operation: "replace" }
              : {}),
          }),
          props: {
            label: "Variable to copy to",
            uniqueKey: getOptionValues("variable")[0],
            isInputArgumentOnly: true,
            inputArgumentProps: { Format: "short-text", IsReadonly: false },
            showOperationSelector: true,
          },
        },
      ],
    },

    "file:store-permanently": {
      inputs: [
        {
          type: "compound",
          field: `variable`,
          defaultValue: [""],
          optionDefaults: ([{ Value: v }]) => ({
            variableTo: null,
            ...(!inputArguments.find((arg) => arg.UUID === v)?.IsMultiple &&
            getOptionValues("operation")[0] === "append"
              ? { operation: "replace" }
              : {}),
          }),
          props: {
            label: "Temporary file variable",
            isInputArgumentOnly: true,
            inputArgumentProps: { Format: "binary" },
            isValidNewOption: (v) => v.trim(),
          },
        },
        {
          type: "compound",
          field: `file:name`,
          defaultValue: [""],
          props: {
            label: "Rename file to (optional)",
            inputArgumentProps: { Format: "short-text", IsMultiple: false },
            isValidNewOption: (v) => v.trim(),
            isClearable: true,
          },
        },
        {
          type: "compound",
          field: `variableTo`,
          defaultValue: [""],
          optionDefaults: ([{ Value: v }]) => ({
            ...(!inputArguments.find((arg) => arg.UUID === v)?.IsMultiple &&
            getOptionValues("operation")[0] === "append"
              ? { operation: "replace" }
              : {}),
          }),
          props: {
            label: "Store permanent reference in (optional)",
            uniqueKey: getOptionValues("variable")[0],
            isInputArgumentOnly: true,
            inputArgumentProps: { Format: "binary", IsReadonly: false },
            showOperationSelector: true,
            isClearable: true,
          },
        },
      ],
    },

    "file:rows-to-json-variable": {
      inputs: [
        {
          type: "compound",
          field: `variable`,
          defaultValue: [{ Value: "", MainThread: true }],
          props: {
            label: "File variable",
            isInputArgumentOnly: true,
            inputArgumentProps: { Format: "binary" },
            isValidNewOption: (v) => v.trim(),
          },
        },
        {
          type: "compound",
          field: `variableTo`,
          defaultValue: [""],
          optionDefaults: ([{ Value: v }]) => ({
            "file:required-values": null,
            ...(!inputArguments.find((arg) => arg.UUID === v)?.IsMultiple &&
            getOptionValues("operation")[0] === "append"
              ? { operation: "replace" }
              : {}),
          }),
          props: {
            label: "Target JSON variable",
            uniqueKey: getOptionValues("variable")[0],
            isInputArgumentOnly: true,
            inputArgumentProps: { Format: "json", IsReadonly: false },
            showOperationSelector: true,
            isValidNewOption: (v) => v.trim(),
          },
        },
        ...(selectedFileRowsTargetVariable?.DataStructure &&
        selectedFileRowsDataStructureFields.length
          ? [
              {
                type: "option",
                field: "file:required-values",
                defaultValue: [],
                props: {
                  label: "Required values",
                  inputType: "select",
                  isMulti: true,
                  isClearable: true,
                  isValidNewOption: () => false,
                  options: selectedFileRowsDataStructureFields,
                },
              },
              {
                type: "option",
                field: "file:reject-on-data-structure-mismatch",
                defaultValue: ["false"],
                props: {
                  label: "Reject on data structure mismatch",
                  inputType: "radio",
                  radioName: "file:reject-on-data-structure-mismatch",
                  options: [
                    { value: "true", label: "Yes" },
                    { value: "false", label: "No" },
                  ],
                },
              },
            ]
          : []),
      ],
    },

    "variables:move-one-to-another": {
      inputs: [
        {
          type: "html",
          html: (
            <p>
              Move one item from a variable to another variable. If the variable
              types are not compatible, the process will fail.
            </p>
          ),
        },
        {
          type: "compound",
          field: `variable`,
          defaultValue: [""],
          optionDefaults: ([{ Value: v }]) => ({
            variableTo: null,
          }),
          props: {
            label: "Move from a variable",
            isInputArgumentOnly: true,
          },
        },
        {
          type: "compound",
          field: `variableTo`,
          defaultValue: [""],
          optionDefaults: ([{ Value: v }]) => ({
            ...(!inputArguments.find((arg) => arg.UUID === v)?.IsMultiple &&
            getOptionValues("operation")[0] === "append"
              ? { operation: "replace" }
              : {}),
          }),
          props: {
            label: "Variable to move the item to",
            uniqueKey: getOptionValues("variable")[0],
            isInputArgumentOnly: true,
            inputArgumentProps: { IsReadonly: false },
            inputArgumentFilter: (arg) => {
              if (arg.IsReadonly) return false;
              if (arg.UUID === getOptionValues("variable")[0]) {
                return false;
              }
              const ia = inputArguments.find(
                (arg) => arg.UUID === getOptionValues("variable")[0]
              );
              if (!ia) return false;
              if (
                ["sms", "voice", "email"].includes(ia.Format) &&
                arg.Format === "contact"
              ) {
                return true;
              }
              if (["short-text"].includes(ia.Format)) {
                return ["short-text", "number", "json", "date"].includes(
                  arg.Format
                );
              }
              return arg.Format === ia.Format;
            },
            isValidNewOption: (v) => v.trim(),
            showOperationSelector: true,
          },
        },
        {
          type: "option",
          field: "last",
          defaultValue: ["false"],
          props: {
            inputType: "radio",
            radioName: "last",
            label: "Order",
            options: [
              { value: "false", label: "First" },
              { value: "true", label: "Last" },
            ],
          },
        },
      ],
    },

    "variables:delete-one": {
      inputs: [
        {
          type: "compound",
          field: `variable`,
          defaultValue: [""],
          props: {
            label: "Delete one item from variable",
            inputArgumentProps: { IsReadonly: false },
            isInputArgumentOnly: true,
          },
        },
        {
          type: "option",
          field: "last",
          defaultValue: ["false"],
          props: {
            inputType: "radio",
            radioName: "last",
            label: "Order",
            options: [
              { value: "true", label: "First" },
              { value: "false", label: "Last" },
            ],
          },
        },
      ],
    },

    subtractVariables: {
      inputs: [
        {
          type: "compound",
          field: `variable`,
          defaultValue: [""],
          optionDefaults: ([{ Value: v }]) => ({
            variableTo: null,
            ...(!inputArguments.find((arg) => arg.UUID === v)?.IsMultiple &&
            getOptionValues("operation")[0] === "append"
              ? { operation: "replace" }
              : {}),
          }),
          props: {
            label: "Variable to remove from",
            inputArgumentFilter: (arg) =>
              arg.Format !== "json" && !arg.IsReadonly,
            inputArgumentProps: { IsReadonly: false },
            isInputArgumentOnly: true,
          },
        },
        {
          type: "compound",
          field: `variableTo`,
          defaultValue: [""],
          optionDefaults: ([{ Value: v }]) => ({
            ...(!inputArguments.find((arg) => arg.UUID === v)?.IsMultiple &&
            getOptionValues("operation")[0] === "append"
              ? { operation: "replace" }
              : {}),
          }),
          props: {
            label: "Variable to match against",
            uniqueKey: getOptionValues("variable")[0],
            isInputArgumentOnly: true,
            inputArgumentProps: {
              ...(getOptionValues("variable")[0]
                ? {
                    Format: inputArguments.find(
                      (arg) => arg.UUID === getOptionValues("variable")[0]
                    )?.Format,
                  }
                : {}),
            },
            isValidNewOption: (v) => v.trim(),
          },
        },
      ],
    },
    uniqueVariables: {
      inputs: [
        {
          type: "compound",
          field: `variable`,
          defaultValue: [""],
          optionDefaults: ([{ Value: v }]) => ({
            ...(!inputArguments.find((arg) => arg.UUID === v)?.IsMultiple &&
            getOptionValues("operation")[0] === "append"
              ? { operation: "replace" }
              : {}),
          }),
          props: {
            label: "Variable to remove duplicates from",
            inputArgumentFilter: (arg) =>
              arg.Format !== "json" && !arg.IsReadonly,
            inputArgumentProps: { IsReadonly: false },
            isInputArgumentOnly: true,
          },
        },
      ],
    },

    // {
    // 	"Type": "assets:load-contacts-to-context", <<<<<< OLD
    // 	"Type": "assets:contacts-to-variable", <<<< NEW
    "assets:contacts-to-variable": {
      // 	"Options": [
      inputs: [
        // 		// FROM assets
        // 		// uuid of the variable to load the assets from, must be of asset format (single/multiple)
        // 		{"Type": "asset", "Value": "variable-uuid", "MainThread": true/false},
        {
          type: "compound",
          field: `asset`,
          defaultValue: [""],
          props: {
            label: "Variable to load the assets from",
            isInputArgumentOnly: true,
            inputArgumentProps: { Format: "asset" },
            isValidNewOption: (v) => false,
          },
        },
        // 		// STORE TO contacts
        // 		// uuid of the variable to store the contacts in
        // 		// must be of contact format (single/multiple)
        // 		{"Type": "contact", "Value": "variable-uuid", "MainThread": true/false},
        {
          type: "compound",
          field: `contact`,
          defaultValue: [""],
          optionDefaults: ([{ Value: v }]) => ({
            ...(!inputArguments.find((arg) => arg.UUID === v)?.IsMultiple &&
            getOptionValues("operation")[0] === "append"
              ? { operation: "replace" }
              : {}),
          }),
          props: {
            label: "Variable to store the contacts in",
            isInputArgumentOnly: true,
            inputArgumentProps: { Format: "contact", IsReadonly: false },
            isValidNewOption: (v) => v.trim(),
            showOperationSelector: true,
          },
        },

        // 	]
      ],
      // }
    },

    "assets:to-variable": {
      // Load assets to asset variable.
      // Filter by area, name (with wildcard), or kind
      // TODO: later add relationships (parent of, child of, sibling of)
      // {
      // 	"Type": "asset:to-variable",
      // 	"Options": [
      inputs: [
        // 		// STORE TO
        // 		// uuid of the variable to store the assets in
        // 		// must be of asset format (single/multiple)
        // 		{"Type": "variable", "Value": "variable-uuid", "MainThread": true/false},
        {
          type: "compound",
          field: `variable`,
          defaultValue: [""],
          optionDefaults: ([{ Value: v }]) => ({
            ...(!inputArguments.find((arg) => arg.UUID === v)?.IsMultiple &&
            getOptionValues("operation")[0] === "append"
              ? { operation: "replace" }
              : {}),
          }),
          props: {
            label: "Variable to store the assets in",
            isInputArgumentOnly: true,
            inputArgumentProps: { Format: "asset", IsReadonly: false },
            isValidNewOption: (v) => v.trim(),
            showOperationSelector: true,
          },
        },

        // 		// FILTER BY NAME optional (multiple options allowed, default empty = all names)
        // 		// short-text variables only, can be single or multiple
        // 		{"Type": "name", "Value": "name-variable-uuid", "MainThread": true/false},
        {
          type: "compound",
          field: `name`,
          defaultValue: [""],
          // optionDefaults: ([{ Value: v }]) => ({
          //   ...(!inputArguments.find((arg) => arg.UUID === v)?.IsMultiple &&
          //   getOptionValues("operation")[0] === "append"
          //     ? { operation: "replace" }
          //     : {}),
          // }),
          props: {
            label: "Filter assets by name (optional)",
            isInputArgumentOnly: true,
            inputArgumentProps: { Format: "short-text" },
            isClearable: true,
            // isMulti: true,
            // isValidNewOption: (v) => v.trim(),
          },
        },
        // 		// if the name variable is multiple, only first item is used
        // 		{"Type": "isWildcard", "Value": "true"/"false"},

        {
          type: "option",
          field: "isWildcard",
          defaultValue: ["false"],
          props: {
            label: "Asset name - wildcard match",
            className: "d-flex justify-content-between align-items-center",
            inputType: "radio",
            radioName: "asset-name-wildcard",
            isValidNewOption: () => false,
            options: [
              { value: "true", label: "yes" },
              { value: "false", label: "no" },
            ],
          },
        },
        {
          type: "html",
          html: (
            <div className="mt-n5 pt-0 rounded-bottom-0 me-1 fst-italic text-muted">
              "Example" will match "example name", "Examples" and "eexample"
            </div>
          ),
        },
        // 		// FILTER BY LOCATION optional variable (JSON format)
        // 		// only one location variable allowed
        // 		{"Type": "location", "Value": "uuid-123-abc", "MainThread": true/false},
        {
          type: "compound",
          field: "location",
          defaultValue: [""],
          props: {
            label: "Filter by location (optional)",
            showStaticSelector: false,
            isInputArgumentOnly: true,
            inputArgumentProps: { Format: "geo-json" },
            isClearable: true,
          },
        },

        // 		// FILTER BY KIND OF ASSET optional (multiple options allowed, default empty = all kinds)
        // 		{"Type": "kind", "Value": "human"},
        // 		{"Type": "kind", "Value": "server"}
        {
          type: "option",
          field: "kind",
          defaultValue: [],
          props: {
            label: "Filter by type of asset (optional)",
            inputType: "select",
            isMulti: true,
            isValidNewOption: () => false,
            loadOptions: loadAssetKindOptions,
            formatOptionLabel: formatAssetKindOptionLabel,
            getValueLabels: getAssetKindValueLabels,
            isClearable: true,
          },
        },
        {
          type: "compound",
          field: "assetVariableFilter",
          defaultValue: [""],
          props: {
            label: "Filter by assets in variable (optional)",
            isInputArgumentOnly: true,
            inputArgumentFilter: (arg) => ["asset"].includes(arg.Format),
            inputArgumentProps: { Format: "asset" },
            isClearable: true,
          },
        },
        {
          type: "heading",
          label: <div className="mb-n4">Relationship filters</div>,
        },
        // FILTER BY ANOTHER RELATIONSHIP optional (parent of, child of (dependant in UI!))
        // { Type: "relationshipType", Value: "parent" / "child" / "none" },
        {
          type: "compound",
          field: "relationshipType",
          defaultValue: [""],
          optionDefaults: ([{ Value: v }]) => ({
            ...(v === ""
              ? {
                  variableTo: null,
                  relationshipKind: null,
                  relationshipName: null,
                  relationshipIsWildcard: null,
                  relationshipDepth: null,
                }
              : {
                  relationshipDepth: { ValueFrom: "static", Value: "1" },
                  variableTo: "",
                  relationshipKind: null,
                  relationshipName: "",
                  relationshipIsWildcard: "false",
                }),
          }),
          props: {
            label: "Filter by relationship to another asset (optional)",
            inputType: "select",
            isValidNewOption: (v) => v.trim(),
            inputArgumentProps: { Format: "short-text" },
            options: [
              { value: "", label: "None" },
              { value: "parent", label: "Parent" },
              { value: "child", label: "Dependant" },
            ],
          },
        },

        ...(getOptionValues("relationshipType")[0] === ""
          ? []
          : [
              // SEARCH DEPTH - show only if at least one relationship filter is provided: kind or name
              // relationshipDepth > 0 = look deeper (nodes count/depth) until at least one asset is found,
              // if the first level is empty, then look until at least one asset is found
              // 0 = only look one depth level of relationship, default 0, max = 5
              // {"Type": "relationshipDepth","Value": "0"},
              {
                type: "compound",
                field: "relationshipDepth",
                defaultValue: ["1"],
                props: {
                  label: "Relationship depth",
                  type: "number",
                  inputArgumentProps: { Format: "number", IsMultiple: false },
                  min: 1,
                  max: 10,
                  html: (
                    <p className="fst-italic text-muted">
                      Look deeper through nodes count/depth. Must be a number
                      between 1 and 10
                    </p>
                  ),
                },
              },

              // RELATIONSHIP to assets in another variable (hide if relationshipType is "none" or "")
              // select another asset variable, single or multiple
              // {"Type": "variableTo", "Value": "another-asset-variable-uuid", "MainThread": "true"/"false"},
              {
                type: "compound",
                field: "variableTo",
                defaultValue: [""],
                props: {
                  label: "Related variable",
                  isValidNewOption: (v) => v.trim(),
                  isInputArgumentOnly: true,
                  inputArgumentFilter: (arg) => ["asset"].includes(arg.Format),
                  inputArgumentProps: { Format: "asset" },
                },
              },

              // FILTER BY RELATIONSHIP KIND optional (multiple options allowed, default empty = all kinds)
              // (hide if relationshipType is "none" or "")
              // {"Type": "relationshipKind", "Value": "human"},
              // {"Type": "relationshipKind", "Value": "server"},
              {
                type: "option",
                field: "relationshipKind",
                defaultValue: [],
                props: {
                  label: "Filter related asset by type (optional)",
                  inputType: "select",
                  isMulti: true,
                  isValidNewOption: () => false,
                  loadOptions: loadAssetKindOptions,
                  formatOptionLabel: formatAssetKindOptionLabel,
                  getValueLabels: getAssetKindValueLabels,
                  isClearable: true,
                },
              },

              // FILTER BY RELATIONSHIP NAME optional (multiple options allowed, default empty = all names)
              // short-text variables only, can be single or multiple
              // (hide if relationshipType is "none" or "")
              // {"Type": "relationshipName", "Value": "name-variable-uuid", "MainThread": "true"/"false"},
              {
                type: "compound",
                field: "relationshipName",
                defaultValue: [""],
                props: {
                  label: "Filter related asset by name (optional)",
                  isValidNewOption: (v) => v.trim(),
                  isInputArgumentOnly: true,
                  inputArgumentFilter: (arg) =>
                    ["short-text"].includes(arg.Format),
                  inputArgumentProps: { Format: "short-text" },
                  isClearable: true,
                },
              },

              // is wildcard search? default false
              // {"Type": "relationshipIsWildcard","Value": "false"},
              // {
              //   type: "html",
              //   html: (
              //     <div className="bg-purple-gradient p-2 pb-0 mt-n4 mb-n5 rounded-top-3 me-1">
              //       <InputLabel>Relationship name - wildcard match</InputLabel>
              //     </div>
              //   ),
              // },

              {
                type: "option",
                field: "relationshipIsWildcard",
                defaultValue: ["false"],
                className: "",
                // "bg-purple-gradient p-2 pt-0 mt-n4 me-1 d-flex justify-content-between",
                props: {
                  label: "Relationship name - wildcard match",
                  className:
                    "d-flex justify-content-between align-items-center",
                  inputType: "radio",
                  radioName: "asset-name-relationshipIsWildcard",
                  isValidNewOption: () => false,
                  options: [
                    { value: "false", label: "no" },
                    { value: "true", label: "yes" },
                  ],
                },
              },

              {
                type: "html",
                html: (
                  <div className="p-2 pt-0 mt-n5 rounded-bottom-0 me-1">
                    "example" will match "example name", "Examples" and
                    "eexample"
                  </div>
                ),
              },
            ]),
        // 	]
        // }
      ],
    },

    SetCounter: {
      label: "",
      inputs: [
        {
          type: "compound",
          field: "variable",
          defaultValue: [""],
          props: {
            label: "Number variable to be updated",
            isInputArgumentOnly: true,
            isValidNewOption: (v) => v.trim(),
            inputArgumentProps: {
              Format: "number",
              IsMultiple: false,
              IsReadonly: false,
            },
          },
        },
        {
          type: "compound",
          field: "number",
          defaultValue: [""],
          props: {
            label: "Value",
            type: "number",
            isValidNewOption: (v) => v.trim(),
            inputArgumentProps: { Format: "number", IsMultiple: false },
          },
        },
        {
          type: "option",
          field: "operation",
          defaultValue: ["add"],
          props: {
            inputType: "radio",
            radioName: "operation",
            label: "Operation",
            options: [
              { value: "add", label: "Add" },
              { value: "subtract", label: "Subtract" },
              { value: "set", label: "Set" },
            ],
          },
        },
      ],
    },

    "form-complete-message": {
      label: "",
      inputs: [
        {
          type: "option",
          field: "CompletedMessage",
          props: {
            inputType: "textArea",
            valueType: "data",
            label: "Complete message",
            maxLength: 64000,
          },
        },
      ],
    },

    "form-message": {
      label: "",
      inputs: [
        {
          type: "option",
          field: "HTML",
          props: {
            inputType: "textArea",
            valueType: "data",
            label: "Message body",
            maxLength: 64000,
          },
        },
        {
          type: "option",
          field: "HtmlType",
          defaultValue: ["paragraph"],
          props: {
            label: "Message type",
            inputType: "select",
            isValidNewOption: () => false,
            options: [
              { value: "heading", label: "Heading" },
              { value: "paragraph", label: "Paragraph" },
              { value: "alert", label: "Alert" },
            ],
          },
        },
        {
          type: "option",
          field: "DocumentLink",
          props: {
            label: "Document link",
          },
        },
      ],
    },

    "form-question": {
      label: "",
      inputs: [
        {
          type: "option",
          field: "Title",
          props: {
            label: "Question title",
          },
        },

        {
          type: "option",
          field: "Description",
          props: {
            inputType: "textArea",
            label: "Question body",
          },
        },

        {
          type: "option",
          field: "Type",
          defaultValue: ["text"],
          props: {
            label: "Question type",
            inputType: "select",
            isValidNewOption: () => false,
            options: [
              { value: "text", label: "Free text" },
              { value: "date", label: "Date" },
              { value: "time", label: "Time" },
              { value: "rating", label: "Range" },
              { value: "radiogroup", label: "Single selection" },
              { value: "checkbox", label: "Multi selection" },
              { value: "dropdown", label: "Single select dropdown" },
              { value: "tagBox", label: "Multi select dropdown" },
              { value: "file", label: "File" },
              { value: "signaturepad", label: "Signature" },
              { value: "geo-json", label: "Map" },
            ],
          },
          optionDefaults: ([{ Value: value }]) => ({
            inputType: value === "text" ? "text" : null,
            RateMin: value === "rating" ? "0" : null,
            RateMax: value === "rating" ? "10" : null,
            FileType: value === "file" ? "img" : null,
            DefaultValueFrom: ["signaturepad", "file"].includes(value)
              ? null
              : "ignore",
            showOtherItem: [
              "radiogroup",
              "checkbox",
              "dropdown",
              "tagBox",
            ].includes(value)
              ? "false"
              : null,
            Choice: ["radiogroup", "checkbox", "dropdown", "tagBox"].includes(
              value
            )
              ? { ValueFrom: "static-options", Value: "" }
              : null,
            DefaultValueValue: null,
            DefaultValueThread: null,
            Value: null,
            showPastDates: ["date", "dateTime"].includes(value) ? "true" : null,
            showFutureDates: ["date", "dateTime"].includes(value)
              ? "true"
              : null,
            showPastDatesValue: null,
            showPastDatesUnit: null,
            showFutureDatesValue: null,
            showFutureDatesUnit: null,
            showPastTimes: ["time", "dateTime"].includes(value) ? "true" : null,
            showFutureTimes: ["time", "dateTime"].includes(value)
              ? "true"
              : null,
            showPastTimesValue: null,
            showPastTimesUnit: null,
            showFutureTimesValue: null,
            showFutureTimesUnit: null,
          }),
        },

        {
          type: "compound",
          field: "Choice",
          defaultValue: [""],
          ifOptionTypeHas: [
            {
              optionType: "Type",
              values: ["radiogroup", "checkbox", "dropdown", "tagBox"],
            },
          ],
          optionDefaults: ([{ Value: v, ValueFrom }]) => ({
            "static-choice": null,
            DefaultValue: null,
            DefaultValueType: "ignore",
            "choice-handler":
              ValueFrom === "storageData:category-raw" ? "none" : null,
            date:
              ValueFrom === "api" && v === "bookings"
                ? { ValueFrom: "static", Value: "" }
                : null,
            "time-of-day":
              ValueFrom === "api" && v === "bookings"
                ? { ValueFrom: "static", Value: "" }
                : null,
            dateTime:
              ValueFrom === "api" && v === "bookings"
                ? { ValueFrom: "static", Value: "" }
                : null,
          }),
          props: {
            label: "Choices",
            showStaticOptionsSelector: true,
            showStaticSelector: false,
            isValidNewOption: (v) => false,
            onCreateOption: (v) => v,
            inputArgumentProps: {
              IsMultiple: true,
              Format: "short-text",
            },
            isRawData: true,
            showApiSelector: true,
          },
        },
        // show static options if the question has static options
        ...((action.Options || [])
          .filter((actOpt) => actOpt.Type === "Choice")
          .sort((a, b) => a.Order - b.Order)
          .map((opt) => opt.ValueFrom)
          .includes("static-options")
          ? [
              {
                type: "option",
                field: "static-choice",
                defaultValue: [""],
                className:
                  "bg-purple-gradient p-2 pt-0 mt-n5 rounded-top-0 rounded-bottom-3",
                props: {
                  // label: "Static Options",
                  isMulti: true,
                  isValidNewOption: (v) => v,
                  onCreateOption: (v) => v,
                  valueFrom: "Choice",
                },
              },
            ]
          : []),

        // show nylas apis
        ...((action.Options || []).some(
          (opt) =>
            opt.Type === "Choice" &&
            opt.ValueFrom === "api" &&
            opt.Value === "bookings"
        )
          ? [
              {
                type: "option",
                field: "api-connection",
                defaultValue: [""],
                props: {
                  label: "Select API Connection",
                  inputType: "select",
                  isValidNewOption: (v) => false,
                  loadOptions: loadNylasEventsCalendarOptionsPaginated,
                },
              },
            ]
          : []),

        {
          type: "option",
          field: "liveUpdate",
          defaultValue: ["false"],
          ifOptionTypeHas: [
            {
              optionType: "Type",
              values: ["geo-json"],
            },
          ],
          props: {
            label: "Enable live location updates",
            inputType: "select",
            isValidNewOption: () => false,
            options: [
              { value: "false", label: "No" },
              { value: "true", label: "Yes" },
            ],
          },
        },
        {
          type: "option",
          field: "liveUpdateInterval",
          defaultValue: ["60"],
          ifOptionTypeHas: [
            {
              optionType: "Type",
              values: ["geo-json"],
            },
            {
              optionType: "liveUpdate",
              values: ["true"],
            },
          ],
          props: {
            label: "Live location update interval (seconds)",
            type: "number",
            min: 1,
            inputHelp:
              "The browser sends the selected location at this interval while the survey is open.",
          },
        },
        {
          type: "compound",
          field: `variable`,
          defaultValue: [""],
          optionDefaults: ([{ Value: v }]) => ({
            ...(!inputArguments.find((arg) => arg.UUID === v)?.IsMultiple &&
            getOptionValues("operation")[0] === "append"
              ? { operation: "replace" }
              : {}),
          }),
          props: {
            label: "Store reply in variable",
            isClearable: true,
            isInputArgumentOnly: true,
            inputArgumentProps: {
              // Format: "contact",
              ...(["checkbox", "tagBox"].includes(getOptionValues("Type")[0])
                ? { IsMultiple: true, IsReadonly: false }
                : {}),
              ...(["date"].includes(getOptionValues("Type")[0])
                ? { Format: "date", IsReadonly: false }
                : {}),
              ...(["time"].includes(getOptionValues("Type")[0])
                ? { Format: "time", IsReadonly: false }
                : {}),
              ...(["file"].includes(getOptionValues("Type")[0])
                ? { Format: "binary", IsReadonly: false }
                : {}),
              ...(["geo-json"].includes(getOptionValues("Type")[0])
                ? { Format: "geo-json", IsReadonly: false }
                : {}),
              ...([
                "text",
                "radiogroup",
                "checkbox",
                "tagBox",
                "dropdown",
              ].includes(getOptionValues("Type")[0])
                ? { Format: "short-text", IsReadonly: false }
                : {}),
              ...(["rating"].includes(getOptionValues("Type")[0])
                ? { Format: "number", IsReadonly: false }
                : {}),
              // nylas bookings dateTime selection
              ...((action.Options || []).some(
                (opt) =>
                  opt.Type === "Choice" &&
                  opt.ValueFrom === "api" &&
                  opt.Value === "bookings"
              )
                ? { IsMultiple: true, Format: "dateTime", IsReadonly: false }
                : {}),
            },
            isValidNewOption: (v) => v.trim(),
            showOperationSelector: true,
          },
        },

        // email and number input
        {
          type: "option",
          field: "inputType",
          defaultValue: ["text"],
          ifOptionTypeHas: [
            {
              optionType: "Type",
              values: ["text"],
            },
          ],
          props: {
            label: "Input type",
            inputType: "radio",
            radioName: "inputTypeRadio",
            isValidNewOption: () => false,
            options: [
              { value: "text", label: "Text" },
              { value: "email", label: "Email" },
              { value: "number", label: "Number" },
            ],
          },
        },

        // required or minimum selection
        ...(["checkbox", "tagBox"].includes(getOptionValues("Type")[0])
          ? [
              {
                type: "option",
                field: "minSelectedChoices",
                props: {
                  label: "Minimum number of required selections",
                  type: "number",
                },
              },
            ]
          : [
              {
                type: "option",
                field: "IsRequired",
                defaultValue: ["false"],
                props: {
                  label: "Is required?",
                  isValidNewOption: () => false,
                  inputType: "radio",
                  radioName: "isRequired",
                  options: [
                    { value: "false", label: "Not required" },
                    { value: "true", label: "Required" },
                  ],
                },
              },
            ]),

        ...(getOptionValues("Type").includes("rating")
          ? [
              {
                type: "option",
                field: "RateMin",
                props: {
                  label: "Range from",
                  type: "number",
                },
              },
              {
                type: "option",
                field: "RateMax",
                props: {
                  label: "Range to",
                  type: "number",
                },
              },
            ]
          : []),

        ...(!(action.Options || []).some(
          (opt) =>
            opt.Type === "Choice" &&
            opt.ValueFrom === "api" &&
            opt.Value === "bookings"
        )
          ? [
              {
                type: "option",
                field: "showOtherItem",
                ifOptionTypeHas: [
                  {
                    optionType: "Type",
                    values: ["radiogroup", "checkbox", "dropdown", "tagBox"],
                  },
                ],
                props: {
                  label: "Let user enter custom value",
                  inputType: "radio",
                  radioName: "letUserEnterCustomValue",
                  isValidNewOption: () => false,
                  options: [
                    { value: "false", label: "No" },
                    { value: "true", label: "Yes" },
                  ],
                },
              },
            ]
          : [
              {
                type: "compound",
                field: "date",
                defaultValue: [""],
                props: {
                  label: "Date",
                  inputType: "dateTime",
                  dateTimeType: "date",
                  inputArgumentProps: {
                    Format: "date",
                    IsMultiple: false,
                  },
                },
              },
              {
                type: "compound",
                field: "time-of-day",
                defaultValue: [""],
                props: {
                  label: "Time of day (AM or PM)",
                  isValidNewOption: (v) => v.trim(),
                  inputArgumentProps: {
                    Format: "short-text",
                    IsMultiple: false,
                  },
                },
              },
            ]),

        // show past dates
        {
          type: "option",
          field: "showPastDates",
          ifOptionTypeHas: [
            {
              optionType: "Type",
              values: ["date", "dateTime"],
            },
          ],
          defaultValue: ["true"],
          optionDefaults: ([{ Value: v }]) => ({
            showPastDatesUnit: ["minusInterval", "plusInterval"].includes(v)
              ? "day"
              : null,
            showPastDatesValue: ["minusInterval", "plusInterval"].includes(v)
              ? { ValueFrom: "static", Value: `1` }
              : v === "static"
              ? {
                  ValueFrom: "static",
                  Value: `${new Date().getFullYear()}-${
                    new Date().getMonth() + 1
                  }-${new Date().getDate()}`,
                }
              : null,
          }),
          props: {
            label: "Show past dates",
            inputType: "radio",
            radioName: "showPastDates",
            isValidNewOption: () => false,
            options: [
              { value: "true", label: "Yes" },
              { value: "false", label: "No" },
              { value: "minusInterval", label: "Now - x" },
              { value: "plusInterval", label: "Now + x" },
              { value: "static", label: "Fixed date" },
            ],
          },
        },
        ...(getOptionValues("showPastDates").includes("static")
          ? [
              {
                type: "compound",
                defaultValue: [
                  `${new Date().getFullYear()}-${
                    new Date().getMonth() + 1
                  }-${new Date().getDate()}`,
                ],
                field: "showPastDatesValue",
                props: {
                  label: "Select date",
                  inputType: "dateTime",
                  dateTimeType: "date",
                },
              },
            ]
          : []),
        ...(getOptionValues("showPastDates").includes("minusInterval") ||
        getOptionValues("showPastDates").includes("plusInterval")
          ? [
              {
                type: "compound",
                field: "showPastDatesValue",
                defaultValue: ["1"],
                group: true,
                props: {
                  label: "Value",
                  type: "number",
                },
              },
              {
                type: "option",
                field: "showPastDatesUnit",
                defaultValue: ["days"],
                group: true,
                props: {
                  label: "Unit",
                  inputType: "radio",
                  radioName: "showPastDatesUnit",
                  isValidNewOption: () => false,
                  options: [
                    { value: "day", label: "Days" },
                    { value: "month", label: "Months" },
                  ],
                },
              },
            ]
          : []),

        // show future dates
        {
          type: "option",
          field: "showFutureDates",
          ifOptionTypeHas: [
            {
              optionType: "Type",
              values: ["date", "dateTime"],
            },
          ],
          optionDefaults: ([{ Value: v }]) => ({
            showFutureDatesUnit: ["minusInterval", "plusInterval"].includes(v)
              ? "day"
              : null,
            showFutureDatesValue: ["minusInterval", "plusInterval"].includes(v)
              ? { ValueFrom: "static", Value: `1` }
              : v === "static"
              ? {
                  ValueFrom: "static",
                  Value: `${new Date().getFullYear()}-${
                    new Date().getMonth() + 1
                  }-${new Date().getDate()}`,
                }
              : null,
          }),
          defaultValue: ["true"],
          props: {
            label: "Show future dates",
            inputType: "radio",
            radioName: "showFutureDates",
            isValidNewOption: () => false,
            options: [
              { value: "true", label: "Yes" },
              { value: "false", label: "No" },
              { value: "minusInterval", label: "Now - x" },
              { value: "plusInterval", label: "Now + x" },
              { value: "static", label: "Fixed date" },
            ],
          },
        },
        ...(getOptionValues("showFutureDates").includes("static")
          ? [
              {
                type: "compound",
                defaultValue: [
                  `${new Date().getFullYear()}-${
                    new Date().getMonth() + 1
                  }-${new Date().getDate()}`,
                ],
                field: "showFutureDatesValue",
                props: {
                  label: "Select date",
                  inputType: "dateTime",
                  dateTimeType: "date",
                },
              },
            ]
          : []),
        ...(getOptionValues("showFutureDates").includes("plusInterval") ||
        getOptionValues("showFutureDates").includes("minusInterval")
          ? [
              {
                type: "compound",
                field: "showFutureDatesValue",
                defaultValue: ["1"],
                group: true,
                props: {
                  label: "Value",
                  type: "number",
                },
              },
              {
                type: "option",
                field: "showFutureDatesUnit",
                defaultValue: ["days"],
                group: true,
                props: {
                  label: "Unit",
                  inputType: "radio",
                  radioName: "showFutureDatesUnit",
                  isValidNewOption: () => false,
                  options: [
                    { value: "day", label: "Days" },
                    { value: "month", label: "Months" },
                  ],
                },
              },
            ]
          : []),

        // Accept time before
        {
          type: "option",
          field: "showPastTimes",
          ifOptionTypeHas: [
            {
              optionType: "Type",
              values: ["time", "dateTime"],
            },
          ],
          optionDefaults: ([{ Value: v }]) => ({
            showPastTimesUnit: ["minusInterval", "plusInterval"].includes(v)
              ? "hour"
              : null,
            showPastTimesValue: ["minusInterval", "plusInterval"].includes(v)
              ? { ValueFrom: "static", Value: `1` }
              : v === "static"
              ? { ValueFrom: "static", Value: `12:00` }
              : null,
          }),
          defaultValue: ["true"],
          props: {
            label: "Accept time before",
            inputType: "radio",
            radioName: "showPastTimes",
            isValidNewOption: () => false,
            options: [
              { value: "true", label: "Yes" },
              { value: "false", label: "No" },
              { value: "minusInterval", label: "Now - x" },
              { value: "plusInterval", label: "Now + x" },
              { value: "static", label: "Fixed time" },
            ],
          },
        },
        ...(getOptionValues("showPastTimes").includes("static")
          ? [
              {
                type: "compound",
                defaultValue: ["12:00"],
                field: "showPastTimesValue",
                props: {
                  label: "Select time",
                  inputType: "dateTime",
                  dateTimeType: "time",
                },
              },
            ]
          : []),
        ...(getOptionValues("showPastTimes").includes("minusInterval") ||
        getOptionValues("showPastTimes").includes("plusInterval")
          ? [
              {
                type: "compound",
                field: "showPastTimesValue",
                defaultValue: ["1"],
                group: true,
                props: {
                  label: "Value",
                  type: "number",
                },
              },
              {
                type: "option",
                field: "showPastTimesUnit",
                group: true,
                defaultValue: ["hours"],
                props: {
                  label: "Unit",
                  inputType: "radio",
                  radioName: "showPastTimesUnit",
                  isValidNewOption: () => false,
                  options: [
                    { value: "hour", label: "Hours" },
                    { value: "minute", label: "Minutes" },
                  ],
                },
              },
            ]
          : []),

        // accept time after
        {
          type: "option",
          field: "showFutureTimes",
          ifOptionTypeHas: [
            {
              optionType: "Type",
              values: ["time", "dateTime"],
            },
          ],
          optionDefaults: ([{ Value: v }]) => ({
            showFutureTimesUnit: ["minusInterval", "plusInterval"].includes(v)
              ? "hour"
              : null,
            showFutureTimesValue: ["minusInterval", "plusInterval"].includes(v)
              ? { ValueFrom: "static", Value: `1` }
              : v === "static"
              ? { ValueFrom: "static", Value: `12:00` }
              : null,
          }),
          defaultValue: ["true"],
          props: {
            label: "Accept time after",
            inputType: "radio",
            radioName: "showFutureTimes",
            isValidNewOption: () => false,
            options: [
              { value: "true", label: "Yes" },
              { value: "false", label: "No" },
              { value: "minusInterval", label: "Now - x" },
              { value: "plusInterval", label: "Now + x" },
              { value: "static", label: "Fixed time" },
            ],
          },
        },
        ...(getOptionValues("showFutureTimes").includes("static")
          ? [
              {
                type: "compound",
                defaultValue: ["12:00"],
                field: "showFutureTimesValue",
                props: {
                  label: "Select time",
                  inputType: "dateTime",
                  dateTimeType: "time",
                },
              },
            ]
          : []),
        ...(getOptionValues("showFutureTimes").includes("minusInterval") ||
        getOptionValues("showFutureTimes").includes("plusInterval")
          ? [
              {
                type: "compound",
                field: "showFutureTimesValue",
                defaultValue: ["1"],
                group: true,
                props: {
                  label: "Value",
                  type: "number",
                },
              },
              {
                type: "option",
                field: "showFutureTimesUnit",
                group: true,
                defaultValue: ["hours"],
                props: {
                  label: "Unit",
                  inputType: "radio",
                  radioName: "showFutureTimesUnit",
                  isValidNewOption: () => false,
                  options: [
                    { value: "hour", label: "Hours" },
                    { value: "minute", label: "Minutes" },
                  ],
                },
              },
            ]
          : []),

        ...(["file"].includes(getOptionValues("Type")[0])
          ? [
              {
                type: "option",
                field: "FileType",
                props: {
                  label: "Allowed file types",
                  inputType: "select",
                  isValidNewOption: () => false,
                  options: [
                    { value: "pdf", label: "PDF" },
                    { value: "img", label: "Image" },
                  ],
                },
              },
            ]
          : []),

        ...(!(action.Options || []).some(
          (opt) =>
            opt.Type === "Choice" &&
            opt.ValueFrom === "api" &&
            opt.Value === "bookings"
        ) &&
        [
          "text",
          "rating",
          "radiogroup",
          "checkbox",
          "dropdown",
          "tagBox",
          undefined,
        ].includes(getOptionValues("Type")[0])
          ? compoundInputs({
              context,
              field: "Default",
              fromField: "DefaultValueType",
              label: "Default value from",
              isValueFromTab: true,
              isDataCategory: false,
              filterFn: () => true,
              inputProps: {
                ...(getOptionValues("Type")[0] === "rating"
                  ? {
                      type: "number",
                      min: getOptionValues("RateMin")[0],
                      max: getOptionValues("RateMax")[0],
                    }
                  : {}),
                ...([
                  "text",
                  "rating",
                  "radiogroup",
                  "checkbox",
                  "dropdown",
                  "tagBox",
                  undefined,
                ].includes(getOptionValues("Type")[0]) &&
                (action.Options || [])
                  .filter((actOpt) => actOpt.Type === "Choice")
                  .sort((a, b) => a.Order - b.Order)
                  .map((opt) => opt.ValueFrom)
                  .includes("static-options")
                  ? {
                      inputType: "select",
                      options: getOptionValues("static-choice").map((o) => ({
                        value: o,
                        label: o,
                      })),
                    }
                  : {}),
              },
            })
          : []),
      ],
    },

    "form:reject": {
      label: "",
      inputs: compoundInputs({
        context,
        field: "value",
        label: "Reject message from",
        isValueFromTab: true,
        filterFn: () => true,
        fixedField: "rejectFormReason",
        inputProps: { label: "Reject reason" },
        optionDefaults: () => ({
          inputArgument: null,
          "storageData:category": null,
          rejectFormReason: null,
        }),

        inputArgumentField: "inputArgument",
        dataField: "storageData:category",
        isDataCreatable: true,
        threadType: "property",
        threadField: "UseMainThread",
      }),
    },

    "form:redirect": {
      label: "",
      inputs: [
        {
          type: "compound",
          field: "redirectFormURL",
          defaultValue: [""],
          props: {
            label: "Redirect to URL",
            inputArgumentFilter: (arg) => arg.Format === "short-text",
          },
        },
      ],
    },

    "form:reply-to-variable": {
      label: "",
      inputs: [
        {
          type: "option",
          field: "question",
          optionDefaults: ([{ Value: v }]) => ({
            variable: null,
            operation: "replace",
          }),
          props: {
            label: "Question",
            inputType: "select",
            isValidNewOption: () => false,
            options: getFormQuestions()
              .filter((q) => q.Type !== "file" && q.Type !== "signaturepad")
              .map((q) => ({
                value: q.UUID,
                label: q.Title,
              })),
          },
        },
        {
          type: "compound",
          field: `variable`,
          defaultValue: [""],
          optionDefaults: ([{ Value: v }]) => ({
            ...(!inputArguments.find((arg) => arg.UUID === v)?.IsMultiple &&
            getOptionValues("operation")[0] === "append"
              ? { operation: "replace" }
              : {}),
          }),
          props: {
            label: "Variable name",
            isInputArgumentOnly: true,
            inputArgumentProps: {
              // Format: "contact",
              ...(["checkbox", "tagBox"].includes(
                getFormQuestions().find(
                  (q) => q.UUID === getOptionValues("question")[0]
                )?.Type
              )
                ? { IsMultiple: true, IsReadonly: false }
                : {}),
              ...(["date"].includes(
                getFormQuestions().find(
                  (q) => q.UUID === getOptionValues("question")[0]
                )?.Type
              )
                ? { Format: "date", IsReadonly: false }
                : {}),
              ...(["time"].includes(
                getFormQuestions().find(
                  (q) => q.UUID === getOptionValues("question")[0]
                )?.Type
              )
                ? { Format: "time", IsReadonly: false }
                : {}),
              ...([
                "text",
                "radiogroup",
                "checkbox",
                "tagBox",
                "dropdown",
              ].includes(
                getFormQuestions().find(
                  (q) => q.UUID === getOptionValues("question")[0]
                )?.Type
              )
                ? { Format: "short-text", IsReadonly: false }
                : {}),
              ...(["rating"].includes(
                getFormQuestions().find(
                  (q) => q.UUID === getOptionValues("question")[0]
                )?.Type
              )
                ? { Format: "number", IsReadonly: false }
                : {}),
            },
            isValidNewOption: (v) => v.trim(),
            showOperationSelector: true,
          },
        },
      ],
    },

    "form:book-event": {
      label: "",
      inputs: [
        {
          type: "compound",
          field: "time",
          props: {
            label: "Event time",
            isInputArgumentOnly: true,
            inputArgumentProps: { Format: "dateTime", IsMultiple: true },
            isValidNewOption: (v) => v.trim(),
          },
        },
        {
          type: "compound",
          field: "email",
          defaultValue: [""],
          props: {
            label: "Email",
            isEmail: true,
          },
        },
        {
          type: "compound",
          field: "name",
          defaultValue: [""],
          props: {
            label: "Name",
          },
        },
        {
          type: "compound",
          field: "comment",
          defaultValue: [""],
          props: {
            label: "Comment",
          },
        },
        {
          type: "option",
          field: "api-connection",
          defaultValue: [],
          props: {
            label: "Select Nylas API Connection",
            inputType: "select",
            isValidNewOption: (v) => false,
            loadOptions: loadNylasEventsCalendarOptionsPaginated,
          },
        },
        {
          type: "compound",
          field: "booked-date-time",
          props: {
            label: "Store the booked event time",
            isInputArgumentOnly: true,
            inputArgumentProps: { Format: "dateTime", IsMultiple: true },
            isValidNewOption: (v) => v.trim(),
          },
        },
      ],
    },

    "time-zone:from-location": {
      label: "",
      inputs: [
        {
          type: "compound",
          field: "variable",
          defaultValue: [""],
          props: {
            label: "Location from",
            isInputArgumentOnly: true,
            inputArgumentFilter: (arg) =>
              ["short-text", "sms", "voice"].includes(arg.Format),
          },
        },

        ...(["short-text"].includes(
          inputArguments.find(
            (ia) => ia.UUID === getOptionValues("variable")[0]
          )?.Format
        )
          ? [
              {
                type: "option",
                field: "LocationFormat",
                defaultValue: ["CountryCode"],
                props: {
                  label: "Location format",
                  inputType: "select",
                  isValidNewOption: () => false,
                  options: [
                    { value: "USAState", label: "USA state" },
                    { value: "CountryCode", label: "Country code" },
                  ],
                },
              },
            ]
          : []),
        {
          type: "option",
          field: "default",
          defaultValue: ["projectDefault"],
          props: {
            label: "Default location",
            inputType: "select",
            isValidNewOption: () => false,
            options: [
              { value: "", label: `No default location` },
              {
                value: "projectDefault",
                label: `Project default (${pageProps.defaultTZ})`,
              },
              ...timezones.map((tz) => ({
                value: tz.tzCode,
                label: tz.label,
              })),
            ],
          },
        },
        { type: "heading", label: "Store time zone to variable" },
        {
          type: "compound",
          field: "variableTo",
          defaultValue: [""],
          props: {
            label: "Location variable",
            isValidNewOption: (v) => v.trim(),
            isInputArgumentOnly: true,
            inputArgumentProps: {
              Format: "configuration",
              DataStructure: "Time Zones",
              IsReadonly: false,
            },
            showOperationSelector: true,
          },
        },
      ],
    },

    // {
    //   "type": "api:make-request",
    "api:make-request": {
      label: "API profile",
      //   "options": [
      inputs: [
        //     // one required, the uuid of the api profile, static only
        //     {"Type": "api:profile", "Value": "uuid-abc-123"},
        {
          type: "option",
          field: "api:profile",
          defaultValue: [""],
          props: {
            label: "API Profile",
            inputType: "select",
            loadOptions: loadApiProfilesPaginated,
            getValueLabels: async (values) => {
              const profiles = await Promise.all(
                values.filter(Boolean).map(async (uuid) => {
                  const profile = await proxyAPI({
                    prefix: "logic/workflows/api",
                    url: `api-profile/${uuid}`,
                  });

                  return {
                    value: profile.Uuid,
                    label: `${profile.Label} - (BaseUrl: "${profile.BaseUrl}")`,
                  };
                })
              );

              return profiles;
            },
            isValidNewOption: (v) => false,
          },
        },
        {
          type: "html",
          className: "mt-n5 rounded-top-0",
          html: (
            <Link to="/library/api-connections" target="_blank">
              See API connections
            </Link>
          ),
        },
        //     // one required: GET, POST, PUT, DELETE, PATCH
        //     {"Type": "api:method", "Value": "GET", "ValueFrom": "static"},
        {
          type: "compound",
          field: "api:method",
          defaultValue: ["GET"],
          props: {
            isStaticOnly: true,
            label: "Method",
            inputType: "select",
            options: [
              { value: "GET", label: "GET" },
              { value: "POST", label: "POST" },
              { value: "PUT", label: "PUT" },
              { value: "DELETE", label: "DELETE" },
              { value: "PATCH", label: "PATCH" },
            ],
          },
        },
        // one optional, input argument example
        // optionally append the path to the base url (baseUrl)/path
        // {"Type": "api:path", "Value": "uuid-123-abc", "ValueFrom": "inputArgument", "DefaultValue": "/optionally-use-this-if-input-is-missing", "MainThread": true},
        {
          type: "compound",
          field: "api:path",
          defaultValue: [""],
          props: {
            label: "API Path to append to base URL (optional)",
            isClearable: true,
            isValidNewOption: (v) => v.trim(),
            inputArgumentProps: { Format: ["short-text", "number"] },
          },
        },
        //     // query fields appended to the url, multiple values for the same key are allowed
        //     // they can be static, input arguments or data
        //     // if the key is present in the api profile, the values from the profile are ignored even if values from the action are empty
        //     {"Type": "api:query-params", "Value": "value-1", "ValueFrom": "static", "Key": "field-1"},
        //     {"Type": "api:query-params", "Value": "uuid-123-abc", "ValueFrom": "inputArgument", "Key": "field-1", "DefaultValue": "value-2"},
        //     {"Type": "api:query-params", "Value": "QUERY_PARAM", "ValueFrom": "storageData:category", "Key": "field-1", "DefaultValue": "value-3", "MainThread": true},
        {
          type: "compound",
          field: "api:query-params",
          defaultValue: [""],
          props: {
            label: "API Query Params",
            isMulti: true,
            showKeyInput: true,
            inputArgumentProps: { Format: ["short-text", "number"] },
            isValidNewOption: (v) => v.trim(),
          },
        },
        //     // one required, the type of the payload: json (default), form, static only
        //     // this option adds the Content-Type header to the request: application/json, application/x-www-form-urlencoded
        //     {"Type": "api:payload-type", "Value": "json"},
        {
          type: "compound",
          field: "api:payload-type",
          defaultValue: ["json"],
          ifOptionTypeHas: [
            {
              optionType: "api:method",
              values: ["POST", "PUT", "PATCH"],
            },
          ],
          props: {
            isStaticOnly: true,
            label: "Request Payload Type",
            inputType: "select",
            options: [
              { value: "json", label: "JSON" },
              { value: "form", label: "Form" },
            ],
          },
        },

        //     // the payload for the request, only if the payload type is json, exactly one payload is required
        //     // this overrides the payload from the api profile
        //     // ----->>>> use Data, not Value! Data is up to 64KB, Value is up to 255B
        //     {"Type": "api:json-payload", "Data": "{\"key\": \"value\"}"},
        {
          type: "compound",
          field: "api:json-payload",
          defaultValue: [""],
          ifOptionTypeHas: [
            {
              optionType: "api:payload-type",
              values: ["json"],
            },
            {
              optionType: "api:method",
              values: ["POST", "PUT", "PATCH"],
            },
          ],
          props: {
            label: "JSON Payload",
            useDataField: true,
            inputType: "textArea",
            maxLength: 64000,
          },
        },
        //     // form data for the request, only if the payload type is form, at least one key-value pair is required
        //     // this overrides the form data from the api profile
        //     {"Type": "api:form-data", "Value": "value-1", "ValueFrom": "static", "Key": "field-1"},
        //     {"Type": "api:form-data", "Value": "uuid-123-abc", "ValueFrom": "inputArgument", "Key": "field-1", "DefaultValue": "value-2"},
        //     {"Type": "api:form-data", "Value": "FORM_DATA_IGNORED_IF_EMPTY", "ValueFrom": "storageData:category", "Key": "field-1", "DefaultValue": "", "MainThread": true},
        {
          type: "compound",
          field: "api:form-data",
          defaultValue: [""],
          ifOptionTypeHas: [
            {
              optionType: "api:payload-type",
              values: ["form"],
            },
          ],
          props: {
            label: "Form Data",
            isMulti: true,
            showKeyInput: true,
            inputArgumentProps: { Format: ["short-text", "number"] },
            isValidNewOption: (v) => v.trim(),
          },
        },

        //     // store the response code in the data storage
        //     {"Type": "api:request-error", "Value": "ERROR", "MainThread": false},
        {
          type: "compound",
          field: "api:request-error",
          defaultValue: [""],
          props: {
            label: "Store error in variable",
            isInputArgumentOnly: true,
            inputArgumentProps: { Format: "short-text" },
          },
        },
        //     // store the response code in the data storage
        //     {"Type": "api:response-code", "Value": "CODE", "MainThread": false},
        {
          type: "compound",
          field: "api:response-code",
          defaultValue: [""],
          props: {
            label: "Store response code in variable",
            isInputArgumentOnly: true,
            inputArgumentProps: { Format: ["number"] },
            isValidNewOption: (v) => v.trim(),
          },
        },
        //     // store the response body in the data storage
        //     {"Type": "api:response-body", "Value": "BODY", "MainThread": false},
        {
          type: "compound",
          field: "api:response-body",
          defaultValue: [""],
          props: {
            label: "Store response body in variable",
            isInputArgumentOnly: true,
            inputArgumentProps: { Format: ["short-text", "json"] },
            isValidNewOption: (v) => v.trim(),
          },
        },
        //   ]
        // }
      ],
    },
    // {
    //   "type": "extract:from-data-structure",
    "extract:from-data-structure": {
      label: "Extract from data structure",
      //   "options": [
      inputs: [
        // SOURCE variable
        // json (with or without data structure)
        // or data structure format (asset, contact, event, ticket, weather-alert, data-entry),
        // can be single or multiple, we will take all values from it
        {
          type: "compound",
          field: "json",
          defaultValue: [""],
          props: {
            label: "Variable to extract from",
            isInputArgumentOnly: true,
            inputArgumentProps: {
              Format: [
                "json",
                "asset",
                "contact",
                "group",
                "event",
                "ticket",
                "data-entry",
              ],
            },
            // inputArgumentFilter: (arg) =>
            //   [
            //     "json",
            //     "asset",
            //     "contact",
            //     "group",
            //     "event",
            //     "ticket",
            //     "data-entry",
            //   ].includes(arg.Format),
          },
        },

        // SHOW IF "json" variable selected above is not associated with a collection,
        // one required, JSON collection uuid, static only
        // {"Type": "json:collection", "Value": "uuid-abc-123"},
        ...(inputArguments.some(
          (arg) =>
            arg.UUID === getOptionValues("json")[0] &&
            !arg.DataStructure &&
            arg.Format === "json"
        )
          ? [
              {
                type: "compound",
                field: "json:collection",
                defaultValue: [jsonCollections[0]?.Uuid],
                props: {
                  isStaticOnly: true,
                  label: "Data structure",
                  inputType: "select",
                  options: jsonCollections.map((c) => ({
                    value: c.Uuid,
                    label: c.Label,
                  })),
                },
              },
            ]
          : []),

        // one required, the path to the field in the json/variable structure, static only
        // {"Type": "json:path", "Value": "path.to.field"},
        {
          type: "option",
          field: "json:path",
          defaultValue: [""],
          props: {
            label: "Field",
            inputType: "select",
            // show system data structures options if the input argument is of that type
            // otherwise show the fields from the selected json collection
            isValidNewOption: () => false,
            options: ["asset", "contact", "group", "event", "ticket"].includes(
              inputArguments.find(
                (arg) => arg.UUID === getOptionValues("json")[0]
              )?.Format
            )
              ? systemDataStructures?.[
                  inputArguments.find(
                    (arg) => arg.UUID === getOptionValues("json")[0]
                  )?.Format
                ].Fields.map((f) => ({
                  value: f.Path,
                  label: f.Name,
                }))
              : // show weather-alert data structure fields
              inputArguments.some(
                  (arg) =>
                    arg.UUID === getOptionValues("json")[0] &&
                    arg.Format === "json" &&
                    arg.DataStructure === "weather-alert"
                )
              ? systemDataStructures?.["weather-alert"]?.Fields.map((f) => ({
                  value: f.Path,
                  label: f.Name,
                }))
              : ["data-entry"].includes(
                  inputArguments.find(
                    (arg) => arg.UUID === getOptionValues("json")[0]
                  )?.Format
                )
              ? reportingTemplates
                  ?.find(
                    (c) =>
                      c.uuid ===
                      inputArguments.find(
                        (arg) => arg.UUID === getOptionValues("json")[0]
                      )?.DataStructure
                  )
                  ?.fields.map((f) => ({
                    value: f.uuid,
                    label: f.label,
                  }))
              : jsonCollections
                  .find(
                    (c) =>
                      c.Uuid ===
                      (inputArguments.find(
                        (arg) => arg.UUID === getOptionValues("json")[0]
                      )?.DataStructure || getOptionValues("json:collection")[0])
                  )
                  ?.Fields?.map((f) => ({
                    value: f.Path,
                    label: f.Path,
                  })),
          },
        },

        // optional, "true" = fail with error if the json string is missing
        // or incompatible types when storing the extracted values, otherwise just skip the action
        //     {"Type": "must", "Value": "true"},
        // {
        //   type: "option",
        //   field: "must",
        //   defaultValue: ["false"],
        //   props: {
        //     isRadioButton: true,
        //     radioName: "must",
        //     label: "Fail with error if JSON string is missing",
        //     options: [
        //       { value: "true", label: "Yes" },
        //       { value: "false", label: "No" },
        //     ],
        //   },
        // },

        // STORE TO variable, one required, can be single or multiple,
        // formats:
        // IF the source format is asset/contact/event/ticket = short-text only
        // IF the source format is weather-alert = short-text OR json WITHOUT data structure
        // IF the source format is data-entry = short-text, datetime, number
        // IF the source format is json = any format
        {
          type: "compound",
          field: `variable`,
          defaultValue: [""],
          optionDefaults: ([{ Value: v }]) => ({
            ...(!inputArguments.find((arg) => arg.UUID === v)?.IsMultiple &&
            getOptionValues("operation")[0] === "append"
              ? { operation: "replace" }
              : {}),
          }),
          props: {
            label: "Variable to store the values",
            isInputArgumentOnly: true,
            isValidNewOption: (v) => v.trim(),
            showOperationSelector: true,
            inputArgumentFilter: (arg) => {
              if (arg.IsReadonly) return false;
              const source = inputArguments.find(
                (ia) => ia.UUID === getOptionValues("json")[0]
              );
              if (
                ["asset", "contact", "group", "event", "ticket"].includes(
                  source?.Format
                )
              ) {
                return arg.Format === "short-text";
              }
              if (source?.Format === "data-entry") {
                return ["short-text", "dateTime", "number"].includes(
                  arg.Format
                );
              }
              if (
                source?.Format === "json" &&
                source?.DataStructure === "weather-alert"
              ) {
                return (
                  arg.Format === "short-text" ||
                  arg.Format === "geo-json" ||
                  (arg.Format === "json" && !arg.DataStructure)
                );
              }
              if (source?.Format === "json") {
                return true;
              }
            },
          },
        },

        ...(inputArguments.find(
          (arg) => arg.UUID === getOptionValues("variable")[0]
        )?.Format === "contact"
          ? [
              {
                type: "option",
                field: "extract:reference-by",
                defaultValue: ["uuid"],
                props: {
                  label: "Reference is by",
                  inputType: "select",
                  isValidNewOption: () => false,
                  options: [
                    { value: "uuid", label: "Uuid" },
                    { value: "primary", label: "Primary contact field" },
                  ],
                },
              },
            ]
          : []),

        ...(inputArguments.find(
          (arg) => arg.UUID === getOptionValues("variable")[0]
        )?.Format === "contact" &&
        getOptionValues("extract:reference-by")[0] === "primary"
          ? [
              {
                type: "option",
                field: "extract:contact-create-if-not-found",
                defaultValue: ["false"],
                props: {
                  inputType: "radio",
                  radioName: "extract-contact-create-if-not-found",
                  label: "Create contact if not found",
                  options: [
                    { value: "true", label: "Yes" },
                    { value: "false", label: "No" },
                  ],
                },
              },
            ]
          : []),

        ...(inputArguments.find(
          (arg) => arg.UUID === getOptionValues("variable")[0]
        )?.Format === "asset"
          ? [
              {
                type: "option",
                field: "extract:reference-by",
                defaultValue: ["uuid"],
                props: {
                  label: "Reference is by",
                  inputType: "select",
                  isValidNewOption: () => false,
                  options: [
                    { value: "uuid", label: "Uuid" },
                    { value: "reference", label: "Reference" },
                    { value: "label", label: "Label" },
                    { value: "type", label: "Type" },
                  ],
                },
              },
            ]
          : []),

        ...(inputArguments.find(
          (arg) => arg.UUID === getOptionValues("variable")[0]
        )?.Format === "geo-json"
          ? [
              {
                type: "option",
                field: "extract:geojson-resolve",
                defaultValue: ["geojson"],
                props: {
                  label: "Resolve location from",
                  inputType: "select",
                  isValidNewOption: () => false,
                  options: [
                    { value: "geojson", label: "GeoJSON string" },
                    { value: "lat-lng", label: "lat,lng" },
                    { value: "lng-lat", label: "lng,lat" },
                  ],
                },
              },
            ]
          : []),

        //     // optional, "csv" = store the values as a single string separated by a comma, static only
        //     {"Type": "array", "Value": "csv"|"raw"},
        // {
        //   type: "compound",
        //   field: "array",
        //   defaultValue: ["csv"],
        //   optionDefaults: () => ({
        //     "storageData:category": null,
        //   }),
        //   props: {
        //     label: "Data format",
        //     isSelect: true,
        //     isStaticOnly: true,
        //     options: [
        //       { value: "csv", label: "CSV" },
        //       { value: "raw", label: "Raw" },
        //     ],
        //   },
        // },
        //     // optional, the separator for the array, default is a comma, static only
        //     // this option should be available only if the "json:path" is an array
        //     {"Type": "array:separator", "Value": ";"}
        // {
        //   type: "compound",
        //   field: "array:separator",
        //   defaultValue: [";"],
        //   ifOptionTypeHas: [
        //     {
        //       optionType: "json:path",
        //       values: (
        //         jsonCollections.find(
        //           (c) => c.Uuid === getOptionValues("json:collection")[0]
        //         )?.Fields || []
        //       )
        //         ?.filter((f) => f.Path.includes("[*]"))
        //         ?.map((f) => f.Path),
        //     },
        //     { optionType: "array", values: ["csv"] },
        //   ],
        //   props: {
        //     label: "Array separator (optional)",
        //     isSelect: false,
        //     isStaticOnly: true,
        //   },
        // },

        //   ]
        // }
      ],
    },

    // {
    "event:create": {
      label: "Create event",
      // 	"Type": "event:create",
      // 	"Options": [
      inputs: [
        // 		// TITLE can be static or from input argument
        // 		{"Type": "event:title", "Value": "New event title", "ValueFrom": "static"},
        // 		// OR from short-text input argument
        // 		{"Type": "event:title", "Value": "abc-123-uuid", "ValueFrom": "inputArgument", "MainThread": true},
        {
          type: "compound",
          field: "event:title",
          defaultValue: [""],
          props: {
            label: "Event title",
            inputArgumentProps: { Format: "short-text" },
          },
        },
        {
          type: "compound",
          field: "event:use-reference",
          defaultValue: [""],
          props: {
            label: "Event use system reference",
            isInputArgumentOnly: true,
            inputArgumentProps: { Format: "short-text" },
          },
        },
        {
          type: "compound",
          field: "event:store-reference",
          defaultValue: [""],
          props: {
            label: "Event store system reference",
            isInputArgumentOnly: true,
            inputArgumentProps: { Format: "short-text" },
          },
        },

        // 		// DESCRIPTION can be static or from input argument
        // 		{"Type": "event:description", "Value": "Event description", "ValueFrom": "static"},
        // 		// OR from short-text input argument
        // 		{"Type": "event:description", "Value": "abc-123-uuid", "ValueFrom": "inputArgument", "MainThread": true},
        {
          type: "compound",
          field: "event:description",
          defaultValue: [""],
          props: {
            label: "Event description",
            inputArgumentProps: { Format: "short-text" },
          },
        },
        {
          type: "compound",
          field: "user-reference",
          defaultValue: [""],
          props: {
            label: "User reference",
            inputArgumentProps: { Format: "short-text" },
          },
        },

        // 		// SEVERITY can be static or from input argument
        // 		{"Type": "event:severity", "Value": "high", "ValueFrom": "static"},
        // 		// OR from short-text input argument
        // 		{"Type": "event:severity", "Value": "abc-123-uuid", "ValueFrom": "inputArgument", "MainThread": true},
        {
          type: "compound",
          field: "event:severity",
          defaultValue: [""],
          props: {
            label: "Event severity",
            inputArgumentProps: { Format: "short-text" },
          },
        },

        // // TICKET can be associated to event
        // // select source of ticket, leave empty for no ticket
        // {"Type": "event:ticket-from", "Value": "variable"/"context"/"",},
        {
          type: "option",
          field: "event:ticket-from",
          defaultValue: [""],
          props: {
            label: "Event ticket from",
            inputType: "radio",
            radioName: "event-ticket-ref",
            options: [
              { value: "", label: "No ticket" },
              { value: "variable", label: "From variable" },
              // { value: "context", label: "From context" },
            ],
          },
        },

        //  // TICKET VALUE from variable (optional if event:ticket-from is "context")
        // 	// short-text variable only, can be single or multiple, if multiple first ticket will be associated to the event
        // 	{"Type": "event:ticket-ref", "Value": "uuid-123-abc", "ValueFrom": "inputArgument", "MainThread": true,},
        {
          type: "compound",
          field: "event:ticket-ref",
          defaultValue: [""],
          ifOptionTypeHas: [
            {
              optionType: "event:ticket-from",
              values: ["variable"],
            },
          ],
          props: {
            label: "Ticket from variable",
            isInputArgumentOnly: true,
            inputArgumentProps: { Format: ["ticket", "short-text"] },
            isValidNewOption: (v) => false,
            isClearable: true,
          },
        },

        //  // ASSETS can be associated to event (optional)
        // 	// asset format variable only, can be single or multiple
        // 	{"Type": "event:assets-add", "Value": "uuid-123-abc", "ValueFrom": "inputArgument", "MainThread": true},
        {
          type: "compound",
          field: "event:assets-add",
          defaultValue: [""],
          props: {
            label: "Assets can be associated to event",
            isInputArgumentOnly: true,
            inputArgumentProps: { Format: "asset" },
            isValidNewOption: (v) => !!v.trim(),
            isClearable: true,
          },
        },
        {
          type: "compound",
          field: "event:contacts",
          defaultValue: [""],
          props: {
            label: "Contacts can be associated to event",
            isInputArgumentOnly: true,
            inputArgumentProps: { Format: "contact" },
            isValidNewOption: (v) => !!v.trim(),
            isClearable: true,
          },
        },
        {
          type: "compound",
          field: "location",
          defaultValue: [""],
          props: {
            label: "Event locations",
            isInputArgumentOnly: true,
            inputArgumentProps: { Format: "geo-json" },
            isValidNewOption: (v) => !!v.trim(),
            isClearable: true,
          },
        },
        // // EVENT can be stored to variable (optional)
        // // event format variable only, can be single or multiple
        // {"Type": "inputArgument", "Value": "uuid-123-abc", "MainThread": true},
        {
          type: "compound",
          field: `inputArgument`,
          defaultValue: [""],
          optionDefaults: ([{ Value: v }]) => ({
            ...(!inputArguments.find((arg) => arg.UUID === v)?.IsMultiple &&
            getOptionValues("operation")[0] === "append"
              ? { operation: "replace" }
              : {}),
          }),
          props: {
            label: "Variable to store the event",
            isInputArgumentOnly: true,
            inputArgumentProps: { Format: "event", IsReadonly: false },
            isValidNewOption: (v) => v.trim(),
            showOperationSelector: true,
            isClearable: true,
          },
        },
        // 	]
      ],
      // }
    },
    // {
    // 	"Type": "event:find-by-ticket", <<< NEW
    "event:find-by-ticket": {
      // 	"Options": [
      inputs: [
        // 		// STORE TO
        // 		// uuid of the variable to store the events in
        // 		// must be of event format (single/multiple)
        // 		{"Type": "variable", "Value": "variable-uuid", "MainThread": true/false},
        {
          type: "compound",
          field: `variable`,
          defaultValue: [""],
          optionDefaults: ([{ Value: v }]) => ({
            ...(!inputArguments.find((arg) => arg.UUID === v)?.IsMultiple &&
            getOptionValues("operation")[0] === "append"
              ? { operation: "replace" }
              : {}),
          }),
          props: {
            label: "Variable to store the events in",
            isInputArgumentOnly: true,
            inputArgumentProps: { Format: "event", IsReadonly: false },
            isValidNewOption: (v) => v.trim(),
            showOperationSelector: true,
          },
        },

        // 		// FILTER BY STATUS
        // 		// optional, short-text variable, single or multiple, if multiple all events with any of the statuses will be loaded
        // 		{"Type": "event:status", "Value": "uuid-123-abc", "ValueFrom": "inputArgument", "MainThread": true},
        {
          type: "compound",
          field: "event:status",
          defaultValue: [""],
          props: {
            label: "Filter by status",
            isInputArgumentOnly: true,
            inputArgumentProps: { Format: "short-text" },
            isValidNewOption: (v) => false,
            isClearable: true,
          },
        },

        // 		// TICKET VALUE from variable
        // 		// short-text variable only, can be single or multiple, if multiple then all events with any of the tickets will be selected
        // 		{"Type": "event:ticket-ref" , "Value": "uuid-123-abc", "ValueFrom": "inputArgument", "MainThread": true}
        {
          type: "compound",
          field: "event:ticket-ref",
          defaultValue: [""],
          // ifOptionTypeHas: [
          //   {
          //     optionType: "event:ticket-from",
          //     values: ["variable"],
          //   },
          // ],
          props: {
            label: "Ticket from variable",
            isInputArgumentOnly: true,
            inputArgumentProps: { Format: ["ticket", "short-text"] },
            isValidNewOption: (v) => false,
            html: inputArguments.find(
              (arg) => arg.UUID === getOptionValues("event:ticket-ref")[0]
            )?.IsMultiple && (
              <>
                <div className="alert alert-warning w-100 mt-2">
                  If multiple variable is selected, all events matching all
                  tickets will be selected.
                </div>
              </>
            ),
          },
        },
        // 	]
      ],
      // }
    },

    // 	"Type": "event:find-by-reference", <<< NEW
    "event:find-by-reference": {
      // 	"Options": [
      inputs: [
        // 		// STORE TO
        // 		// uuid of the variable to store the events in
        // 		// must be of event format (single/multiple)
        // 		{"Type": "variable", "Value": "variable-uuid", "MainThread": true/false},
        {
          type: "compound",
          field: `variable`,
          defaultValue: [""],
          optionDefaults: ([{ Value: v }]) => ({
            ...(!inputArguments.find((arg) => arg.UUID === v)?.IsMultiple &&
            getOptionValues("operation")[0] === "append"
              ? { operation: "replace" }
              : {}),
          }),
          props: {
            label: "Variable to store the events in",
            isInputArgumentOnly: true,
            inputArgumentProps: { Format: "event", IsReadonly: false },
            isValidNewOption: (v) => v.trim(),
            showOperationSelector: true,
          },
        },

        // 		// FILTER BY STATUS
        // 		// optional, short-text variable, single or multiple, if multiple all events with any of the statuses will be loaded
        // 		{"Type": "event:status", "Value": "uuid-123-abc", "ValueFrom": "inputArgument", "MainThread": true},
        {
          type: "compound",
          field: "event:status",
          defaultValue: [""],
          props: {
            label: "Filter by status",
            isInputArgumentOnly: true,
            inputArgumentProps: { Format: "short-text" },
            isValidNewOption: (v) => false,
            isClearable: true,
          },
        },

        // 		// TICKET VALUE from variable
        // 		// short-text variable only, can be single or multiple, if multiple then all events with any of the tickets will be selected
        // 		{"Type": "event:ticket-ref" , "Value": "uuid-123-abc", "ValueFrom": "inputArgument", "MainThread": true}
        {
          type: "compound",
          field: "reference",
          defaultValue: [""],
          // ifOptionTypeHas: [
          //   {
          //     optionType: "event:ticket-from",
          //     values: ["variable"],
          //   },
          // ],
          props: {
            label: "Reference",
            isInputArgumentOnly: true,
            inputArgumentProps: { Format: "short-text", IsMultiple: false },
            isValidNewOption: (v) => v.trim(),
            // html: inputArguments.find(
            //   (arg) => arg.UUID === getOptionValues("event:ticket-ref")[0]
            // )?.IsMultiple && (
            //   <>
            //     <div className="alert alert-warning w-100 mt-2">
            //       If multiple variable is selected, all events matching all
            //       tickets will be selected.
            //     </div>
            //   </>
            // ),
          },
        },
        // 	]
      ],
      // }
    },
    "event:find-by-user-reference": {
      // 	"Options": [
      inputs: [
        // 		// STORE TO
        // 		// uuid of the variable to store the events in
        // 		// must be of event format (single/multiple)
        // 		{"Type": "variable", "Value": "variable-uuid", "MainThread": true/false},
        {
          type: "compound",
          field: `variable`,
          defaultValue: [""],
          optionDefaults: ([{ Value: v }]) => ({
            ...(!inputArguments.find((arg) => arg.UUID === v)?.IsMultiple &&
            getOptionValues("operation")[0] === "append"
              ? { operation: "replace" }
              : {}),
          }),
          props: {
            label: "Variable to store the events in",
            isInputArgumentOnly: true,
            inputArgumentProps: { Format: "event", IsReadonly: false },
            isValidNewOption: (v) => v.trim(),
            showOperationSelector: true,
          },
        },
        {
          type: "compound",
          field: "event:status",
          defaultValue: [""],
          props: {
            label: "Filter by status",
            isInputArgumentOnly: true,
            inputArgumentProps: { Format: "short-text" },
            isValidNewOption: (v) => false,
            isClearable: true,
          },
        },
        {
          type: "compound",
          field: "user-reference",
          defaultValue: [""],
          props: {
            label: "User Reference",
            isInputArgumentOnly: false,
            inputArgumentProps: { Format: "short-text", IsMultiple: false },
            isValidNewOption: (v) => v.trim(),
          },
        },
      ],
    },

    //  {
    //         "Type": "event:assets",
    "event:assets": {
      //         "Options": [
      inputs: [
        //             // EVENTS variable to add assets to
        //             // event format variable only, can be single or multiple
        //             {"Type": "variable", "Value": "uuid-123-abc", "ValueFrom": "inputArgument", "MainThread": true},
        {
          type: "compound",
          field: `variable`,
          defaultValue: [""],
          props: {
            label: "Event variable to store the assets in",
            isInputArgumentOnly: true,
            inputArgumentProps: { Format: "event" },
            isValidNewOption: (v) => v.trim(),
          },
        },
        //             // ASSETS to add
        //             // asset format variable only, can be single or multiple
        //             {"Type": "event:assets-add", "Value": "uuid-123-cde", "ValueFrom": "inputArgument", "MainThread": true, "Operation": "append"},
        {
          type: "compound",
          field: `event:add-assets`,
          defaultValue: [""],
          props: {
            label: "Assets to add",
            isInputArgumentOnly: true,
            inputArgumentProps: { Format: "asset" },
            isValidNewOption: (v) => v.trim(),
            isClearable: true,
          },
        },
        {
          type: "compound",
          field: `event:remove-assets`,
          defaultValue: [""],
          props: {
            label: "Assets to remove",
            isInputArgumentOnly: true,
            inputArgumentProps: { Format: "asset" },
            isValidNewOption: (v) => v.trim(),
            isClearable: true,
          },
        },
        //         ]
      ],
      //     }
    },

    // {
    //     "Type": "event:locations",
    //     "Options": [
    //         // EVENT variable to update
    //         // event format variable only, can be single or multiple (all events will be updated)
    //         {"Type": "variable", "Value": "uuid-123-abc", "ValueFrom": "inputArgument", "MainThread": true},

    //         // LOCATIONS to add
    //         // geojson format variable only, can be single or multiple
    //         {"Type": "location", "Value": "uuid-123-abc", "ValueFrom": "inputArgument", "MainThread": true},
    //     ]
    // }
    "event:locations": {
      inputs: [
        {
          type: "compound",
          field: `variable`,
          defaultValue: [""],
          props: {
            label: "Event variable to update",
            isInputArgumentOnly: true,
            inputArgumentProps: { Format: "event" },
            isValidNewOption: (v) => v.trim(),
          },
        },
        {
          type: "compound",
          field: "location",
          defaultValue: [""],
          props: {
            label: "Locations to add (GeoJSON format)",
            showStaticSelector: false,
            isInputArgumentOnly: true,
            inputArgumentProps: { Format: "geo-json" },
            isClearable: true,
            isValidNewOption: (v) => v.trim(),
          },
        },
      ],
    },

    // {
    // 	"Type": "event:update",
    "event:update": {
      // 	"Options": [
      inputs: [
        // 		// EVENT variable to update
        // 		// event format variable only, can be single or multiple (all events will be updated)
        // 		{"Type": "variable", "Value": "uuid-123-abc", "ValueFrom": "inputArgument", "MainThread": true},
        {
          type: "compound",
          field: `variable`,
          defaultValue: [""],
          props: {
            label: "Event variable to update",
            isInputArgumentOnly: true,
            inputArgumentProps: { Format: "event" },
            isValidNewOption: (v) => v.trim(),
          },
        },
        // 		// FIELD TO UPDATE - static only, select one
        // 		{"Type": "field", "Value": "title/description/severity"}
        {
          type: "option",
          field: "field",
          defaultValue: ["title"],
          props: {
            label: "Field to update",
            inputType: "select",
            options: [
              { value: "title", label: "Title" },
              { value: "description", label: "Description" },
              { value: "severity", label: "Severity" },
            ],
          },
        },
        // 		// VALUE can be static or from variable
        // 		// short-text variable only,
        // 		// can be single or multiple (first one used)
        // 		{"Type": "value", "Value": "uuid-123-abc", "ValueFrom": "inputArgument", "MainThread": true},
        {
          type: "compound",
          field: "value",
          defaultValue: [""],
          props: {
            label: "Value",
            inputArgumentProps: { Format: "short-text" },
            isValidNewOption: (v) => !!v.trim(),
            isClearable: true,
          },
        },
        // 	]
      ],
      // }
    },

    // {
    // 	"Type": "event:status-update",
    "event:status-update": {
      // 	"Options": [
      inputs: [
        // 		// EVENT variable to update
        // 		// event format variable only, can be single or multiple (all events will be updated)
        // 		{"Type": "variable", "Value": "uuid-123-abc", "ValueFrom": "inputArgument", "MainThread": true},
        {
          type: "compound",
          field: `variable`,
          defaultValue: [""],
          props: {
            label: "Event variable to update",
            isInputArgumentOnly: true,
            inputArgumentProps: { Format: "event" },
            isValidNewOption: (v) => v.trim(),
          },
        },
        // 		// STATUS can be static or from variable
        // 		// short-text variable only,
        // 		// can be single or multiple (first one used)
        // 		// when manually then status must: active/closed/resolved/archived
        // 		// incorrect status will be ignored
        // 		{"Type": "status", "Value": "uuid-123-abc", "ValueFrom": "inputArgument", "MainThread": true},
        {
          type: "compound",
          field: "status",
          defaultValue: [""],
          props: {
            label: "Status",
            inputArgumentProps: { Format: "short-text" },
            isValidNewOption: (v) => !!v.trim(),
            isClearable: true,
            inputType: "select",
            options: [
              { value: "active", label: "Active" },
              { value: "closed", label: "Closed" },
              { value: "resolved", label: "Resolved" },
              { value: "archived", label: "Archived" },
            ],
          },
        },
        // 	]
      ],
      // }
    },
    // {
    // 	"Type": "event:tickets",
    "event:tickets": {
      label: "Add or remove tickets from events",
      // 	"Options": [
      inputs: [
        // 		// EVENT variable to update
        // 		// event format variable only, can be single or multiple (all events will be updated)
        // 		{"Type": "variable", "Value": "uuid-123-abc", "ValueFrom": "inputArgument", "MainThread": true},
        {
          type: "compound",
          field: `variable`,
          defaultValue: [""],
          props: {
            label: "Event variable to update",
            isInputArgumentOnly: true,
            inputArgumentProps: { Format: "event", IsReadonly: false },
            isValidNewOption: (v) => v.trim(),
          },
        },
        // 		// TICKETS to add
        // 		// short-text variable only, can be single or multiple
        // 		{"Type": "event:ticket-ref", "Value": "uuid-123-abc", "ValueFrom": "inputArgument", "MainThread": true},
        {
          type: "compound",
          field: `event:ticket-ref`,
          defaultValue: [""],
          props: {
            label: "Tickets to add/remove",
            isInputArgumentOnly: true,
            inputArgumentProps: { Format: ["ticket", "short-text"] },
            isValidNewOption: (v) => v.trim(),
          },
        },
        {
          type: "option",
          field: "operation",
          defaultValue: ["add"],

          props: {
            label: "Action",
            inputType: "radio",
            radioName: "event-tickets-action",
            options: [
              { value: "add", label: "Add" },
              { value: "remove", label: "Remove" },
            ],
          },
        },
        // 	]
      ],
      // }
    },

    "event:contacts": {
      label: "Add or remove contacts from events",
      // 	"Options": [
      inputs: [
        // 		// EVENT variable to update
        // 		// event format variable only, can be single or multiple (all events will be updated)
        // 		{"Type": "variable", "Value": "uuid-123-abc", "ValueFrom": "inputArgument", "MainThread": true},
        {
          type: "compound",
          field: `variable`,
          defaultValue: [""],
          props: {
            label: "Event variable to update",
            isInputArgumentOnly: true,
            inputArgumentProps: { Format: "event", IsReadonly: false },
            isValidNewOption: (v) => v.trim(),
          },
        },
        {
          type: "compound",
          field: `event:contacts`,
          defaultValue: [""],
          props: {
            label: "Contacts to add/remove",
            isInputArgumentOnly: true,
            inputArgumentProps: { Format: "contact" },
            isValidNewOption: (v) => v.trim(),
          },
        },
        {
          type: "option",
          field: "operation",
          defaultValue: ["add"],

          props: {
            label: "Action",
            inputType: "radio",
            radioName: "event-contacts-action",
            options: [
              { value: "add", label: "Add" },
              { value: "remove", label: "Remove" },
            ],
          },
        },
        // 	]
      ],
      // }
    },

    // Close all events that match ticket or variable.
    // 	{
    // 		"Type": "event:context-close-all", >>>> OLD
    // 		"Type": "event:close-all", >>>> NEW
    "event:close-all": {
      // 		"Options": [
      inputs: [
        // 			// MATCHING TICKET OR VARIABLE
        // 			{"Type": "from" , "Value": "ticket"/"variable"},
        {
          type: "option",
          field: "from",
          defaultValue: ["variable"],
          optionDefaults: ([{ Value: v }]) => ({
            // "event:ticket-from": v === "ticket" ? "context" : null,
            "event:ticket-ref": null,
            variable: null,
          }),
          props: {
            label: "Event matching ticket or variable",
            inputType: "radio",
            radioName: "event-ticket-or-variable",
            options: [
              { value: "ticket", label: "Ticket" },
              { value: "variable", label: "Variable" },
            ],
          },
        },
        // 			// IF FROM TICKET
        // 			{"Type": "event:ticket-from" , "Value": "context"/"variable"},
        // {
        //   type: "option",
        //   field: "event:ticket-from",
        //   defaultValue: ["context"],
        //   ifOptionTypeHas: [
        //     {
        //       optionType: "from",
        //       values: ["ticket"],
        //     },
        //   ],
        //   optionDefaults: ([{ Value: v }]) => ({
        //     "event:ticket-ref": null,
        //     variable: null,
        //   }),
        //   props: {
        //     label: "Event ticket from",
        //     isRadioButton: true,
        //     radioName: "event-ticket-from",
        //     options: [
        //       { value: "context", label: "From context" },
        //       { value: "variable", label: "From variable" },
        //     ],
        //   },
        // },
        // 			// TICKET VALUE from variable (optional if event:ticket-from is "context")
        // 			// short-text variable only, can be single or multiple, if multiple first ticket will be used
        // 			{"Type": "event:ticket-ref" , "Value": "uuid-123-abc", "ValueFrom": "inputArgument", "MainThread": true},
        {
          type: "compound",
          field: "event:ticket-ref",
          defaultValue: [""],
          ifOptionTypeHas: [
            {
              optionType: "from",
              values: ["ticket"],
            },
            // {
            //   optionType: "event:ticket-from",
            //   values: ["variable"],
            // },
          ],
          props: {
            label: "Ticket from variable",
            isInputArgumentOnly: true,
            inputArgumentProps: { Format: ["ticket", "short-text"] },
            isValidNewOption: (v) => false,
            isClearable: true,
            html: inputArguments.find(
              (arg) => arg.UUID === getOptionValues("event:ticket-ref")[0]
            )?.IsMultiple && (
              <>
                <div className="alert alert-warning w-100 mt-2">
                  If multiple variable is selected, all events matching all
                  tickets will be closed.
                </div>
              </>
            ),
          },
        },
        // 			// IF CLOSE EVENTS FROM VARIABLE
        // 			// event format variable only, can be single or multiple, if multiple all events will be closed
        // 			{"Type": "variable" , "Value": "uuid-123-abc", "ValueFrom": "inputArgument", "MainThread": true}
        {
          type: "compound",
          field: "variable",
          defaultValue: [""],
          ifOptionTypeHas: [
            {
              optionType: "from",
              values: ["variable"],
            },
          ],
          props: {
            label: "Event from variable",
            isInputArgumentOnly: true,
            inputArgumentProps: { Format: "event" },
            isValidNewOption: (v) => false,
            html: inputArguments.find(
              (arg) => arg.UUID === getOptionValues("variable")[0]
            )?.IsMultiple && (
              <>
                <div className="alert alert-warning w-100 mt-2">
                  If multiple variable is selected, all matching events will be
                  closed.
                </div>
              </>
            ),
          },
        },
        // 		]
      ],
      // 	}
    },
    // {
    // 	"Type": "ticket:create",
    "ticket:create": {
      // 	"Options": [
      inputs: [
        // 		// TICKET reference - optional, if not provided a new ticket will be created
        // 		// short-text variable, single or multiple, only the first item from multiple values will be used
        // 		{"Type": "ticket", "Value": "uuid-abc-123", "ValueFrom": "inputArgument", "MainThread": "true"/"false"},
        {
          type: "compound",
          field: "ticket",
          defaultValue: [""],
          props: {
            label: "Ticket reference (leave empty to create new ticket)",
            isInputArgumentOnly: true,
            inputArgumentProps: { Format: "short-text" },
            isValidNewOption: (v) => false,
            isClearable: true,
            html: inputArguments.find(
              (arg) => arg.UUID === getOptionValues("ticket")[0]
            )?.IsMultiple && (
              <>
                <div className="alert alert-warning w-100 mt-2">
                  If multiple variable is selected, only the first item from
                  multiple values will be used.
                </div>
              </>
            ),
          },
        },
        // 		// TICKET description - optional,
        // 		// short-text variable, single or multiple, only the first item from multiple values will be used
        // 		{"Type": "description", "Value": "uuid-abc-123", "ValueFrom": "inputArgument", "MainThread": "true"/"false"},
        {
          type: "compound",
          field: "description",
          defaultValue: [""],
          props: {
            label: "Ticket description",
            isInputArgumentOnly: true,
            inputArgumentProps: { Format: "short-text" },
            isValidNewOption: (v) => !!v.trim(),
            isClearable: true,
            html: inputArguments.find(
              (arg) => arg.UUID === getOptionValues("description")[0]
            )?.IsMultiple && (
              <>
                <div className="alert alert-warning w-100 mt-2">
                  If multiple variable is selected, only the first item from
                  multiple values will be used.
                </div>
              </>
            ),
          },
        },
        // 		// TOPIC, select reporting topic - required,
        // 		{"Value": "abc-123-uuid","Type": "Topic"},
        {
          type: "option",
          field: "Topic",
          defaultValue: [reportingTopics[0]?.uuid],
          props: {
            label: "Reporting topic",
            inputType: "select",
            options: reportingTopics.map((t) => ({
              value: t.uuid,
              label: t.label,
            })),
          },
        },
        // 		// ASSIGN CONTACTS - optional,
        // 		// if variable is selected, it must be a contact format variable, single or multiple
        //    {"Type": "reporting:assign-contact", "Value": ""/"context"/"variable"/"recipient" (recipient only for message reply/ status update/ form events)}
        {
          type: "option",
          field: "reporting:assign-contact",
          defaultValue: [""],
          optionDefaults: ([{ Value: v }]) => ({
            contact: null,
          }),
          props: {
            label: "Assign contact",
            inputType: "radio",
            radioName: "reporting:assign-contact",
            isClearable: true,
            options: [
              { value: "", label: "Do not assign contact" },
              // { value: "context", label: "From context" },
              { value: "variable", label: "From variable" },
              // recipient only for message reply/ status update/ form events
              ...([
                "message:reply",
                "outbound-delivery-status",
                "form-submitted",
                "form-accessed",
              ].includes(event)
                ? [{ value: "recipient", label: "From recipient" }]
                : []),
            ],
          },
        },
        // 		// variable uuid if variable is selected in ASSIGN CONTACTS
        // 		{"Type": "contact", "Value": "variable-uuid-123", "MainThread": "true"/"false"}
        {
          type: "compound",
          field: "contact",
          defaultValue: [""],
          ifOptionTypeHas: [
            {
              optionType: "reporting:assign-contact",
              values: ["variable"],
            },
          ],
          props: {
            label: "Contact from variable",
            isInputArgumentOnly: true,
            inputArgumentProps: { Format: "contact" },
            isValidNewOption: (v) => false,
            isClearable: true,
          },
        },
        // 		// STORE TICKET reference - required,
        // 		// ticket variable, single or multiple
        // 		{"Type": "variable", "Value": "uuid-abc-123", "MainThread": "true"/"false"},
        {
          type: "compound",
          field: `variable`,
          defaultValue: [""],
          optionDefaults: ([{ Value: v }]) => ({
            ...(!inputArguments.find((arg) => arg.UUID === v)?.IsMultiple &&
            getOptionValues("operation")[0] === "append"
              ? { operation: "replace" }
              : {}),
          }),
          props: {
            label: "Variable to store the ticket reference in",
            isInputArgumentOnly: true,
            inputArgumentProps: { Format: "ticket", IsReadonly: false },
            isValidNewOption: (v) => v.trim(),
            showOperationSelector: true,
            isClearable: true,
          },
        },

        // 		// STORE TICKET to context - optional,
        // 		{"Type": "context", "Value": "true"/"false"} // default: "false"
        // {
        //   type: "option",
        //   field: "context",
        //   defaultValue: ["false"],
        //   props: {
        //     label: "Store ticket to context",
        //     isRadioButton: true,
        //     radioName: "ticket-to-context",
        //     options: [
        //       { value: "true", label: "Yes" },
        //       { value: "false", label: "No" },
        //     ],
        //   },
        // },
        // 	]
      ],
      // }
    },
    // {
    // 	"Type": "ticket:load",
    "ticket:load": {
      // 	"Options": [
      inputs: [
        // 		// TOPIC - required, select one or more reporting topics
        // 		{"Type": "Topic", "Value": "abc-123-uuid"},
        {
          type: "option",
          field: "Topic",
          defaultValue: [reportingTopics[0]?.uuid],
          optionDefaults: ([{ Value: v }]) => ({
            status: null,
          }),
          props: {
            label: "Reporting topic",
            inputType: "select",
            isMulti: true,
            options: reportingTopics.map((t) => ({
              value: t.uuid,
              label: t.label,
            })),
          },
        },
        // 		// STATUS - optional, static ticket statuses from selected topics, can be multiple
        // 		{"Type": "status", "Value": "Open"},
        {
          type: "option",
          field: "status",
          props: {
            label: "Ticket statuses",
            inputType: "select",
            isMulti: true,
            isClearable: true,
            options: [
              ...new Set(
                reportingTopics
                  .filter((topic) =>
                    getOptionValues("Topic").includes(topic.uuid)
                  )
                  .map((topic) => topic.ticketStatuses)
                  .flat(1)
                  .filter(Boolean)
              ),
            ].map((status) => ({
              value: status,
              label: status,
            })),
          },
        },
        // 		// CONTACT - optional, contact variable used to filter tickets
        // 		{"Type": "contact", "Value": "variable-uuid-123", "ValueFrom": "inputArgument", "MainThread": "true"/"false"},
        {
          type: "compound",
          field: "contact",
          defaultValue: [""],
          props: {
            label: "Contact from variable",
            isInputArgumentOnly: true,
            inputArgumentProps: { Format: "contact" },
            isValidNewOption: (v) => false,
            isClearable: true,
          },
        },
        {
          type: "option",
          field: "limit",
          defaultValue: ["100"],
          props: {
            label: "Limit",
            type: "number",
            min: 1,
            max: 100,
            isValidNewOption: (v) => v === "" || Number(v) > 0,
          },
        },
        {
          type: "compound",
          field: "page",
          defaultValue: [""],
          props: {
            label: "Page",
            type: "number",
            inputArgumentProps: { Format: "number" },
            isValidNewOption: (v) => v === "" || Number(v) >= 0,
            isClearable: true,
          },
        },
        // 		// STORE TICKET to variable - required,
        // 		// ticket references are stored in a short-text variable, single or multiple
        // 		{"Type": "variable", "Value": "uuid-abc-123", "MainThread": "true"/"false"},
        {
          type: "compound",
          field: `variable`,
          defaultValue: [""],
          optionDefaults: ([{ Value: v }]) => ({
            ...(!inputArguments.find((arg) => arg.UUID === v)?.IsMultiple &&
            getOptionValues("operation")[0] === "append"
              ? { operation: "replace" }
              : {}),
          }),
          props: {
            label: "Variable to store the ticket in",
            isInputArgumentOnly: true,
            inputArgumentProps: { Format: "ticket", IsReadonly: false },
            isValidNewOption: (v) => v.trim(),
            showOperationSelector: true,
          },
        },
        // 	]
      ],
      // }
    },
    // Set ticket to context
    // {
    // 	"Type": "ticket:set",
    // "ticket:set": {
    //   // 	"Options": [
    //   inputs: [
    //     // 		// TICKET reference - required,
    //     // 		// short-text variable, single or multiple, only the first item from multiple values will be used
    //     // 		{"Type": "ticket", "Value": "uuid-abc-123", "ValueFrom": "inputArgument", "MainThread": "true"/"false"},
    //     {
    //       type: "compound",
    //       field: "ticket",
    //       defaultValue: [""],
    //       props: {
    //         label: "Ticket reference",
    //         isInputArgumentOnly: true,
    //         inputArgumentProps: { Format: "short-text" },
    //         isValidNewOption: (v) => false,
    //         html: inputArguments.find(
    //           (arg) => arg.UUID === getOptionValues("ticket")[0]
    //         )?.IsMultiple && (
    //           <>
    //             <div className="alert alert-warning w-100 mt-2">
    //               If multiple variable is selected, only the first item from
    //               multiple values will be used.
    //             </div>
    //           </>
    //         ),
    //       },
    //     },
    //     {
    //       type: "compound",
    //       field: `variable`,
    //       defaultValue: [""],
    //       props: {
    //         label: "Variable to store the ticket in",
    //         isInputArgumentOnly: true,
    //         inputArgumentProps: {
    //           Format: "short-text",
    //         },
    //         isValidNewOption: (v) => v.trim(),
    //       },
    //     },
    //   ],
    //   // 	]
    //   // }
    // },
    // {
    // 	"Type": "ticket:set-status",
    "ticket:set-status": {
      // 	"Options": [
      inputs: [
        // 		// short-text variable, single only
        // 		{"Type": "status", "Value": "uuid-abc-123", "ValueFrom": "inputArgument", "MainThread": "true"/"false"},
        {
          type: "compound",
          field: "status",
          defaultValue: [""],
          props: {
            label: "Set ticket status",
            isInputArgumentOnly: true,
            inputArgumentProps: { Format: "short-text", IsMultiple: false },
            isValidNewOption: (v) => v.trim(),
          },
        },

        {
          type: "compound",
          field: "ticket",
          defaultValue: [""],
          props: {
            label: "Ticket",
            isInputArgumentOnly: true,
            inputArgumentProps: { Format: "ticket" },
            isValidNewOption: (v) => v.trim(),
          },
        },
        // 	]
      ],
      // }
    },

    // {
    // 	"type": "reporting:new-entry",
    "reporting:new-entry": {
      // 	"options": [
      inputs: [
        // 		// TOPIC
        // 		// one required, select reporting topic
        // 		{"Type": "Topic", "Value": "abc-123-uuid"},
        {
          type: "option",
          field: "Topic",
          defaultValue: [reportingTopics[0]?.uuid],
          props: {
            label: "Reporting topic",
            inputType: "select",
            options: reportingTopics.map((t) => ({
              value: t.uuid,
              label: t.label,
            })),
          },
        },
        // 		// TEMPLATE
        // 		// one required, template for the entry, select from topic templates
        // 		{"Type": "Template", "Value": "abc-123-uuid"},
        {
          type: "option",
          field: "reporting:template",
          defaultValue: [reportingTemplates[0]?.uuid],
          optionDefaults: ([{ Value: v }]) => ({
            // remove previous values
            ...action.Options.reduce((p, c) => {
              if (c.Type === "reportingField") {
                return { ...p, [c.Type]: null };
              }
              return p;
            }, {}),
            // add new ones
            ...(
              reportingTemplates.find((t) => t.uuid === v) ||
              reportingTemplates[0]
            )?.fields.reduce(
              (p, c) => ({
                ...p,
                // check if field has defaultValue
                reportingField: [
                  ...p.reportingField,
                  {
                    Value: c.defaultValue || "",
                    Key: c.uuid,
                    ValueFrom: "static",
                  },
                ],
              }),
              { reportingField: [] }
            ),
          }),
          props: {
            label: "Reporting template",
            inputType: "select",
            options: reportingTemplates.map((t) => ({
              value: t.uuid,
              label: t.label,
            })),
          },
        },
        // 		// FOR EACH field in the template:
        // 		// match variable format -> template field format
        // 		// short-text -> text, selects
        // 		// number -> number, duration
        // 		// datetime -> time
        // 		// if the field is required in the template, it must be provided here
        // 		{"Type": "reportingField", "Key": "reporting-field-uuid-abc-123", "Value": "uuid-abc-123", "ValueFrom": "inputArgument", "MainThread": true/false},
        // 		// OR static value
        // 		// make sure to add datetime picker in the form for time field and make number field number only
        // 		{"Type": "reportingField", "Key": "reporting-field-uuid-abc-123", "Value": "static value", "ValueFrom": "static"},

        ...((
          reportingTemplates.find(
            (t) => t.uuid === getOptionValues("reporting:template")[0]
          ) || reportingTemplates[0]
        )?.fields?.map((f) => ({
          type: "compound",
          field: "reportingField",
          defaultValue: [""],
          props: {
            label: f.label,
            keyValue: f.uuid,
            uniqueKey: f.uuid,
            showSolutionRoleSelector:
              action.SolutionRole === "public_reporting",
            inputArgumentProps: {
              Format:
                f?.type === "TIME"
                  ? "dateTime"
                  : ["NUMBER", "DURATION"].includes(f?.type)
                  ? "number"
                  : "short-text",
              IsMultiple: f?.isMultiple || false,
            },
            isValidNewOption: (v) => v.trim(),
            type: ["NUMBER", "DURATION"].includes(f?.type) ? "number" : "text",
            isDateTime: f?.type === "TIME",
            dateTimeType: "dateTime",
            inputType: f?.type === "CATEGORY" ? "select" : undefined,
            options:
              f?.type === "CATEGORY"
                ? f.categories.map((o) => ({
                    value: o,
                    label: o,
                  }))
                : [],
          },
        })) ?? []),

        // 		// ASSIGN CONTACTS - optional,
        // 		// if variable is selected, it must be a contact format variable, single or multiple
        // 		{"Type": "reporting:assign-contact", "Value": ""/"context"/"variable"/"recipient" (recipient only for message reply/ status update/ form events)}
        {
          type: "option",
          field: "reporting:assign-contact",
          defaultValue: [""],
          optionDefaults: ([{ Value: v }]) => ({
            contact: null,
          }),
          props: {
            label: "Assign contact",
            inputType: "radio",
            radioName: "reporting:assign-contact",
            isClearable: true,
            options: [
              { value: "", label: "Do not assign contact" },
              // { value: "context", label: "From context" },
              { value: "variable", label: "From variable" },
              // recipient only for message reply/ status update/ form events
              ...([
                "message:reply",
                "outbound-delivery-status",
                "form-submitted",
                "form-accessed",
              ].includes(event)
                ? [{ value: "recipient", label: "From recipient" }]
                : []),
            ],
          },
        },
        // 		// variable uuid if variable is selected
        // 		{"Type": "contact", "Value": "variable-uuid-123", "MainThread": "true"/"false"}
        {
          type: "compound",
          field: "contact",
          defaultValue: [""],
          ifOptionTypeHas: [
            {
              optionType: "reporting:assign-contact",
              values: ["variable"],
            },
          ],
          props: {
            label: "Contact from variable",
            isInputArgumentOnly: true,
            inputArgumentProps: { Format: "contact" },
            isValidNewOption: (v) => false,
          },
        },

        // // ASSIGN TICKET - optional,
        // // default to variable
        // {"Type": "event:ticket-from", "Value": "variable"/"context"/""}
        // // IF event:ticket-from == variable
        // // must be a short-text variable, single only
        // {"Type": "event:ticket-ref", "Value": "variable-uuid-123", "MainThread": "true"/"false"}
        {
          type: "option",
          field: "event:ticket-from",
          defaultValue: [""],
          optionDefaults: ([{ Value: v }]) => ({
            "event:ticket-ref": null,
          }),
          props: {
            label: "Ticket from",
            inputType: "radio",
            radioName: "event-ticket-from",
            options: [
              { value: "", label: "Do not use a ticket" },
              // { value: "context", label: "Ticket from context" },
              { value: "variable", label: "Ticket from variable" },
            ],
          },
        },
        {
          type: "compound",
          field: "event:ticket-ref",
          defaultValue: [""],
          ifOptionTypeHas: [
            {
              optionType: "event:ticket-from",
              values: ["variable"],
            },
          ],
          props: {
            label: "Ticket from variable",
            isInputArgumentOnly: true,
            inputArgumentProps: {
              Format: ["ticket", "short-text"],
              IsMultiple: false,
            },
            isValidNewOption: (v) => false,
          },
        },

        ...(projectData.IsSolution || !!projectData.SolutionUuid
          ? [
              {
                type: "property",
                field: "SolutionRole",
                defaultValue: [""],
                props: {
                  label: "Public reporting",
                  inputType: "radio",
                  radioName: "public-reporting",
                  options: [
                    { value: "", label: "Disabled" },
                    { value: "public_reporting", label: "Enabled" },
                  ],
                },
              },
            ]
          : []),

        // 	]
      ],
      // }
    },
    // Load an existing entry from reporting system to a text variable
    // 		{
    // 			"type": "reporting:load-entry",
    "reporting:load-entry": {
      // 			"options": [
      inputs: [
        // 				// TOPIC
        // 				// one required, select reporting topic
        // 				{"Type": "Topic", "Value": "abc-123-uuid"},
        {
          type: "option",
          field: "Topic",
          defaultValue: [reportingTopics[0]?.uuid],
          props: {
            label: "Reporting topic",
            inputType: "select",
            options: reportingTopics.map((t) => ({
              value: t.uuid,
              label: t.label,
            })),
          },
        },
        // 				// TEMPLATE
        // 				// one required, template for the entry, select from topic templates
        // 				{"Type": "reporting:template", "Value": "abc-123-uuid"},
        {
          type: "option",
          field: "reporting:template",
          defaultValue: [reportingTemplates[0]?.uuid],
          optionDefaults: ([{ Value: v }]) => ({
            // remove previous values
            ...action.Options.reduce((p, c) => {
              if (c.Type === "reportingField" || c.Type === "operator") {
                return { ...p, [c.Type]: null };
              }
              return p;
            }, {}),
            // add new ones
            ...(
              reportingTemplates.find((t) => t.uuid === v) ||
              reportingTemplates[0]
            )?.fields.reduce(
              (p, c) => ({
                ...p,
                // check if field has defaultValue
                reportingField: [
                  ...p.reportingField,
                  {
                    Value: c.defaultValue || "",
                    Key: c.uuid,
                    ValueFrom: "static",
                  },
                ],
                operator: [
                  ...p.operator,
                  {
                    Value: "is",
                    Key: c.uuid,
                    ValueFrom: "static",
                  },
                ],
              }),
              { reportingField: [], operator: [] }
            ),
          }),
          props: {
            label: "Reporting template",
            inputType: "select",
            options: reportingTemplates.map((t) => ({
              value: t.uuid,
              label: t.label,
            })),
          },
        },
        // 				// Load created in last X minutes/hours/days/weeks/months
        // 				// After unit
        // 				{"Type": "after:unit", "Value": "2"}, // default: "minute"/"hour"/"day"/"week"/"month"
        {
          type: "compound",
          field: `after:unit`,
          defaultValue: ["minute"],
          props: {
            label: "Filter by time unit",
            isStaticOnly: true,
            inputType: "select",
            options: [
              { value: "minute", label: "Minute" },
              { value: "hour", label: "Hour" },
              { value: "day", label: "Day" },
              { value: "week", label: "Week" },
              { value: "month", label: "Month" },
            ],
          },
        },
        // 				// After value - can be static or numeric variable (single)
        // 				{"Type": "after:value", "Value": "variable-uuid-123", "ValueFrom": "inputArgument", "MainThread": "true"},
        {
          type: "compound",
          field: `after:value`,
          defaultValue: ["0"],
          props: {
            label: "Filter by time value",
            type: "number",
            min: 0,
            inputArgumentProps: { Format: "number", IsMultiple: false },
          },
        },
        // 				// FILTER by contact - optional,
        // 				// if variable is selected, it must be a contact format variable, single or multiple
        // 				{"Type": "contact:load", "Value": ""/"context"/"variable"/"recipient" (recipient only for message reply/ status update/ form events)}
        {
          type: "option",
          field: "contact:load",
          defaultValue: [""],
          optionDefaults: ([{ Value: v }]) => ({
            contact: null,
          }),
          props: {
            label: "Filter by contact",
            inputType: "radio",
            radioName: "contact:load",
            isClearable: true,
            options: [
              { value: "", label: "Do not filter by contact" },
              // { value: "context", label: "From context" },
              { value: "variable", label: "From variable" },
              // recipient only for message reply/ status update/ form events
              ...([
                "message:reply",
                "outbound-delivery-status",
                "form-submitted",
                "form-accessed",
              ].includes(event)
                ? [{ value: "recipient", label: "From recipient" }]
                : []),
            ],
          },
        },
        // 				// variable uuid if variable is selected
        // 				{"Type": "contact", "Value": "variable-uuid-123", "MainThread": "true"/"false"}
        {
          type: "compound",
          field: "contact",
          defaultValue: [""],
          ifOptionTypeHas: [
            {
              optionType: "contact:load",
              values: ["variable"],
            },
          ],
          props: {
            label: "Contact from variable",
            isInputArgumentOnly: true,
            inputArgumentProps: { Format: "contact" },
            isValidNewOption: (v) => false,
          },
        },

        ...((
          reportingTemplates.find(
            (t) => t.uuid === getOptionValues("reporting:template")[0]
          ) || reportingTemplates[0]
        )?.fields?.map((f) => ({
          type: "compound",
          field: "reportingField",
          defaultValue: [
            { Value: "", Key: f.uuid, ValueFrom: "static", Filter: "is" },
          ],
          props: {
            label: "Filter by field: " + f.label,
            keyValue: f.uuid,
            uniqueKey: f.uuid,
            inputArgumentProps: {
              Format:
                f?.type === "TIME"
                  ? "dateTime"
                  : ["NUMBER", "DURATION"].includes(f?.type)
                  ? "number"
                  : "short-text",
              IsMultiple: false,
            },
            isValidNewOption: (v) => v.trim(),
            // show number input for NUMBER, DURATION
            type: ["NUMBER", "DURATION"].includes(f?.type) ? "number" : "text",
            // show datetime picker for TIME
            isDateTime: f?.type === "TIME",
            dateTimeType: "dateTime",
            inputType: f?.type === "CATEGORY" ? "select" : undefined,
            options:
              f?.type === "CATEGORY"
                ? f.categories.map((o) => ({
                    value: o,
                    label: o,
                  }))
                : [],
            showFilterOptions: true,
            filterOptions: [
              { value: "is", label: "Is" },
              { value: "is-not", label: "Is not" },
              ...(["NUMBER", "TIME", "DURATION"].includes(f?.type)
                ? [
                    { value: "lte", label: "Less than or equal" },
                    {
                      value: "gte",
                      label: "Greater than or equal",
                    },
                  ]
                : []),
            ],
          },
        })) ?? []),

        // 				// STORE records to a variable - required,
        // 				// text variable, single or multiple to store multiple entries
        // 				{"Type": "variable", "Value": "uuid-abc-123", "MainThread": "true"/"false"},
        {
          type: "compound",
          field: `variable`,
          defaultValue: [""],
          optionDefaults: ([{ Value: v }]) => ({
            ...(!inputArguments.find((arg) => arg.UUID === v)?.IsMultiple &&
            getOptionValues("operation")[0] === "append"
              ? { operation: "replace" }
              : {}),
          }),
          props: {
            label: "Variable to store the reporting entries in",
            isInputArgumentOnly: true,
            inputArgumentProps: {
              Format: "data-entry",
              DataStructure: getOptionValues("reporting:template")[0],
              IsReadonly: false,
            },
            isValidNewOption: (v) => v.trim(),
            showOperationSelector: true,
          },
        },

        // // TICKET can be associated to event
        // // select source of ticket, leave empty for no ticket
        // {"Type": "event:ticket-from", "Value": "variable"/"context"/"",},
        {
          type: "option",
          field: "event:ticket-from",
          defaultValue: [""],
          props: {
            label: "Filter by ticket",
            inputType: "radio",
            radioName: "filter-by-ticket",
            options: [
              { value: "", label: "No ticket" },
              { value: "variable", label: "From variable" },
              // { value: "context", label: "From context" },
            ],
          },
        },

        //  // TICKET VALUE from variable (optional if event:ticket-from is "context")
        // 	// short-text variable only, can be single or multiple, if multiple first ticket will be associated to the event
        // 	{"Type": "event:ticket-ref", "Value": "uuid-123-abc", "ValueFrom": "inputArgument", "MainThread": true,},
        {
          type: "compound",
          field: "event:ticket-ref",
          defaultValue: [""],
          ifOptionTypeHas: [
            {
              optionType: "event:ticket-from",
              values: ["variable"],
            },
          ],
          props: {
            label: "Ticket from variable",
            isInputArgumentOnly: true,
            inputArgumentProps: { Format: ["ticket", "short-text"] },
            isValidNewOption: (v) => false,
            isClearable: true,
          },
        },

        // LIMIT, static
        // required, how many items to take, default 1, max
        // {"Type": "limit", "Value": "1"},

        {
          type: "compound",
          field: "limit",
          defaultValue: ["100"],
          props: {
            type: "number",
            label: "Limit",
            isClearable: true,
            isStaticOnly: true,
            // inputArgumentProps: { Format: "number", IsMultiple: false },
          },
        },

        // OFFSET
        // optional, offset to start from, default 0
        // number variable (single) OR static
        // {"Type": "page", "Value": "uuid-123", "ValueFrom": "inputArgument", "MainThread": true},
        {
          type: "compound",
          field: "page",
          defaultValue: ["0"],
          props: {
            type: "number",
            label: "Page",
            isClearable: true,
            inputArgumentProps: { Format: "number", IsMultiple: false },
          },
        },
        // 			]
      ],
      // 		}
    },

    // {
    // 	"type": "contact:load",
    "contact:load": {
      // 	"options": [
      inputs: [
        // 	// required, contact field uuid to query
        // 	{"Type": "contactFieldUUID", "Value": "uuid-abc-123"},
        {
          type: "compound",
          field: "contactFieldUUID",
          defaultValue: [contactFields[0]?.Uuid],
          props: {
            isStaticOnly: true,
            label: "Contact field",
            inputType: "select",
            options: contactFields.map((f) => ({
              value: f.uuid,
              label: f.label,
            })),
          },
        },
        // 	// value to query
        // 	// text variable (single/multiple) OR static
        // 	{"Type": "values", "Value": "uuid-123", "ValueFrom": "inputArgument", "MainThread": true},
        {
          type: "compound",
          field: "values",
          defaultValue: [""],
          props: {
            label: "Value",
            inputArgumentProps: { Format: "short-text" },
          },
        },
        // 	// exactly one required, how to match value: prefix, suffix, contains, is (default)
        // 	{"Type": "operator", "Value": "is"},
        {
          type: "compound",
          field: "operator",
          defaultValue: ["is"],
          props: {
            isStaticOnly: true,
            label: "Match value",
            inputType: "select",
            options: [
              { value: "is", label: "Is" },
              { value: "prefix", label: "Prefix" },
              { value: "suffix", label: "Suffix" },
              { value: "contains", label: "Contains" },
              // { value: "not-prefix", label: "Not prefix" },
              // { value: "not-suffix", label: "Not suffix" },
              // { value: "not-contains", label: "Not contains" },
              // { value: "not", label: "Not" },
            ],
          },
        },
        // 	// LIMIT
        // 	// required, how many contacts to take, default 1, max 100
        // 	{"Type": "limit", "Value": "1"},
        {
          type: "compound",
          field: "limit",
          defaultValue: ["1"],
          props: {
            type: "number",
            label: "Limit",
            isClearable: true,
            inputArgumentProps: { Format: "number", IsMultiple: false },
          },
        },

        // 	// OFFSET
        // 	// optional, offset to start from, default 0
        // 	// number variable (single) OR static
        // 	{"Type": "page", "Value": "uuid-123", "ValueFrom": "inputArgument", "MainThread": true},
        {
          type: "compound",
          field: "page",
          defaultValue: ["0"],
          props: {
            type: "number",
            label: "Page",
            isClearable: true,
            inputArgumentProps: { Format: "number", IsMultiple: false },
          },
        },
        // 	// STORE contacts to a variable - required,
        // 	// contact variable, single or multiple to store multiple entries
        // 	{"Type": "variable", "Value": "uuid-abc-123", "MainThread": "true"/"false"},
        {
          type: "compound",
          field: `variable`,
          defaultValue: [""],
          optionDefaults: ([{ Value: v }]) => ({
            ...(!inputArguments.find((arg) => arg.UUID === v)?.IsMultiple &&
            getOptionValues("operation")[0] === "append"
              ? { operation: "replace" }
              : {}),
          }),
          props: {
            label: "Variable to store the contacts in",
            isInputArgumentOnly: true,
            inputArgumentProps: { Format: "contact", IsReadonly: false },
            isValidNewOption: (v) => v.trim(),
            showOperationSelector: true,
          },
        },

        // 	]
      ],
      // }
    },

    // {
    //     "type": "contact:first-or-create",
    "contact:first-or-create": {
      //     "options": [
      inputs: [
        //     // required, inputArgument short-text, single value
        //     {"Type": "value", "Value": "uuid-123", "ValueFrom": "inputArgument", "MainThread": true},
        {
          type: "compound",
          field: "value",
          defaultValue: [""],
          props: {
            label: "Value",
            isInputArgumentOnly: true,
            inputArgumentProps: { Format: "short-text", IsMultiple: false },
          },
        },
        //     // STORE contacts to a variable - optional,
        //     // contact variable, single or multiple to store multiple entries
        //     {"Type": "contact", "Value": "uuid-abc-123", "MainThread": "true"/"false"},
        {
          type: "compound",
          field: `contact`,
          defaultValue: [""],
          optionDefaults: ([{ Value: v }]) => ({
            ...(!inputArguments.find((arg) => arg.UUID === v)?.IsMultiple &&
            getOptionValues("operation")[0] === "append"
              ? { operation: "replace" }
              : {}),
          }),
          props: {
            label: "Variable to store the contacts in (optional)",
            isInputArgumentOnly: true,
            inputArgumentProps: { Format: "contact", IsReadonly: false },
            isValidNewOption: (v) => v.trim(),
            showOperationSelector: true,
            isClearable: true,
          },
        },

        //     // Was just created, string variable to store YES or NO, optional
        //     {"Type": "variable", "Value": "uuid-123-abc", "MainThread": true},
        //     {"Type": "variableOperation", "Value": "append"}, // default: "append"/"replace"/"delete-first"
        {
          type: "compound",
          field: `variable`,
          defaultValue: [""],
          optionDefaults: ([{ Value: v }]) => ({
            ...(!inputArguments.find((arg) => arg.UUID === v)?.IsMultiple &&
            getOptionValues("variableOperation")[0] === "append"
              ? { variableOperation: "replace" }
              : {}),
          }),
          props: {
            label:
              "Variable to store YES or NO, if contact was just created (optional)",
            isInputArgumentOnly: true,
            inputArgumentProps: { Format: "short-text", IsReadonly: false },
            isValidNewOption: (v) => v.trim(),
            showOperationSelector: true,
            isClearable: true,
          },
        },

        //     // optional, store the error response, text variable, single/multiple
        //     {"Type": "error", "Value": "uuid-123-xyz", "MainThread": true},
        //     {"Type": "errorOperation", "Value": "append"}, // default: "append"/"replace"/"delete-first"
        {
          type: "compound",
          field: `error`,
          defaultValue: [""],
          optionDefaults: ([{ Value: v }]) => ({
            ...(!inputArguments.find((arg) => arg.UUID === v)?.IsMultiple &&
            getOptionValues("errorOperation")[0] === "append"
              ? { errorOperation: "replace" }
              : {}),
          }),
          props: {
            label: "Variable to store the error in (optional)",
            isInputArgumentOnly: true,
            inputArgumentProps: { Format: "short-text", IsReadonly: false },
            isValidNewOption: (v) => v.trim(),
            showOperationSelector: true,
            isClearable: true,
          },
        },

        // optional, store the contact uuid to context
        // {"Type": "context", "Value": "true"},
        // {
        //   type: "option",
        //   field: "context",
        //   defaultValue: ["false"],
        //   props: {
        //     isRadioButton: true,
        //     isClearable: true,
        //     radioName: "context",
        //     label: "Store the contact uuid to context (optional)",
        //     options: [
        //       { value: "true", label: "Yes" },
        //       { value: "false", label: "No" },
        //     ],
        //   },
        // },

        //     // optional, if true the action will fail if contact cannot be created/found
        //     {"Type": "must", "Value": "true"},
        {
          type: "option",
          field: "must",
          defaultValue: ["false"],
          props: {
            inputType: "radio",
            isClearable: true,
            radioName: "must",
            label: "Stop the process on error (optional)",
            options: [
              { value: "true", label: "Yes" },
              { value: "false", label: "No" },
            ],
          },
        },
        //   ]
      ],
      // }
    },

    // {
    //   "Type": "contact:delete",
    "contact:delete": {
      //   "Options": [
      inputs: [
        //     // FROM  contact/context/recipient (if reply/status/form)
        //     {"Type": "from", "Value": "contact"},
        {
          type: "option",
          field: "from",
          defaultValue: ["contact"],
          optionDefaults: ([{ Value: v }]) => ({
            contact: null,
          }),
          props: {
            label: "Contact from",
            inputType: "radio",
            radioName: "from",
            options: [
              // { value: "context", label: "From context" },
              { value: "contact", label: "From contact variable" },
              // recipient only for message reply/ status update/ form events
              ...([
                "message:reply",
                "outbound-delivery-status",
                "form-submitted",
                "form-accessed",
              ].includes(event)
                ? [{ value: "recipient", label: "From recipient" }]
                : []),
            ],
          },
        },
        //     // contact variable to delete if from=contact, single or multiple, contact format
        //     {"Type": "contact", "Value": "variable-uuid-123", "MainThread": true/false},
        {
          type: "compound",
          field: "contact",
          defaultValue: [""],
          ifOptionTypeHas: [
            {
              optionType: "from",
              values: ["contact"],
            },
          ],
          props: {
            label: "Contact from variable",
            isInputArgumentOnly: true,
            inputArgumentProps: { Format: "contact" },
            isValidNewOption: (v) => false,
          },
        },
        //     // optional, if true the action will fail if contact cannot be deleted
        //     {"Type": "must", "Value": "true"}
        {
          type: "option",
          field: "must",
          defaultValue: ["false"],
          props: {
            inputType: "radio",
            isClearable: true,
            radioName: "must",
            label: "Stop the process on error (optional)",
            options: [
              { value: "true", label: "Yes" },
              { value: "false", label: "No" },
            ],
          },
        },
        //     // optional, store the error response, text variable, single/multiple
        //       {"Type": "error", "Value": "uuid-123-xyz", "MainThread": true},
        //       {"Type": "errorOperation", "Value": "append"}, // default: "append"/"replace"/"delete-first"
        {
          type: "compound",
          field: `error`,
          defaultValue: [""],
          optionDefaults: ([{ Value: v }]) => ({
            ...(!inputArguments.find((arg) => arg.UUID === v)?.IsMultiple &&
            getOptionValues("errorOperation")[0] === "append"
              ? { errorOperation: "replace" }
              : {}),
          }),
          props: {
            label: "Variable to store the error in (optional)",
            isInputArgumentOnly: true,
            inputArgumentProps: { Format: "short-text", IsReadonly: false },
            isValidNewOption: (v) => v.trim(),
            showOperationSelector: true,
            isClearable: true,
          },
        },
        //   ]
      ],
      // }
    },

    "contact:update-location": {
      inputs: [
        {
          type: "compound",
          field: "location",
          defaultValue: [""],
          props: {
            label: "Location from variable",
            isInputArgumentOnly: true,
            inputArgumentProps: { Format: "geo-json", IsMultiple: false },
            isValidNewOption: (v) => v.trim(),
          },
        },
        {
          type: "compound",
          field: "contact",
          defaultValue: [""],
          props: {
            label: "Contacts from variable",
            isInputArgumentOnly: true,
            inputArgumentProps: { Format: "contact" },
            isValidNewOption: (v) => v.trim(),
          },
        },
        {
          type: "option",
          field: "must",
          defaultValue: ["false"],
          props: {
            inputType: "radio",
            isClearable: true,
            radioName: "must",
            label: "Stop the process on error (optional)",
            options: [
              { value: "true", label: "Yes" },
              { value: "false", label: "No" },
            ],
          },
        },
        {
          type: "compound",
          field: `error`,
          defaultValue: [""],
          optionDefaults: ([{ Value: v }]) => ({
            ...(!inputArguments.find((arg) => arg.UUID === v)?.IsMultiple &&
            getOptionValues("errorOperation")[0] === "append"
              ? { errorOperation: "replace" }
              : {}),
          }),
          props: {
            label: "Variable to store the error in (optional)",
            isInputArgumentOnly: true,
            inputArgumentProps: { Format: "short-text", IsReadonly: false },
            isValidNewOption: (v) => v.trim(),
            showOperationSelector: true,
            isClearable: true,
          },
        },
      ],
    },

    "contact:get-location": {
      inputs: [
        {
          type: "compound",
          field: "contact",
          defaultValue: [""],
          props: {
            label: "Contact from variable",
            isInputArgumentOnly: true,
            inputArgumentProps: { Format: "contact", IsMultiple: false },
            isValidNewOption: (v) => v.trim(),
          },
        },
        {
          type: "compound",
          field: "variableTo",
          defaultValue: [""],
          props: {
            label: "Store location in variable",
            isInputArgumentOnly: true,
            inputArgumentProps: { Format: "geo-json", IsMultiple: false },
            isValidNewOption: (v) => v.trim(),
            showOperationSelector: true,
          },
        },
        {
          type: "option",
          field: "must",
          defaultValue: ["false"],
          props: {
            inputType: "radio",
            isClearable: true,
            radioName: "must",
            label: "Stop the process on error (optional)",
            options: [
              { value: "true", label: "Yes" },
              { value: "false", label: "No" },
            ],
          },
        },
        {
          type: "compound",
          field: `error`,
          defaultValue: [""],
          optionDefaults: ([v]) => ({
            error: v,
            errorOperation: "append",
          }),
          props: {
            label: "Variable to store the error in (optional)",
            isInputArgumentOnly: true,
            inputArgumentProps: { Format: "short-text", IsReadonly: false },
            isValidNewOption: (v) => v.trim(),
            showOperationSelector: true,
            isClearable: true,
          },
        },
      ],
    },

    //  {
    // 		"Type": "AddResponderToContactSet",
    // 		// TypeUUID is the uuid of the contact set (group) to manage in:
    // 		// RemoveResponderFromContactSet, RemoveStarterFromContactSet, RemoveAllFromContactSet,
    // 		// AddStarterToContactSet, AddResponderToContactSet, contact:add-to-group, contact:remove-from-group
    // 		"TypeUUID": "contact-set-uuid-123",
    // 		"Options": [
    // 			// if contact variable is selected as who (contact:add-to-group/contact:remove-from-group),
    // 			// then select the variable that contains the contacts to add/remove
    // 			{"Type": "variable", "Value": "variable-uuid-123", "MainThread": true/false},
    // 		]
    // 	}

    "contact:add-to-group": {
      label: "Add contact to group",
      inputs: [
        {
          type: "compound",
          field: "group",
          defaultValue: [""],
          props: {
            label: "Contact group",
            inputType: "contactSet",
            contactSetType: "static",
            inputArgumentProps: { Format: "group", IsMultiple: false },
            isValidNewOption: (v) => v.trim(),
          },
        },
        {
          type: "compound",
          field: "variable",
          defaultValue: [""],
          props: {
            label: "Contact from variable",
            isInputArgumentOnly: true,
            inputArgumentProps: { Format: "contact" },
            isValidNewOption: (v) => false,
          },
        },
      ],
    },
    "contact:remove-from-group": {
      label: "Remove contact from group",
      inputs: [
        {
          type: "compound",
          field: "group",
          defaultValue: [""],
          props: {
            label: "Contact group",
            inputType: "contactSet",
            contactSetType: "static",
            inputArgumentProps: { Format: "group", IsMultiple: false },
            isValidNewOption: (v) => v.trim(),
          },
        },
        {
          type: "compound",
          field: "variable",
          defaultValue: [""],
          props: {
            label: "Contact from variable",
            isInputArgumentOnly: true,
            inputArgumentProps: { Format: "contact" },
            isValidNewOption: (v) => false,
          },
        },
      ],
    },

    // {
    //   "Type": "editContact",
    editContact: {
      //   "Options": [
      inputs: [
        //     // FROM  contact/context/recipient (if reply/status/form)
        //     {"Type": "from", "Value": "contact"},
        {
          type: "option",
          field: "from",
          defaultValue: ["contact"],
          optionDefaults: ([{ Value: v }]) => ({
            contact: null,
          }),
          props: {
            label: "Contact from",
            inputType: "radio",
            radioName: "from",
            options: [
              // { value: "context", label: "From context" },
              { value: "contact", label: "From contact variable" },
              // recipient only for message reply/ status update/ form events
              ...([
                "message:reply",
                "outbound-delivery-status",
                "form-submitted",
                "form-accessed",
              ].includes(event)
                ? [{ value: "recipient", label: "From recipient" }]
                : []),
            ],
          },
        },
        {
          type: "compound",
          field: "contact",
          defaultValue: [""],
          ifOptionTypeHas: [
            {
              optionType: "from",
              values: ["contact"],
            },
          ],
          props: {
            label: "Contact from variable",
            isInputArgumentOnly: true,
            inputArgumentProps: { Format: "contact" },
            isValidNewOption: (v) => false,
          },
        },
        //     // contact field to update, select from contact fields
        //     {"Type": "contactFieldUUID", "Value": "variable-uuid-123"},
        {
          type: "compound",
          field: "contactFieldUUID",
          defaultValue: [contactFields[0]?.Uuid],
          props: {
            isStaticOnly: true,
            label: "Contact field to update",
            inputType: "select",
            options: contactFields.map((f) => ({
              value: f.uuid,
              label: f.label,
            })),
          },
        },
        //     action to perform on the field:
        //     update (replace[or single value fields]/append[for multiple value fields] current data)
        //     OR delete (remove all data)
        //     {"Type": "action", "Value": "update"},
        {
          type: "option",
          field: "action",
          defaultValue: ["update"],
          props: {
            label: "Action to perform on the field",
            inputType: "radio",
            radioName: "action",
            options: [
              { value: "update", label: "Update" },
              { value: "delete", label: "Delete" },
            ],
          },
        },
        //     // values to update the field if action is "update"
        //     // short-text variable, single or multiple
        //     {"Type": "edit", "Value": "variable-uuid-234", "ValueFrom": "inputArgument", "MainThread": true/false},
        {
          type: "compound",
          field: "edit",
          defaultValue: [""],
          ifOptionTypeHas: [
            {
              optionType: "action",
              values: ["update"],
            },
          ],
          props: {
            label: "Value to update the field with",
            isInputArgumentOnly: true,
            inputArgumentProps: { Format: "short-text" },
          },
        },
        //     // optional, if true the action will fail if action can't be completed
        //     {"Type": "must", "Value": "true"}
        {
          type: "option",
          field: "must",
          defaultValue: ["false"],
          props: {
            inputType: "radio",
            radioName: "must",
            label: "Stop the process on error",
            options: [
              { value: "true", label: "Yes" },
              { value: "false", label: "No" },
            ],
          },
        },

        //     // optional, store the error response, text variable, single/multiple
        //       {"Type": "error", "Value": "uuid-123-xyz", "MainThread": true},
        //       {"Type": "errorOperation", "Value": "append"}, // default: "append"/"replace"/"delete-first"
        {
          type: "compound",
          field: `error`,
          defaultValue: [""],
          optionDefaults: ([{ Value: v }]) => ({
            ...(!inputArguments.find((arg) => arg.UUID === v)?.IsMultiple &&
            getOptionValues("errorOperation")[0] === "append"
              ? { errorOperation: "replace" }
              : {}),
          }),
          props: {
            label: "Variable to store the error in (optional)",
            isInputArgumentOnly: true,
            inputArgumentProps: { Format: "short-text", IsReadonly: false },
            isValidNewOption: (v) => v.trim(),
            showOperationSelector: true,
            isClearable: true,
          },
        },

        //   ]
      ],
      // }
    },

    "extractText:inputArgument": {
      label: "",
      inputs: [
        {
          type: "property",
          field: "TypeUUID",
          props: {
            label: "Extract from",
            inputType: "select",
            isValidNewOption: () => false,
            options: [
              ...(event === "message:reply"
                ? [
                    {
                      value: "message:reply",
                      label: "Message reply",
                    },
                  ]
                : []),
              {
                value: "EmailOrSmsBody",
                label: "Email or sms body",
              },
              {
                value: "EmailSubject",
                label: "Email subject",
              },
              ...inputArguments
                .filter(
                  (arg) =>
                    ["short-text"].includes(arg.Format) && !arg.IsMultiple
                )
                .map((variable) => ({
                  value: variable.UUID,
                  label: variable.Label,
                })),
            ],
          },
        },
        {
          children: (
            <ExtractOptions
              options={action.ExtractOptions || {}}
              onChange={(options) =>
                dispatch({
                  type: "edit-action",
                  actionId: action.UUID,
                  newAction: {
                    ...action,
                    ExtractOptions: options,
                  },
                })
              }
            />
          ),
        },
        {
          type: "compound",
          field: `variable`,
          defaultValue: [""],
          optionDefaults: ([{ Value: v }]) => ({
            ...(!inputArguments.find((arg) => arg.UUID === v)?.IsMultiple &&
            getOptionValues("operation")[0] === "append"
              ? { operation: "replace" }
              : {}),
          }),
          props: {
            label: "Variable to store the extracted items in",
            isInputArgumentOnly: true,
            inputArgumentFilter: (arg) =>
              ["short-text", "number"].includes(arg.Format) && !arg.IsReadonly,
            isValidNewOption: (v) => v.trim(),
            showOperationSelector: true,
            inputArgumentProps: { IsReadonly: false },
          },
        },
        {
          type: "option",
          field: "validateAs",

          props: {
            label: "Validate as",
            isMulti: true,
            inputType: "select",
            inputHelp:
              "Validate the extracted text as Email, SMS, Voice or Integer.",
            isValidNewOption: () => false,
            options: [
              { label: "Email", value: "email" },
              { label: "SMS", value: "sms" },
              { label: "Voice", value: "voice" },
              { label: "Integer", value: "number" },
            ],
          },
        },
        {
          children: <TestExtraction action={action} />,
        },
      ],
    },

    "file:tag-files": {
      inputs: [
        {
          type: "compound",
          field: `variable`,
          defaultValue: [{ Value: "", MainThread: true }],
          props: {
            label: "File variable",
            isInputArgumentOnly: true,
            inputArgumentProps: { Format: "binary" },
            isValidNewOption: (v) => v.trim(),
          },
        },
        {
          type: "compound",
          field: `file:tags`,
          defaultValue: [""],
          props: {
            label: "Tags",
            inputArgumentProps: { Format: "short-text" },
            isValidNewOption: (v) => v.trim(),
          },
        },
        {
          type: "option",
          field: "operation",
          defaultValue: ["add"],
          props: {
            label: "Action",
            inputType: "radio",
            radioName: "operation",
            options: [
              { value: "add", label: "Add" },
              { value: "remove", label: "Remove" },
            ],
          },
        },
      ],
    },

    "ai:prompt": {
      // {
      // 	"Type": "ai:prompt"
      // 	"Options": [
      inputs: [
        // 		// PROMPT input argument
        // 		// short-text variable, single only, can be static or from input argument
        // 		// user interface must handle enough text area for prompt input if static is used
        // 		{"Type": "variable", "Value": "uuid-123-abc", "MainThread": true, "ValueFrom": "inputArgument"},
        {
          type: "compound",
          field: `variable`,
          defaultValue: [""],
          props: {
            label: "AI prompt",
            inputArgumentProps: { Format: "short-text" },
            isValidNewOption: (v) => v.trim(),
            inputType: "textArea",
          },
        },

        {
          type: "compound",
          field: `ai:model`,
          defaultValue: [""],
          props: {
            label: "AI model",
            inputArgumentProps: {
              Format: "configuration",
              DataStructure: "AI Models",
            },
            isValidNewOption: (v) => v.trim(),
            inputType: "select",
            options: Object.entries(
              pageProps.configurations?.["AI Models"] || {}
            ).map(([key, model]) => ({
              value: key,
              label: model,
            })),
          },
        },

        // 		// variables containing files to attach to the prompt (optional)
        // 		{"Type": "file", "Value": "uuid-123-abc", "ValueFrom": "inputArgument", "MainThread": true},
        {
          type: "compound",
          field: `file`,
          defaultValue: [""],
          props: {
            label:
              "Variable containing files to attach to the prompt (optional)",
            inputArgumentProps: { Format: "binary" },
            isValidNewOption: (v) => v.trim(),
            isInputArgumentOnly: true,
          },
        },
        // 		// output variable to store the AI response (required)
        // 		// short-text variable, single only,
        // 		// standard operation applies: delete-first, replace, append
        // 		{"Type": "variableTo", "Value": "uuid-abc-123", "MainThread": true, "Operation": "replace"},
        {
          type: "compound",
          field: `variableTo`,
          defaultValue: [""],
          props: {
            label: "Output variable to store the AI response (required)",
            inputArgumentProps: { Format: "short-text", IsReadonly: false },
            isValidNewOption: (v) => v.trim(),
            isInputArgumentOnly: true,
            showOperationSelector: true,
          },
        },
        // 	]
      ],
      // }
    },

    "delivery-status:to-variable": {
      label: "",
      events: ["outbound-delivery-status"],
      inputs: [
        {
          type: "compound",
          field: "variable",
          defaultValue: [""],
          optionDefaults: ([{ Value: v }]) => ({
            ...(!inputArguments.find((arg) => arg.UUID === v)?.IsMultiple &&
            getOptionValues("operation")[0] === "append"
              ? { operation: "replace" }
              : {}),
          }),
          props: {
            label: "Variable name",
            isInputArgumentOnly: true,
            inputArgumentProps: { Format: "short-text", IsReadonly: false },
            isValidNewOption: (v) => v.trim(),
            showOperationSelector: true,
          },
        },
      ],
    },

    "error:to-variable": {
      label: "Error to variable",
      events: ["process:error"],
      inputs: [
        {
          type: "option",
          field: "errorInformation",
          defaultValue: ["message"],
          props: {
            label: "Error information",
            inputType: "select",
            isValidNewOption: () => false,
            options: [
              { value: "message", label: "Message" },
              { value: "type", label: "Type" },
              { value: "resource", label: "Resource" },
            ],
          },
        },
        {
          type: "compound",
          field: "variable",
          defaultValue: [""],
          props: {
            label: "Target variable",
            isInputArgumentOnly: true,
            isValidNewOption: (v) => v.trim(),
            inputArgumentProps: { Format: "short-text" },
            showOperationSelector: true,
          },
        },
      ],
    },

    //{
    // "Type": "add-log-entry",
    // "Options": [
    // // VARIABLE to add log entry to, required, event/asset/contact/group format variable, single or multiple
    // {"Type": "variable", "Value": "uuid-123-abc", "ValueFrom": "inputArgument", "MainThread": true},

    // // MESSAGE, required, static only, defines the type of the message:
    // // for example "reviewed", "message-sent" - up to 100 characters
    // {"Type": "message", "Value": "message", "ValueFrom": "static"},

    // // DATA content, required, short-text variable, single/multiple or static
    // {"Type": "data", "Value": "uuid-123-abc", "ValueFrom": "inputArgument", "MainThread": true},
    // // OR static
    // {"Type": "data", "Value": "Log entry content", "ValueFrom": "static"},
    // ]
    // }
    "add-log-entry": {
      inputs: [
        {
          type: "compound",
          field: `variable`,
          defaultValue: [""],
          props: {
            label: "Variable to add log entry to",
            isInputArgumentOnly: true,
            inputArgumentProps: {
              Format: ["event", "asset", "contact", "group"],
            },
            isValidNewOption: (v) => v.trim(),
          },
        },
        {
          type: "compound",
          field: `message`,
          defaultValue: [""],
          props: {
            label: "Log message",
            isStaticOnly: true,
            maxLength: 100,
          },
        },
        {
          type: "compound",
          field: `data`,
          defaultValue: [""],
          props: {
            label: "Data content",
            inputArgumentProps: { Format: "short-text" },
            isValidNewOption: (v) => v.trim(),
            inputType: "textArea",
          },
        },
      ],
    },
  };
};

export default actionOptionsConfig;
